// ============================================================================
// ui/state.js — pure, DOM-FREE design-state core (V2 element model)
// ============================================================================
// The one place all mutable design logic lives, structured so it imports WITHOUT
// a DOM and is fully node-testable. main.js owns the single live instance and
// wires it to the DOM; this module never touches window/document/localStorage.
//
// The Design Profile (design-profile.js) now carries EVERYTHING design-affecting
// (palette, elements, weightValue, unit, layoutPreset), so the undo/redo +
// autosave snapshot is just the serialized profile. `ui` holds only session /
// non-design bits (current step, product selection, palette-extraction chips,
// a weight/unit mirror for the specs strip).
// ============================================================================

import {
  createDefaultProfile,
  createDefaultLogo,
  createElement,
  serializeProfile,
  serializeElement,
  defaultOutlinesFor,
  seedOutlinesFrom,
  outlineThicknessFor,
  usableAccents,
  readsOn,
  isNearNeutralHex,
  contrastRatio,
  DEFAULT_ACCENTS,
  normalizeHex,
  assessQuality,
  resolvePaletteRef,
  colorsConflict,
  colorsIndistinguishable,
  isNearBlackHex,
  applyAutoStyleToElements,
  DEFAULT_LAYOUT_PRESET,
  SWATCH_CAP,
  shownLogoTones,
  ringPreferenceOrder,
  demoteNearNeutrals,
  inferHandStyledForMigration,
  hexChroma,
  bestContrastRef,
  contrastPoolFrom,
  effectiveTextStyle,
  LOGO_BOX_NOMINAL_VB,
  logoGapFromStructure,
} from '../design-profile.js';
import { FONTS, DEFAULT_BODY_COLOR, clampBigNudge, WEIGHT_NUDGE_MAX, WEIGHT_NUDGE_STEP, plateDefaultSequence, nearMerges } from '../design-profile.js';
import {
  applyLayoutPreset, LAYOUT_PRESET_IDS, FAMILY_DEFAULT_PRESET, isPlaceholderText,
  LAYOUT_PRESETS, getLayoutPreset, layoutPresetProduct, matchPresetToComposition,
} from '../layouts.js';
import { layoutFamilyFor, modelFor, productSupportsBodyColor, designableWeights, productKey, parseProductKey, baseProductKey, productSupportsMultiConfig, maxConfigsFor, defaultWeightFor, configLadder } from '../products.js';
import { seedDefaultSizes } from '../render-engine.js';
// The peel's own labeller, asked at analysis time whether each detected band has any pixels to act
// on — see `bandPeelable` in `applyLogoStructure`.
import { renderPaletteFor, markDominantTone } from '../logo-outline.js';
import { bandPixelCounts, bandsVisibleOnPlate, repaintReads, bandConflictView, bandViewOptions, bandRescuePool, wrapToneFromTouches, contextualFillSwap, refusedRecolours, unmeasuredPresence, keylineOnlyTones, presentedOuterOutline, markToneOrder, isNeutralTone, WRAP_CONTACT_MIN, bgFaceShare, WRAPPED_TONE_BG_MAX, markWearsBlack, isBlackInk, isPlateMoat, frontierShare, WRAP_FRONTIER_OWN_MIN } from '../logo-outline.js';
/* WD 481 candidate: share of the background frontier a NON-band tone must own before its merge with the plate means the edge does not read (see `offBandEdge`). */
const OFF_BAND_EDGE_MIN = 0.4;

/** A wrap-held merging tone under this ink share is line detail the wrap DRAWS (ECC's letters,
 *  0.111 — hollow reads as outlined type); over it, a hollowed body (the hawk, 0.485). WD 337. */
const WRAP_HELD_MAX_INK = 0.25;
import { deriveArcText, deriveArcTextTri, distributeAcrossArcs } from '../arc-text.js';
import { DEFAULT_SIZE_PCT, KG_PER_LB, composeToneSwap, inheritSavedToneSwaps, OUTLINES_MAX } from '../design-profile.js';

/** The product the stage falls back to when nothing has been picked — the
 *  flagship bumper, which is also the layout family the default preset belongs
 *  to (DEFAULT_LAYOUT_PRESET is a plate layout, so these two must agree). */
export const DEFAULT_PRODUCT_KEY = 'bumper|Competition Urethane';

export const UNDO_LIMIT = 50;
export const AUTOSAVE_KEY = 'wd:v2:autosave';
/** Bumped to 2: the profile shape changed (element model) — old payloads ignored. */
export const AUTOSAVE_VERSION = 3;
/**
 * The one older envelope version this build still ACCEPTS, by migrating it — see `migrateEnvelope`.
 *
 * v2 stored `weightValue` in whatever `unit` said, so a kg design carried a kg number. v3 stores
 * pounds always (design-profile: "UNIT IS A LABEL, NOT A WEIGHT"). Rejecting v2 outright — the
 * behaviour every previous bump used — would have discarded every design already saved or SHARED,
 * including the lb ones, which are the overwhelming majority and need no conversion at all.
 */
export const AUTOSAVE_VERSION_MIGRATABLE = 2;
export const DEFAULT_WEIGHT = 45;

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createState() {
  const profile = createDefaultProfile();
  applyLayoutPreset(profile, profile.layoutPreset); // seed the default layout's elements
  const state = {
    designProfile: profile,
    ui: {
      currentStep: 1, // 1 logo · 2 design · 3 product+quote
      weight: DEFAULT_WEIGHT, // mirror of profile.weightValue (specs strip)
      unit: 'lb', // mirror of profile.unit
      productCategory: null,
      productKind: null,
      // Which product the live 3D stage shows, as a products.js KEY
      // (`${category}|${kind}`). Was a 'plate' | 'dumbbell' BINARY until
      // 2026-07-15 — which could only ever name two of the eleven products, and
      // resolved every other one to the bumper plate by falling through
      // `!== 'dumbbell'`. The registry keys off the same {category, kind} pair the
      // gate's pills, the quote form, and product-info.js already use, so there's
      // one vocabulary for "which product is this" across the whole tool.
      // Seeded from the buyer's left-most gate pick (main.js); this default only
      // survives a resumed session that somehow has no pick.
      product: DEFAULT_PRODUCT_KEY,
      // The landing-gate multi-select (2026-07-14): [{category, kind}], seeded before
      // the tool shell ever mounts. Distinct from productCategory/productKind above,
      // which track the SINGLE "active" pick driving the live 3D + Step 3's prefill.
      selectedProducts: [],
      // Landing-gate CARD selection (2026-07-14 redesign) — which of the 4
      // category cards the buyer picked, before any kind-level detail.
      selectedCategories: [],
      is3DRevealActive: false,
      palette: null, // palette-extraction chips (provenance, not snapshotted)
      hasLogo: false,
      // The plate has been DRESSED at least once — i.e. a real design exists on it (Ryan 2026-07-25).
      // Distinct from `hasLogo`, which flips the moment an image lands, several beats before
      // "Design my Weights" is pressed. Anything that would COMMIT a change (and therefore draw the
      // plate + play the reveal spin) has to wait for this, or it pre-empts the build: the size
      // dials seed their 75% default from the engine, and seeding is a commit.
      hasBuilt: false,
      // This session was opened from a SHARE LINK (`?d=`) rather than started here — Phase D2.
      // Set by main.js's boot after the design restores; `sharedLinkFailed` marks an id that
      // couldn't be fetched, so the status bar can say so once it exists (there is nowhere to
      // put a message that early in the boot).
      isSharedView: false,
      sharedLinkFailed: false,
      /* Every model a shared link carried, `[{product, design}]` in switcher order — what
         presentation mode's arrows walk. Empty for a single-product share and for any link written
         before sets existed; "no set" and "a set of one" are the same thing to every reader. */
      sharedSet: [],
      selectedElementId: profile.elements[0] ? profile.elements[0].id : null,
    },
    history: { undo: [], redo: [] },
  };
  resetHistory(state);
  return state;
}

// ---------------------------------------------------------------------------
// Snapshot (just the serialized profile now)
// ---------------------------------------------------------------------------

export function designSnapshot(state) {
  return { profile: serializeProfile(state.designProfile) };
}

/**
 * The undo/redo snapshot — the design PLUS the picked set.
 *
 * ⭐ Ryan 2026-08-09: *"when they remove all 5 by pressing the 'Competition Rubber' button, they
 * can't then press the undo button in the top right to undo it. They should be able to."* Right —
 * un-picking a product with five designed configurations is the single most destructive thing on
 * that tab, and it was the one action the undo button could not reach.
 *
 * ⚠ WIDER THAN `designSnapshot`, DELIBERATELY, and the two must stay separate. `designSnapshot` is
 * also what `toAutosave` writes, and the autosave carries `picks` on its ENVELOPE instead — so
 * folding them into the design would write the same list twice and give `restorePicks` a second,
 * competing source. History gets its own wider view; the save keeps its shape.
 * ⚠ `product` rides along because undoing back to a set that no longer contains the product on the
 * stage has to know where to put the buyer, and "wherever they happen to be standing" is not it.
 */
export function serializeSnapshot(state) {
  return JSON.stringify({
    ...designSnapshot(state),
    picks: state.ui.selectedProducts.map((p) => serializePick(p)),
    product: state.ui.product,
  });
}

/**
 * Has the buyer given us a logo? (Ryan 2026-07-24 bug.)
 *
 * TRUE for a logo currently ON the plate *or* one merely REMEMBERED in `savedLogo`. That second half
 * is the fix: `savedLogo` is where the logo lives whenever the active layout is TEXT-ONLY, and this
 * flag gates the Customize Design + Request Quote tabs. Deriving it from `elements` alone meant a
 * design saved on a text-only layout resumed as "no logo": the Add Logo pane happily showed the logo
 * (hydrate() has always fallen back to savedLogo) and the identity fields were pre-filled, but tabs 3
 * and 4 were disabled and the resume landed on step 2 — a dead end with no way forward.
 *
 * Not resume-specific either: switching to a text-only layout mid-session would re-lock the tool the
 * same way. The gate's intent is "has a logo been provided YET" (it exists to stop a cold visitor
 * wandering into an empty Design step), and a remembered logo satisfies that.
 */
export function deriveHasLogo(profile) {
  const p = profile || {};
  if ((p.elements || []).some((e) => e.type === 'logo' && e.logo && e.logo.dataUrl)) return true;
  return !!(p.savedLogo && p.savedLogo.dataUrl);
}

/**
 * Has the plate actually been DRESSED — i.e. has "Design My Weights" ever run for this design?
 *
 * ⚠ THE DIFFERENCE FROM `deriveHasLogo` IS THE `savedLogo` FALLBACK, and it is the whole point.
 * Between the upload and the build, the artwork sits in `savedLogo` with nothing placed on the
 * plate — a logo on file, but a blank weight. Only an ON-PLATE logo element proves the build ran.
 *
 * ⛔ DO NOT ASSIGN THIS TO `state.ui.hasBuilt` ON RESTORE. That was the first attempt and it broke
 * resume outright — a WHITE SCREEN, reproduced: `design-panel.js` reads `state.ui.hasBuilt` to mean
 * something else entirely, *"the size dials may now seed their defaults"*. Setting it true while
 * rehydrating switched `seedDefault` on during panel construction, so `wrap.seed` → `onInput` →
 * `valueCommit` called `ctx.requestRender` before the context was wired: `TypeError: ctx.requestRender
 * is not a function`, mount aborted, blank page.
 *   `hasBuilt` is a LIVE-SESSION flag about what the dials may do. "Does a design exist?" is a
 * different question, and this function is the answer to it — callers ask it directly rather than
 * routing a second meaning through one flag (CLAUDE.md §0b: when two places need the same answer,
 * one of them will be wrong; here it was one flag holding two answers).
 */
export function deriveHasBuilt(profile) {
  const p = profile || {};
  return (p.elements || []).some((e) => e.type === 'logo' && e.logo && e.logo.dataUrl);
}

/** Applies a composite snapshot back into `state` (mutates in place). */
export function applySnapshot(state, snapshot) {
  const snap = typeof snapshot === 'string' ? JSON.parse(snapshot) : snapshot;
  const p = (snap && snap.profile) || {};
  /* ⚠ ONLY WHEN THE SNAPSHOT CARRIES THEM. This function is the doorway for opened share links and
     resumed designs too, and those snapshots predate `picks` — restoring an absent list would empty
     the buyer's selection on the way in. Absent means "this snapshot has nothing to say about the
     picked set", never "the picked set was empty". */
  if (Array.isArray(snap && snap.picks)) {
    state.ui.selectedProducts = snap.picks.map((x) => ({
      category: x.category, kind: x.kind,
      ...(Number(x.instance) > 1 ? { instance: Number(x.instance) } : {}),
      ...(Number(x.weight) > 0 ? { weight: Number(x.weight) } : {}),
      ...(Number(x.wAt) > 0 ? { wAt: Number(x.wAt) } : {}),
    }));
    /* ⛔ THE TICK MUST OUTRANK WHAT IT RESTORED. `denomTick` is a module counter starting at 0, so a
       save carrying `wAt: 7` would come back holding stamps the NEXT tap could not beat — every new
       choice would score 1 and read as older than everything on file, and the displacement queue
       would run backwards for the rest of the session. Restoring state has to restore its clock. */
    for (const p of state.ui.selectedProducts) {
      if (Number(p.wAt) > denomTick) denomTick = Number(p.wAt);
    }
    if (snap.product) state.ui.product = snap.product;
  }
  state.designProfile = {
    ...createDefaultProfile(),
    ...p,
    // Spread AFTER the defaults, so a saved `secondary: null` (one-tone logo) stays null instead of
    // being back-filled with the default on resume.
    palette: { primary: '#1E3A8A', secondary: '#FBBF24', tertiary: null, ...(p.palette || {}) },
    elements: Array.isArray(p.elements) ? p.elements.map((e) => ({ ...e })) : [],
  };
  // ── HEAL A LAYOUT ID THAT NO LONGER EXISTS (Ryan 2026-07-30) ──────────────────────────────────
  // A saved design outlives the preset list. Delete a layout — as 166 deleted the grip's mirrored
  // pair — and every autosave still naming it carries a **dangling id**, which `getLayoutPreset`
  // resolves by falling back to `LAYOUT_PRESETS[0]`: a BUMPER preset. Anything that then asks "what
  // family is this design on?" is told `plate`, whatever product is actually on the stage.
  //
  // That is what broke hopping grip → bumper: the hop only rebuilds the elements when the families
  // differ, the dead grip id reported itself as `plate`, so the rebuild was skipped and the bumper
  // rendered the grip's arrangement — a logo at 12 o'clock with two numbers below — while the panel
  // highlighted Varsity Arch and "View all" highlighted nothing. Three symptoms, one dangling id.
  //
  // Healed HERE because this is the doorway every restored design comes through, so it is fixed once
  // for saves already sitting in someone's browser rather than at each of the places that read it.
  // The composition is asked first, exactly as the picker does — `elements` are the truth and the id
  // is only a label — and the family default is the backstop when even that doesn't match.
  if (!LAYOUT_PRESET_IDS.includes(state.designProfile.layoutPreset)) {
    const fam = layoutFamilyFor(state.ui.product);
    state.designProfile.layoutPreset = matchPresetToComposition(
      state.designProfile, LAYOUT_PRESETS.filter((pr) => layoutPresetProduct(pr) === fam),
    ) || FAMILY_DEFAULT_PRESET[fam] || DEFAULT_LAYOUT_PRESET;
  }
  /* A design saved before the hand-styled latch existed has no answer on the wire, and the spread
     above has just given it the default `false` — which would tell the holes flip that a design
     somebody spent an evening colouring is still the app's to re-derive. Read it off the elements
     once, on the way in (Ryan 2026-08-08). Keyed on `undefined`, not on falsiness, so it is a true
     one-time migration: everything written from today carries a real answer, including every
     undo/redo snapshot, and a buyer who deliberately RESET their colours keeps that `false`. */
  if (p.styleUserEdited === undefined) {
    state.designProfile.styleUserEdited = inferHandStyledForMigration(state.designProfile);
  }
  // Self-heal a design SAVED with the pre-fix bug (identity present, but the plate text
  // stuck on the placeholder) so a resume shows the buyer's name, not "YOUR TEXT".
  reseedIdentityIntoPlaceholders(state.designProfile);
  state.ui.weight = Number.isFinite(state.designProfile.weightValue) ? state.designProfile.weightValue : DEFAULT_WEIGHT;
  state.ui.unit = state.designProfile.unit === 'kg' ? 'kg' : 'lb';
  state.ui.hasLogo = deriveHasLogo(state.designProfile);
  if (!state.designProfile.elements.find((e) => e.id === state.ui.selectedElementId)) {
    state.ui.selectedElementId = state.designProfile.elements[0] ? state.designProfile.elements[0].id : null;
  }
}

// ---------------------------------------------------------------------------
// Undo / redo — baseline-in-undo-stack model
// ---------------------------------------------------------------------------

export function resetHistory(state) {
  state.history.undo = [serializeSnapshot(state)];
  state.history.redo = [];
}

export function record(state) {
  const snap = serializeSnapshot(state);
  const undo = state.history.undo;
  if (undo.length && undo[undo.length - 1] === snap) return false;
  undo.push(snap);
  if (undo.length > UNDO_LIMIT) undo.shift();
  state.history.redo.length = 0;
  return true;
}

export function canUndo(state) {
  return state.history.undo.length > 1;
}
export function canRedo(state) {
  return state.history.redo.length > 0;
}

export function undo(state) {
  if (!canUndo(state)) return false;
  const current = state.history.undo.pop();
  state.history.redo.push(current);
  applySnapshot(state, state.history.undo[state.history.undo.length - 1]);
  return true;
}

export function redo(state) {
  if (!canRedo(state)) return false;
  const next = state.history.redo.pop();
  state.history.undo.push(next);
  applySnapshot(state, next);
  return true;
}

// ---------------------------------------------------------------------------
// Autosave
// ---------------------------------------------------------------------------

export function toAutosave(state) {
  return JSON.stringify({
    v: AUTOSAVE_VERSION,
    ts: Date.now(),
    step: state.ui.currentStep,
    design: designSnapshot(state),
    // The picked SET + the live product are UI state, not part of the design PROFILE
    // designSnapshot serializes — but a resumed session needs them, or the Pick
    // Weights panel opens with no picks highlighted and the stage falls back to the
    // default product (Ryan 2026-07-17). They ride the autosave ENVELOPE, deliberately
    // NOT designSnapshot, so undo/redo (which shares it via serializeSnapshot) is
    // untouched. Additive + version-neutral: an old save simply lacks these keys and
    // restores as before. See restorePicks() below.
    /* `instance` rides along ADDITIVELY and version-neutrally, exactly as `picks` itself did: a
       save written before multi-configuration existed has no `instance` on any pick and restores
       as instance 1, which is what it always was. Nothing to migrate — see the INSTANCE block in
       products.js for why the first instance is spelt with no suffix. */
    picks: state.ui.selectedProducts.map((p) => serializePick(p)),
    product: state.ui.product,
    // The RAW logo-extraction palette ({confidence, all:[{hex,population}]}) is UI
    // state, not part of the design PROFILE — like picks/product it rides the ENVELOPE,
    // NOT designSnapshot, so undo/redo is untouched. Without it, a resumed session lost
    // `hasEx`: the Colors caption wrongly read "We couldn't read colors from this logo"
    // and the chip row fell back to the generic starter set instead of the logo's own
    // extracted colors (Ryan 2026-07-19). Additive + version-neutral (old saves lack it).
    palette: state.ui.palette || null,
  });
}

/** Can this build read an envelope of this version at all — natively or by migrating it? */
export function isReadableEnvelopeVersion(v) {
  return v === AUTOSAVE_VERSION || v === AUTOSAVE_VERSION_MIGRATABLE;
}

/**
 * Bring an envelope up to `AUTOSAVE_VERSION`, IN PLACE. Currently one hop: v2 → v3.
 *
 * ⛔ v2 STORED THE WEIGHT IN THE DISPLAY UNIT; v3 STORES POUNDS. So a v2 design saved in kg holds
 * `weightValue: 20` meaning 20 kg, and handing that number to a v3 build would put a genuine 20 LB
 * plate on the stage — the very shrink this whole change removes, re-created at the load door.
 *
 * ⚠ AND THE SNAP IS NOT DECORATION. 20 kg is 44.09 lb, which is not a plate anyone makes. Landing
 * on the nearest real rung of that product's own ladder is what makes the restored design a
 * DENOMINATION again rather than a number — `getLayoutPreset`'s "an unknown id resolves to a
 * bumper" lesson in the same shape: an off-chart weight renders through a nearest-row fallback and
 * says nothing. A product with no ladder (the free-entry fixed barbell) keeps the raw conversion.
 *
 * ⚠ IDEMPOTENT BY VERSION, NOT BY VALUE. There is no way to tell a v2 kg weight from a v3 one by
 * looking at it, so this must run exactly once, keyed on `data.v`, at an EXTERNAL door only.
 * Undo/redo goes through `applySnapshot` directly and must never reach this.
 */
function migrateEnvelope(data) {
  if (!data || data.v !== AUTOSAVE_VERSION_MIGRATABLE) return data;
  const profile = data.design && data.design.profile;
  if (profile && profile.unit === 'kg' && Number.isFinite(Number(profile.weightValue))) {
    const lb = Number(profile.weightValue) / KG_PER_LB;
    const ladder = designableWeights(data.product || state_productOf(data));
    profile.weightValue = Array.isArray(ladder) && ladder.length
      ? ladder.reduce((a, b) => (Math.abs(b - lb) < Math.abs(a - lb) ? b : a), ladder[0])
      : Math.round(lb * 10) / 10;
  }
  data.v = AUTOSAVE_VERSION;
  return data;
}

/** The product key an envelope names, tolerating the older saves that carry none. */
function state_productOf(data) {
  return (data && typeof data.product === 'string') ? data.product : DEFAULT_PRODUCT_KEY;
}

export function fromAutosave(json, state) {
  try {
    const data = migrateEnvelope(JSON.parse(json));
    if (!data || data.v !== AUTOSAVE_VERSION || !data.design) return { restored: false };
    applySnapshot(state, data.design);
    if (Number.isInteger(data.step) && data.step >= 1 && data.step <= 3) {
      state.ui.currentStep = data.step;
    }
    // Restore the raw extraction palette from the envelope (see toAutosave) so the
    // Colors section resumes with hasEx TRUE — right caption + the logo's real colors.
    // Tolerant of old saves (no `palette` key) and malformed data → leave it null.
    state.ui.palette = data.palette && Array.isArray(data.palette.all) ? data.palette : null;
    restorePicks(state, data);
    resetHistory(state);
    return { restored: true, savedAt: Number.isFinite(data.ts) ? data.ts : null };
  } catch {
    return { restored: false };
  }
}

/**
 * Restore the picked set + live product from an autosave envelope (see toAutosave).
 * applySnapshot only rebuilds the design PROFILE; the picks live in `state.ui`, so
 * without this a resumed session shows no highlighted weights in Pick Weights and
 * the stage defaults to the bumper. Tolerant of OLD saves (no `picks`/`product`
 * keys): it just leaves the empty defaults, exactly as before this fix.
 */
function restorePicks(state, data) {
  const keyOf = (p) => productKey(p.category, p.kind, instanceOf(p));
  /* ⚠ DE-DUPLICATED ON THE FULL KEY, which it never needed to be while every pick was unique by
     construction. `instance` now comes off the wire, and two picks landing on the same number
     would be two entries the switcher shows twice and the quote form counts twice — one config's
     quantities, entered against one key, billed to both. Cheap to refuse; expensive to discover. */
  const seen = new Set();
  const picks = (Array.isArray(data.picks) ? data.picks : [])
    .filter((p) => p && typeof p.category === 'string' && typeof p.kind === 'string')
    .map((p) => ({ category: p.category, kind: p.kind, instance: instanceOf(p) }))
    .filter((p) => { const k = keyOf(p); if (seen.has(k)) return false; seen.add(k); return true; });

  // Live product: the saved one if it's still among the picks, else the left-most
  // pick (the same seed the gate uses), else the default already on state.ui.product.
  let key = typeof data.product === 'string' ? data.product : null;
  if (!key || !seen.has(key)) {
    key = picks.length ? keyOf(picks[0]) : state.ui.product;
  }

  // INVARIANT: the product on the stage is always a pick — you can't view a product
  // you didn't pick. A fresh save already satisfies this; an OLD save (no `picks`
  // key) doesn't, so seed the one product being shown. Without this, resuming a
  // pre-fix design opens Pick Weights completely blank — no category badges the
  // weight it's clearly displaying (Ryan 2026-07-17).
  const { category, kind, instance } = parseProductKey(key);
  if (category && !seen.has(key)) picks.push({ category, kind, instance });

  state.ui.selectedProducts = picks;
  // Gate-card selection = the distinct categories among the picks. Derived, not
  // stored, so it can never drift out of sync with the picks it summarises.
  state.ui.selectedCategories = [...new Set(picks.map((p) => p.category))];
  state.ui.product = key;
  /* ⚠ THROUGH `parseProductKey`, not `slice`. These two hand-rolled slices predate the instance
     suffix and would have written `productKind = 'Competition Urethane#2'` — a kind that matches
     no registry row, no CATEGORIES entry and no pick, which is exactly the class of silent
     mismatch the suffix was designed not to cause. The parser strips it; nothing else should try. */
  if (category) {
    state.ui.productCategory = category;
    state.ui.productKind = kind;
  }
}

// ---------------------------------------------------------------------------
// Profile mutations (mutate `state`; UI calls these then re-renders + records)
// ---------------------------------------------------------------------------

/** Apply a layout preset — rebuilds elements. Returns success. */
export function setLayoutPreset(state, id) {
  if (!LAYOUT_PRESET_IDS.includes(id)) return false;
  /* ⛔ STAMP THE LAYOUT BEING LEFT, THEN RESTORE THE ONE ARRIVED AT. Every layout change in the app
     comes through this function (the carousel, View all, the Wording promotion — see the note below
     on why the re-seed lives here), so this is the seam, exactly as the product hop in main.js is. */
  rememberWeightNudge(state, weightNudgeKey(state));
  applyLayoutPreset(state.designProfile, id);
  restoreWeightNudge(state, weightNudgeKey(state, { preset: id }));
  // Restore the buyer's identity into any placeholder slots this layout re-seeded (e.g.
  // after switching through a text-less layout dropped the arc text) — see the helper.
  reseedIdentityIntoPlaceholders(state.designProfile);
  // RE-PEG the default sizes to the new layout's room (Ryan 2026-07-26: "I noticed the logo was too
  // big… the selector had changed to 85% despite me never touching it").
  //   Since entry 85 the dials read as a share of the room, while the profile stores an absolute
  // multiplier — and a layout change moves the room. 75% of a Varsity Arch's logo slot is a bigger
  // multiplier than 75% of a Fieldhouse's, so cycling through layouts left the stored scale frozen
  // at the FIRST layout's number while the readout climbed to whatever fraction that had become. The
  // mark really was too big; the dial was reporting it accurately.
  //   Re-seeding is what makes "the default is 75%" true of the design rather than of one moment in
  // it. `seedDefaultSizes` honours `sizeUserSet` throughout, so it can only ever move a piece nobody
  // has sized by hand — which is exactly the rule Ryan stated: only the −/+ changes that number.
  //   Here rather than in the panel, and pure state rather than a commit: every layout change in the
  // app goes through this function (the carousel, View all, the Wording promotion), the plate is
  // then correct in its FIRST painted frame, and the Add Logo tab's dial — which never re-renders
  // and so cannot re-seed itself — simply reads the corrected value on its next sync.
  seedDefaultSizes(state.designProfile, {
    product: modelFor(state.ui.product),
    pct: DEFAULT_SIZE_PCT,
  });
  state.ui.selectedElementId = state.designProfile.elements[0] ? state.designProfile.elements[0].id : null;
  state.ui.hasLogo = deriveHasLogo(state.designProfile);
  return true;
}

/**
 * If the design is "bare" — no weight/arctext, e.g. just an uploaded logo on the
 * blank plate — seed the current/default LAYOUT so the plate shows a COMPLETE
 * design (the numbered "Design 1"), not a lone logo (Ryan 2026-07-13). The upload
 * was previously landing only a logo element while the preview showed the full
 * default layout — a confusing mismatch. applyLayoutPreset migrates the uploaded
 * logo into the seeded layout. No-op once a real layout exists, so re-uploading
 * never wipes the design. Returns true if it seeded.
 */
export function seedDefaultLayoutIfBare(state) {
  const els = state.designProfile.elements || [];
  if (els.some((e) => e.type === 'weight' || e.type === 'arctext')) return false;
  applyLayoutPreset(state.designProfile, state.designProfile.layoutPreset || DEFAULT_LAYOUT_PRESET);
  state.ui.selectedElementId = state.designProfile.elements[0] ? state.designProfile.elements[0].id : null;
  return true;
}

/**
 * Smart-start the opening design (Fable §3.5) — NEVER a placeholder on a first
 * render. Picks the layout + fills real text from what the buyer gave us:
 *  • name + mascot → Varsity Arch (name arched top, mascot arched bottom).
 *  • name only     → twin-arch (name top + name bottom, same geometry).
 *  • nothing       → logo-led (Big Badge for square-ish logos, Fieldhouse for
 *                    wide), weight opposite, NO text pieces at all.
 * The extracted palette + auto-balance are already applied elsewhere; this just
 * seeds the elements. Pure (mutates `state`). Returns the chosen preset id.
 *
 * ⚠ IT MUST STAY INSIDE THE CURRENT PRODUCT'S LAYOUT FAMILY. Every preset named
 * above is a BUMPER preset, and this used to apply them to whatever was on the
 * stage — so uploading a logo onto a grip plate threw away grip-classic and
 * arched the school's name straight across the hand cutouts and off the plate
 * edge (verified in the browser, 2026-07-15). The same was true of the dumbbell
 * long before the grip existed; it was just less obvious, because a bumper preset
 * on a dumbbell head lands in plausible-looking places.
 *
 * The name/mascot upgrade only applies to the PLATE family, because it's the only
 * one with arc-text slots. On the grip and the dumbbell the buyer's answer to
 * "Who's this for?" currently seeds nothing — an honest gap (those faces have no
 * arched-text layouts yet) and NOT something to paper over by inventing one here.
 */
export function smartStart(state, { name = '', mascot = '' } = {}) {
  const nm = String(name || '').trim();
  const ms = String(mascot || '').trim();
  // Remember the answers on the profile so the Add Logo tab's persistent identity
  // fields (and a resume) stay in sync with the plate text (Ryan 2026-07-18).
  state.designProfile.orgName = nm;
  state.designProfile.mascot = ms;
  const family = layoutFamilyFor(state.ui.product);
  let presetId;
  let topText = null;
  let bottomText = null;
  /* ⛔ THE DUMBBELL'S TEXT BRANCH IS GONE — A PRODUCT'S DEFAULT IS ITS DEFAULT (Ryan 2026-08-08:
   * *"even when text is included, I want the defaults as previously discussed to be the defaults
   * across all weight types, which in the case of dumbbells, is the stacked."*).
   *
   * It used to send a named dumbbell to `db-crest` (2026-07-30) on the argument that a buyer who
   * typed a name must not land on a text-less layout and watch it vanish. That reasoning was sound
   * about the SYMPTOM and wrong about the remedy: Crest is 8/12 in the picker, so typing a name
   * silently moved the buyer seven layouts away from the one the product opens on, and the first
   * thing they saw was not the default they were told to expect.
   *
   * ⚠ THE NAME IS NOT LOST, which is what makes this safe. `orgName` / `mascot` are written to the
   * profile above this branch, unconditionally — they are identity, not layout. The persistent
   * Organization / Mascot fields on Add Logo keep showing them, and `reseedIdentityIntoPlaceholders`
   * fills the arcs from the same source the moment the buyer picks Crest, Signet or Sidelines. So
   * the words are held, not displayed, until a layout exists to display them on.
   *
   * ⚠ NOTHING CHANGES FOR THE BUMPER. `FAMILY_DEFAULT_PRESET.plate` IS `logo-l-weight-r-arch-tb`
   * (Varsity Arch) — the same id the text branch below already hard-codes — so "always use the
   * family default" and the existing rule agree there. The logo-only Fieldhouse case below is a
   * deliberate DEPARTURE from the family default and is left exactly as it was. */
  if (family !== 'plate') {
    // No arc-text slots reached on this path → the family's flagship, text unseeded.
    presetId = FAMILY_DEFAULT_PRESET[family];
  } else if (nm || ms) {
    // B2: derive the two arc lines with common sense about school names — drop/expand
    // "High School" / "HS" / "Jr HS" suffixes, split by balance, mascot below the
    // place name. deriveArcText owns all the rules; the buyer edits the result on
    // Customize Design's Top Text / Bottom Text fields.
    presetId = 'logo-l-weight-r-arch-tb';
    const t = deriveArcText({ name: nm, mascot: ms });
    topText = t.top;
    bottomText = t.bottom;
  }
  else {
    // Logo-only → Fieldhouse (logo left, weight right). NEVER a Badge/big-logo
    // default: the big-logo look is high-risk (Ryan 2026-07-17) — if a buyer dislikes
    // it they may bounce before finding the layout switcher. Fieldhouse is the safe,
    // legible default; a logo + text upgrades to Varsity Arch above.
    presetId = 'logo-l-weight-r';
  }
  applyLayoutPreset(state.designProfile, presetId);
  if (topText != null || bottomText != null) {
    const els = state.designProfile.elements;
    const top = els.find((e) => e.type === 'arctext' && e.slot === 'top');
    const bot = els.find((e) => e.type === 'arctext' && e.slot === 'bottom');
    if (top) top.text = topText || '';
    if (bot) bot.text = bottomText || '';
  }
  // applyLayoutPreset already stamps the B1 auto-style onto any freshly-built
  // text/weight pieces (and migrated pieces carry their own outlines), so the whole
  // design is logo-matched here without an extra pass.
  state.designProfile.layoutPreset = presetId;
  // AFTER the elements exist. applyLogoStructure runs before this on a first upload and its pieces
  // are thrown away here, so the vanishing-ink defaults have to be re-applied to the ones that ship.
  //   The band overlay is re-derived for the same reason and in the same breath (Ryan 2026-07-26):
  // the recoloured tones survive the rebuild on the element (applyLayoutPreset carries them with the
  // art), but the per-band overrides that paint them are derived, and these are brand-new elements.
  // Before `applyVanishingLogoDefaults`, so its "would this mark vanish?" verdict is passed the
  // colours the logo is actually wearing.
  applyToneSwapsToBands(state.designProfile);
  applyVanishingLogoDefaults(state.designProfile);
  state.ui.selectedElementId = state.designProfile.elements[0] ? state.designProfile.elements[0].id : null;
  state.ui.hasLogo = deriveHasLogo(state.designProfile);
  return presetId;
}

/**
 * Update the plate's arc text from the buyer's Organization / Mascot answers WITHOUT
 * changing their layout or styling (Ryan 2026-07-18 — the Add Logo tab's persistent
 * identity fields edit the text live). This is the "editor" counterpart to smartStart's
 * one-time "wizard" seed:
 *   • Stores the answers on the profile (persisted, re-fills the fields on resume).
 *   • If the current layout already has arc-text slots → set their text only (preserve
 *     the buyer's layout / fonts / outlines — they may have customised).
 *   • If there are NO arc slots yet (a logo-led layout) AND a name/mascot is given on a
 *     PLATE → fall back to smartStart to INTRODUCE the arched layout (a name warrants
 *     text). Non-plate faces have no arc-text layouts yet, so the value is stored but
 *     nothing renders — the same honest gap smartStart documents.
 * @returns {{changed: boolean}}
 */
export function applyIdentityText(state, { name = '', mascot = '' } = {}, { force = false } = {}) {
  const p = state.designProfile;
  const nm = String(name || '');
  const ms = String(mascot || '');
  p.orgName = nm;
  p.mascot = ms;
  const els = p.elements || [];
  const top = els.find((e) => e.type === 'arctext' && e.slot === 'top');
  const bot = els.find((e) => e.type === 'arctext' && e.slot === 'bottom');
  const derived = deriveArcText({ name: nm, mascot: ms });
  // ── THREE GAPS, AND THIS FUNCTION ONLY KNEW ABOUT TWO (Ryan 2026-07-30) ────────────────────────
  // *"I tested typing in 'Arcadia High School' into the organization field, and then on the Customize
  // Design tab, the wording transposed 'Arcadia' into the Top Arch Text field, but the Lower-left text
  // and Lower-right text were both left blank."*
  //
  // Exactly that: `top` matched, `bot` did not exist on a grip plate, and the two lower gaps were
  // never looked for — so the top arch got `derived.top`, which is HALF of a two-way split, and the
  // rest of the name was dropped on the floor.
  //
  // This is the Add-Logo tab's writer; `reseedIdentityIntoPlaceholders` is the layout-change one, and
  // it grew its three-arc branch when the Tri- layouts shipped. This one did not, which is why the
  // bug only appeared when typing the name rather than when switching layouts. Both now ask
  // `deriveArcTextTri`, so the two routes into the same three gaps cannot disagree.
  //   It DECLINES the same way too: a name that cannot make three pieces writes nothing at all,
  // rather than filling one gap and leaving two blank — which is what the buyer just saw.
  const triSlots = ['top', 'left', 'right'].map((s) => els.find((e) => e.type === 'arctext' && e.slot === s));
  const arcCount = els.filter((e) => e.type === 'arctext').length;
  if (arcCount === 3 && triSlots.every(Boolean)) {
    const words = triWordsFor(nm, ms);
    triSlots.forEach((e, i) => {
      if (force || e.textUserSet !== true) { e.text = words[i] || ''; if (force) e.textUserSet = false; }
    });
    return { changed: true };
  }
  if (top || bot) {
    // A line the buyer typed on Customize Design → Text is THEIRS (Ryan 2026-07-25). deriveArcText's
    // balance-split is a starting guess — "Big Johns Gym" lands as BIG / JOHNS GYM, and plenty of
    // people would rather repeat the whole name on both arcs. Once they've said so, editing the org
    // name here must not silently undo it. `textUserSet` is cleared by emptying the field, which is
    // how they hand the line back to the auto-split.
    //   `force` is the buyer typing in THESE fields, right now (Ryan 2026-07-25: "if you update the
    // AL tab text, when you go back to the CD tab it should update there too"). The guard above
    // exists to stop an INCIDENTAL re-derive — a layout switch, a resume — from undoing a hand-typed
    // arc. A deliberate edit of the name is not incidental: it is the most recent thing the buyer
    // said about these words, and last edit wins whichever screen it came from. The flag is cleared
    // with it so the two lines are back in step.
    if (top && (force || top.textUserSet !== true)) { top.text = derived.top || ''; if (force) top.textUserSet = false; }
    if (bot && (force || bot.textUserSet !== true)) { bot.text = derived.bottom || ''; if (force) bot.textUserSet = false; }
    return { changed: true };
  }
  // ARCS THIS FUNCTION CANNOT NAME — the same gap `reseedIdentityIntoPlaceholders` had, and for the
  // same reason (2026-08-02). Standing on `db-sidelines` and typing an organisation name wrote to
  // nothing: no `top`, no `bot`, no triple, so it fell straight through to the smart-start fallback
  // below — which is for a design with NO arcs at all and would have replaced the buyer's layout.
  const otherArcs = els.filter((e) => e.type === 'arctext');
  if (otherArcs.length && fillUnnamedArcs(otherArcs, nm, ms, { force })) return { changed: true };
  if ((nm.trim() || ms.trim()) && layoutFamilyFor(state.ui.product) === 'plate') {
    smartStart(state, { name: nm, mascot: ms }); // logo-led → arch tb, so the text can show
    return { changed: true };
  }
  return { changed: false };
}

/**
 * The three gaps' words, ALWAYS — where `deriveArcTextTri` answers the stricter question "can this
 * identity fill three gaps properly?" and is entitled to say no.
 *
 * Both questions are real and they are not the same one. Eligibility asks the strict version, because
 * a layout that cannot be filled should not be offered. But a buyer already STANDING on a Tri- layout
 * who shortens their organisation name to two words has to see the new name — and the first cut of
 * this wrote nothing at all in that case, leaving the previous school's name sitting on the plate
 * after it had been typed over. Stale wording is worse than a visibly empty third gap: one of them
 * looks like a bug and the other looks like the requirement it is.
 *
 * So the fallback splits what there is across the first two gaps and BLANKS the third. That also keeps
 * the picker honest without any extra bookkeeping — the design now carries two worded arcs, which is
 * short of the three `presetEligibility` counts, so the card correctly shows as locked.
 */
/**
 * THE WHOLE IDENTITY, ON ONE LINE — the string a single arch shows, and the string any layout whose
 * arcs this file cannot name by hand gets re-cut from.
 *
 * Lifted out of `reseedIdentityIntoPlaceholders` on 2026-08-02 so `applyIdentityText` can ask the same
 * question and get the same answer. It was inline in one of the two writers, which is the shape
 * CLAUDE.md §0b warns about: the Add Logo tab and the layout-change path both decide what the arches
 * say, and only one of them knew how to join.
 *
 * The join REFUSES to say the same words twice — it declined already when the two halves were
 * identical (the twin-arch case), and that was extended to the half that merely CONTAINS the other,
 * which is the shape a corrupted identity takes: `{ name: "Denver Broncos", mascot: "Broncos" }` joins
 * to "Denver Broncos Broncos" (Ryan 2026-07-28). The pair is prevented from reaching that state at its
 * source — design-panel's `syncIdentityFromArcs` — and this is the second line of defence at the point
 * where a duplicate would actually reach the plate. Worth having both: the same class of bug has now
 * surfaced twice by two different routes, and only one of them was the route fixed the first time.
 */
function wholeIdentityLine(nm, ms) {
  const derived = deriveArcText({ name: nm, mascot: ms });
  const dTop = String(derived.top || '').trim();
  const dBot = String(derived.bottom || '').trim();
  const dupe = !!dTop && !!dBot
    && (dTop === dBot
      || dTop.toLowerCase().endsWith(` ${dBot.toLowerCase()}`)
      || dBot.toLowerCase().startsWith(`${dTop.toLowerCase()} `));
  return (dTop && dBot && !dupe)
    ? `${dTop} ${dBot}`
    : (dupe ? (dTop.length >= dBot.length ? dTop : dBot) : (dTop || dBot || nm || ms));
}

/**
 * The arcs this file cannot name by hand — anything that is not the `{top,bottom}` pair or the
 * `{top,left,right}` triple (2026-08-02).
 *
 * Both identity writers below grew a branch per shape as products arrived, and both stopped at the
 * two shapes the bumper and the grip plate have. The dumbbell's `db-sidelines` arches its two lines at
 * `left` and `right`, so it matched neither: `reseedIdentityIntoPlaceholders` fell into the ONE-arc
 * `else`, where `top || bot` is undefined and nothing is written at all, and `applyIdentityText` fell
 * past its `if (top || bot)` to the smart-start fallback. Arriving there from a text-less layout left
 * raw YOUR TEXT / GOES HERE on the plate with the buyer's organisation name sitting one tab away —
 * the exact bug `reseedIdentityIntoPlaceholders` exists to fix, re-opened by a slot pair it had never
 * been told about.
 *
 * Answered by COUNT rather than by another named shape, so the next product's zone cannot re-open it.
 * Element order is the layout's own, which is the only ordering either writer has ever had.
 */
function fillUnnamedArcs(arcs, nm, ms, { force = false } = {}) {
  const parts = distributeAcrossArcs(wholeIdentityLine(nm, ms), arcs.length);
  if (!parts.length) return false;
  arcs.forEach((e, i) => {
    if (!force && e.textUserSet === true) return;
    e.text = parts[i] || '';
    if (force) e.textUserSet = false;
  });
  return true;
}

function triWordsFor(nm, ms) {
  // THREE OR MORE PIECES → three different ones, one per gap.
  const tri = deriveArcTextTri({ name: nm, mascot: ms });
  if (tri) return [tri.top, tri.left, tri.right];

  // FEWER THAN THREE → the whole thing, REPEATED in all three (Ryan 2026-07-30: *"if someone chooses
  // 1 or 2 words, it should default to repeating those 1 or 2 words in all 3 spots… if they choose
  // 3+ words, it should open up the possibility to having different words on top vs bottom left vs
  // bottom right"*).
  //
  // Repeating is what lets these layouts drop their word gate entirely. The earlier version wrote the
  // pieces it had and left the third gap EMPTY, which is why they needed a "needs three words" lock
  // in the first place; a one-word school now simply gets its name three times, which is a real
  // design and the one the deleted mirrored layouts used to exist to provide.
  const name = String(nm || '').trim();
  const mascot = String(ms || '').trim();
  let whole;
  if (name && mascot) {
    // WITH a mascot, the school suffix drops and the mascot joins the place — `deriveArcText`'s own
    // rule, and exactly Ryan's example: "Arcadia High School" + "Anchovies" reads ARCADIA ANCHOVIES
    // rather than the whole mouthful.
    const pair = deriveArcText({ name, mascot });
    const a = String(pair.top || '').trim();
    const b = String(pair.bottom || '').trim();
    whole = (a && b && a !== b) ? `${a} ${b}` : (a || b);
  } else {
    // NAME ONLY → exactly what they typed. ⚠ NOT `deriveArcText` here: its two-arc rules drop a
    // suffix they judge lopsided against the place, which cost "Denver North City High School" its
    // "High School" on a plate that repeats the line three times. Those rules exist to BALANCE two
    // arcs against each other; there is no second arc here to balance against.
    whole = name || mascot;
  }
  return [whole, whole, whole];
}

/**
 * After a layout switch re-seeds a text layout with PLACEHOLDER arc text, restore the
 * buyer's stored identity into those placeholder slots — the fix for "the Add Logo field
 * still has my name but the plate shows YOUR TEXT / GOES HERE" (Ryan 2026-07-20). It
 * happens when you pass THROUGH a text-less layout: the real arc text has no slot to
 * migrate into and is dropped (only the logo is remembered, via savedLogo), so returning
 * to a text layout re-seeds the raw placeholder. orgName/mascot always persist, so
 * re-derive from them. Only fills slots STILL showing a placeholder — it never clobbers
 * real text the buyer carried in, and no-ops when there's no stored identity.
 *
 * EXPORTED (Ryan 2026-07-25) so the layout PREVIEWS can run it too. A preview builds its plate by
 * applying a preset to a clone, which seeds the same raw placeholders — so from a Badge layout (no
 * text pieces to migrate from) every text preset in "View all" advertised YOUR TEXT / GOES HERE,
 * then showed the real name the moment you picked one. The picker has to preview what you'd GET.
 */
export function reseedIdentityIntoPlaceholders(profile) {
  const nm = String(profile.orgName || '').trim();
  const ms = String(profile.mascot || '').trim();
  const arcs = (profile.elements || []).filter((e) => e.type === 'arctext');
  if (!arcs.length) return; // this layout has no arc-text slots
  const top = arcs.find((e) => e.slot === 'top');
  const bot = arcs.find((e) => e.slot === 'bottom');
  if (nm || ms) {
    const derived = deriveArcText({ name: nm, mascot: ms });
    // The WHOLE identity on one line, for a layout with a single arc — the SAME WORDS the two-arc
    // layout shows, joined (Ryan 2026-07-25). deriveArcText already drops a school suffix on the
    // two-arc path ("Lincoln HS" + "Tigers" -> LINCOLN / TIGERS), so putting the raw org name on a
    // single arc would have switched Varsity Arch -> Marquee from "LINCOLN / TIGERS" to "LINCOLN HS":
    // different words for the same design, which reads as a bug and undoes the whole point of
    // preserving identity across a layout change. Joining keeps the mascot and stays consistent.
    //   Guarded against the twin-arch case, where deriveArcText returns the same string for both
    // arcs — joining that would print the name twice.
    //   The join itself now lives in `wholeIdentityLine`, so the Add Logo tab's writer gets the same
    // answer — see its note for why that had to stop being inline here.
    const whole = wholeIdentityLine(nm, ms);
    // ── THE ARCHES ARE A VIEW OF THE IDENTITY (Ryan 2026-07-28) ───────────────────────────────
    // "the Organization of 'DENVER BRONCOS' should split those two words, one to the top and one to
    // the bottom, whenever I click onto the Varsity Arch layout, or the 'The Seal' layout… until I
    // override it and manually type in."
    //
    // So the rule is a derivation, not a repair: on every landing, an arch the buyer has NOT typed
    // into shows what the identity says it should — the split across two arches, the whole name on
    // one. `textUserSet` is the entire exception, and it is set only by typing into the Wording
    // field (cleared by emptying it, which is how a line is handed back to the auto-split).
    //
    // This REPLACES a pile of repair heuristics: fill-only-if-placeholder, re-split-if-the-sibling-
    // is-free, expand-if-it-matches-a-derived-half. Each was a guess at whether the app or the buyer
    // had written a line, and each guess was a way to be wrong — two of them shipped as bugs this
    // session ("DENVER BARBOS / BARBOS", then "DENVER DENVER BRONCOS"). Asking the flag outright
    // cannot drift, and it makes the arches a pure function of (identity, overrides) at every
    // layout change instead of the running total of every layout that came before.
    //
    // ⚠ Safe ONLY because every caller of this function is a layout change or a design load, never a
    // keystroke — check that before adding a fifth. Emptying an arch to turn a Varsity Arch into a
    // Marquee still works: nothing re-derives until the buyer next picks a layout, which is exactly
    // when they would expect the auto-split back.
    // THREE ARCS — the grip plate's gaps (2026-07-30). Taken first because a three-arc layout has a
    // `top` and would otherwise fall into the one-arc `else` below, which fills `top` with the whole
    // identity and leaves the other two gaps showing placeholders forever.
    //   `deriveArcTextTri` can decline (a two-word name cannot make three pieces). When it does,
    // nothing is written and the layout stays greyed out in the picker, which is the honest pairing:
    // `presetEligibility` asks the SAME function, so what the card promises and what the plate can
    // actually render are one decision, not two that can disagree.
    const triSlots = ['top', 'left', 'right'].map((s) => arcs.find((e) => e.slot === s));
    if (arcs.length === 3 && triSlots.every(Boolean)) {
      const words = triWordsFor(nm, ms);
      triSlots.forEach((e, i) => { if (e.textUserSet !== true) e.text = words[i] || ''; });
    } else if (top && bot) {
      if (top.textUserSet !== true) top.text = derived.top || '';
      if (bot.textUserSet !== true) bot.text = derived.bottom || '';
    } else if (arcs.length > 1) {
      // MORE THAN ONE ARC, but not a shape named above — the dumbbell's `{left,right}` today. Without
      // this it fell into the one-arc `else`, where `top || bot` is undefined and NOTHING was written.
      // See `fillUnnamedArcs`.
      fillUnnamedArcs(arcs, nm, ms);
    } else {
      // ONE arc slot — the WHOLE identity goes on it, never half of a two-arc split (Ryan
      // 2026-07-25). "Big Johns Gym" splits to "Big" / "Johns Gym" on the Varsity Arch; switching to
      // the Marquee, which has no bottom arc, used to leave just "BIG" on the plate with the rest of
      // the name silently gone. `derived.top` is the top HALF, not the primary line.
      const only = top || bot;
      if (only && only.textUserSet !== true) {
        only.text = whole || derived.top || derived.bottom || '';
      }
    }
  }
  // THE PLACEHOLDER IS AN INVITATION, AND ONLY AN EMPTY DESIGN NEEDS INVITING (Ryan 2026-07-25).
  // Once ANY line carries real words, a sibling slot still reading YOUR TEXT / GOES HERE is just
  // debris — it happens when a layout seeds two arcs and only one of them can be filled (a typed top
  // line with no organisation name to derive a bottom from). Blank those instead: the render skips
  // empty arc text, and the Wording field's own placeholder is where the invitation belongs now.
  // A design with NO text anywhere keeps its placeholders, which is the case Ryan carved out.
  if (arcs.some((e) => e.text && !isPlaceholderText(e.text))) {
    for (const e of arcs) if (isPlaceholderText(e.text)) e.text = '';
  }
}

// Arc-text derivation now lives in the pure leaf module `../arc-text.js` (2026-07-29) — `layouts.js`
// needs the same split and cannot import this file without closing an import cycle. Re-exported here
// so every existing importer of these names keeps working.
export {
  SPLIT_NAME_MIN_LENGTH,
  balanceSplit,
  splitNameAcrossArcs,
  parseSchoolSuffix,
  deriveArcText,
  balanceSplitN,
  deriveArcTextTri,
  distributeAcrossArcs,
} from '../arc-text.js';

/**
 * B1 — apply a detected logo STRUCTURE (logo-structure.js analyzeStructure result).
 * The logo's own layering DEFINES the palette roles and the text outline stack, so
 * the school name + weight number echo the logo's letter treatment (Elite "E" =
 * teal fill / white / rust). Only acts on a high-confidence result; otherwise the
 * caller keeps the plain applyPalette styling. Sets:
 *   • palette.primary = fill, secondary = inner outline, tertiary = outer outline
 *     (a rare 4th outline colour rides as a literal hex);
 *   • profile.autoStyle = the fill/outline template (re-stamped on new pieces by
 *     applyLayoutPreset, so every layout/side/weight matches the logo);
 *   • the current text/weight elements, immediately (covers the re-upload path,
 *     which keeps the existing design and doesn't call smartStart).
 * @returns {{applied:boolean, mode?:string, fillHex?:string, outlineHexes?:string[]}}
 */
/**
 * A logo whose colour MERGES with the plate would be invisible, and so would text carrying that
 * colour as its fill (Ryan 2026-07-25: "if they upload a black or near-black logo, the plate should
 * remain black, but the logo and font colours should default to white for contrast").
 *
 * The plate is deliberately NOT changed — black is the default and the fastest-shipping finish. What
 * moves is the INK: every text/weight fill, and the logo's own fill, default to the neutral that
 * reads against the plate.
 *   Both are stored, not merely rendered. `fillContrastRemap` already swaps a vanishing fill at PAINT
 * time, so the plate looked right while the design said something else — exactly the split that let
 * "+ Add" pick a ring against a colour nobody could see (entry 64). Storing it keeps every reader
 * agreeing.
 *   ONE-TONE ONLY, by construction: this runs on the plain-palette path where the logo has a single
 * colour, so repainting its fill can't destroy detail — there is none to lose. It writes the same
 * `logoCenterColor` the buyer's own Logo picker writes, so it is visible, editable, and reverts to
 * the original art with one click.
 */
/** How legible `hex` is against the WORST of its two neighbours — the plate behind a ring and the
 *  fill inside it. A ring has to clear both, so the weaker side is the one that decides. */
function worstReadOf(hex, ...against) {
  const others = against.map(normalizeHex).filter(Boolean);
  if (!hex || !others.length) return 0;
  return Math.min(...others.map((o) => contrastRatio(o, hex)));
}

/* Exported for `probe-number-rescue.mjs` only — the rule 3 net has to score THIS function rather
   than a reimplementation of its three lines, which is the fault WD 448 was written up for. */
export function applyVanishingLogoDefaults(p) {
  const pal = p.palette || {};
  const primary = normalizeHex(pal.primary);
  const plate = normalizeHex(p.bodyColor) || normalizeHex(DEFAULT_BODY_COLOR);
  /* ⛔ THE STAMP IS CLEARED ON ENTRY, AND THAT IS NOT TIDINESS. The corpus runner reads it PER
     PLATE and this function returns early on most plates — so a stamp left from the previous plate
     is dumped against this one and rule 3 is recorded as firing where it did not. It has been null
     on every plate of every pass so far (WD 456), so nothing has been mis-read yet; the moment its
     field is seeded it starts firing and the staleness becomes real. */
  p.__keylineStepAside = null;
  if (!primary || !plate) return false;
  /* ⛔ IT ASKED ONLY ABOUT `primary`, SO HALF OF EVERY TWO-TONE MARK WAS UNPROTECTED (Ryan
   * 2026-08-15). His report named the Layton spartan and cleared the Wallabies — *"I'm not sure why
   * the wallabies logo is working fine, but the Layton logo isn't"* — and driving both marks through
   * both of their own tones (`tools/probe-plate-dye-flatten.mjs`) shows the Wallabies failing too, on
   * the dye he had not tried. Dye the plate the mark's PRIMARY and the rescue fires; dye it the
   * SECONDARY and the same tone vanishes into the same plate with nothing looking at it. Two marks,
   * four dyes, two of them broken — the logo was never the variable, the tone's ROLE was.
   *   The function's own subject is "a colour of this mark is about to disappear into this plate",
   * and a role is not part of that question. So it asks the mark for its tones: the palette roles
   * plus whatever the structure detected. ⚠ SWATCHES ARE DELIBERATELY NOT HERE — that row is the
   * buyer's pool, including house neutrals and their own picks, and firing a whole-mark flatten
   * because the plate matched a colour that is merely AVAILABLE is a different rule than this one.
   *
   * ⚠ THE WIDENING CANNOT MOVE A TEXT FILL, which is what bounds its blast radius. `fromRow` takes
   * the first row colour that clears the plate, and in every newly-admitted case `primary` clears it
   * by construction — so `ink` is `primary`, the fill it already resolved to, `fillMoved` stays
   * false and `assignOutlineColors` is not re-run. The only new behaviour is the logo flatten, which
   * is the thing being asked for.
   * ⚠ Every existing guard still stands in the loop below: a badge (`colorBorneDetail`) is never
   * flattened, and a colour the buyer chose is never overwritten. */
  const ls = p.logoStructure || {};
  const markTones = [pal.primary, pal.secondary, pal.tertiary, ls.fillHex, ...(Array.isArray(ls.bands) ? ls.bands : [])]
    .map(normalizeHex).filter(Boolean);
  if (!markTones.some((t) => colorsConflict(plate, t))) return false;

  /* ⛔ A MARK WITH A KEYLINE THAT CLEARS THE PLATE IS NOT VANISHING (Ryan 2026-08-17).
   * Everything above asks "does a colour of this mark match the plate?" and treats yes as
   * proof the mark is disappearing. For an OUTLINED mark that is simply false, and the
   * plate in front of us proves it: UVA Wise is a red fill inside a white keyline, and on a
   * red plate the mark reads perfectly — the keyline is holding it off the surface. Ryan,
   * testing it: *"the logo stays red with the white outline (looks great), but the weight
   * numbers change to white with a black outline… don't you think the weight numbers should
   * also be red with a white outline?"* They should, and the rescue firing here is the only
   * reason they aren't: it writes a LITERAL contrasting ink onto the element, which is why
   * `effectiveTextStyle`'s own `logoOutlined` guard — built for exactly this case — never
   * gets a say. The decision is already baked in by the time the renderer looks.
   *
   * ⚠ `bandTouchesFill`, NOT thickness, and not merely "has a band". Thickness is not stored
   * (only hexes, peelability and touches-fill survive `applyLogoStructure`), and inventing a
   * hairline threshold nobody has measured would be a worse guess than the signal already
   * here. A band that ABUTS the fill is by construction the thing standing between the fill
   * and everything outside it; a detached ring separates nothing.
   *
   * ⚠ AND IT MUST CLEAR THE PLATE ITSELF, which is what keeps the white-plate case correct.
   * Dye the same mark WHITE and its white keyline vanishes into the plate too — nothing is
   * holding the mark off any more, so the rescue is genuinely right and still fires.
   * Measured both ways with `tools/probe-ink-vs-logo.mjs`. */
  const bands = Array.isArray(ls.bands) ? ls.bands : [];
  /* ⚠ A DETACHED KEYLINE HOLDS TOO (Ryan 2026-08-18, the Hanover red plate — this drops the
   * `touches[i] === true` requirement the 08-17 fix added). The paragraph above argues a detached
   * ring "separates nothing", and for the lettering-rings it feared that is true — but those are
   * demoted to low confidence before they ever reach here. The detached bands that SURVIVE
   * detection are the gap marks' real keylines, and Hanover's red plate is the proof in front of
   * us: red letters, moat, white keyline — the letters merge with the plate and the keyline still
   * draws the whole silhouette. Ryan: "the logo looks fine, but it's got a white outline when the
   * text all has a black outline. Should match." With the requirement dropped, the rescue stops
   * firing, the text keeps the mark's own fill + keyline treatment, and the two match.
   * The clear-the-plate requirement stands — dye it white and the white keyline holds nothing. */
  const keylineHolds = bands.some((hex) => {
    const h = normalizeHex(hex);
    return h && !colorsConflict(plate, h);
  });
  /* ── THE FIELD-COLOUR POP (Ryan 2026-08-18, "Version B is where it's at") ─────────────────────
   * A held mark on a plate dyed its OWN fill colour renders tone-on-tone: red letters, red 45,
   * white keylines — cohesive, and Ryan chose the alternative from a side-by-side: when the
   * design has a SEATED SCHOOL COLOUR (the keyed-out field, `keyedFieldHex`), both fills pop to
   * it instead — navy CH + navy 45, white keylines, on the red plate.
   *   Scoped three ways so it fires on exactly this configuration class and nowhere else:
   *   • the plate must WEAR the mark's presenting fill (tone-on-tone is the case being traded);
   *   • the candidate is ONLY the seated field colour — marks without one (UVA Wise, Mission)
   *     keep today's held treatment untouched;
   *   • the field must clear the plate AND pass `repaintReads` against the mark's own tones —
   *     the 2a fusion veto, which is what stops a white pop fusing into the white keyline.
   * The pop flows through the standard rescue below (ink forced), so every existing guard —
   * colorBorne never flattens, buyer picks never overwritten, the ring re-derives when the fill
   * moves — applies unchanged. */
  let forcedInk = null;
  if (keylineHolds) {
    const presenting = normalizeHex(
      ((p.elements || []).find((e) => e.type === 'logo' && e.logo && e.logo.dataUrl) || {}).logoCenterColor,
    ) || normalizeHex(ls.fillHex) || normalizeHex(pal.primary);
    // The field counts only while its swatch still lives on the bench. ⚠ This WAS the test
    // written out by hand; `seatedFieldOf` is the one definition (2026-09-18).
    const field = seatedFieldOf(p);
    if (presenting && field && colorsConflict(plate, presenting)
        && !colorsConflict(plate, field)
        && repaintReads(ls.toneTouches || null, presenting, field)) {
      forcedInk = field;
    } else {
      return false;
    }
  }
  // The colour that reads against the PLATE (not against the logo) — that is the surface both the
  // ink and the mark have to stand off.
  const onRow = [pal.primary, pal.secondary, pal.tertiary, ...(Array.isArray(p.swatches) ? p.swatches : [])]
    .map(normalizeHex).filter(Boolean);
  // THE BUYER'S OWN COLOURS COME FIRST (Ryan 2026-07-25). A house neutral is the fallback for a
  // design that has nothing legible left, not the first answer. The Wallabies mark is green +
  // yellow: dyeing the plate yellow used to turn the text and logo WHITE — a colour nowhere in the
  // design and, since house neutrals now only appear for one-tone marks, not even on the row. Green
  // was sitting right there and reads perfectly against yellow. Picking from the design keeps the
  // result on-brand AND keeps the active swatch highlightable, which a colour nobody can see cannot.
  /* ⭐ RULE 3 — AND THIS ROW IS WHERE THE KEYLINE GREEN CAME FROM (Ryan 2026-09-20, Chatham):
   * *"the green colour which is extracted for the weightfill colour seems like a bit of a stretch..
   * hardly seeing any green in the actual logo."* The row is tried in order and `primary` IS the
   * plate on configuration #1, so the answer is `secondary` — and on that mark `secondary` is a
   * colour that exists only as a one-pixel keyline around two words. The number then gets painted
   * in it.
   *   So a tone that is all stroke AND a minor part of the mark steps aside and the next colour on
   * the row answers. ⚠ NEVER TO NOTHING: if every colour on the row is a keyline the row is used
   * unfiltered, because a number painted in a thin brand colour is a smaller fault than a number
   * painted in an invented one.
   *   ⛔ The bars are his, not mine, and they are the middles of gaps in his own 23 answers — he
   * called a 1.3% survivor WRONG and a 1.6% survivor FINE, so thinness alone is not the fault; see
   * `keylineOnlyTones`. And the measurement is absent on every profile analysed before it existed,
   * where this filter removes nothing and the row behaves exactly as it always has. */
  const keyline = keylineOnlyTones(p);
  const bodyRow = keyline.length
    ? onRow.filter((c) => !keyline.some((k) => colorsConflict(c, k)))
    : onRow;
  const row = bodyRow.length ? bodyRow : onRow;
  const fromRow = row.find((c) => !colorsConflict(plate, c));
  /* ⛔ THE RULE RECORDS ITS OWN FIRING, because nothing offline can score it. Three fix points
     were tried and measured: the fill ladder moved the wrong plates, the render palette's pool
     moved none (the element arrives carrying a baked literal), and this line is the right one —
     but a replay of THIS function outside the app reproduces only 81% of a rendered pass, so a
     corpus net taken from it would be measuring the harness. What can be trusted is the render
     itself, so the decision is stamped here and the runner dumps it: the pass then names the plates
     rule 3 moved, and the review page spotlights exactly those. */
  const plainRow = onRow.find((c) => !colorsConflict(plate, c));
  const house = usableAccents([plate])[0];
  // `usableAccents` only checks its neutrals against the PLATE, never against the colours already in
  // the design, so it returns the house #FFFFFF for a logo whose own white is #FEFEFE — visually
  // identical, never equal, so nothing highlighted. Snap to the near-identical colour already there.
  const ink = forcedInk || fromRow || (house ? (onRow.find((c) => colorsIndistinguishable(c, house)) || house) : null);
  /* The stamp compares the two FINAL answers, not the two row hits — on Chatham the filtered row
     clears nothing at all and the rescue falls through to the house neutral, which is the whole
     point of the rule there, and comparing row hits recorded nothing on the one plate it exists
     for. */
  const inkPlain = forcedInk || plainRow || (house ? (onRow.find((c) => colorsIndistinguishable(c, house)) || house) : null);
  p.__keylineStepAside = (ink && inkPlain && normalizeHex(ink) !== normalizeHex(inkPlain))
    ? { from: normalizeHex(inkPlain), to: normalizeHex(ink), keyline }
    : null;
  if (!ink) return false;
  // NEVER overwrite a colour the buyer chose (Ryan 2026-07-25). This runs on every plate pick, and it
  // used to repaint explicit picks: flatten the logo yellow, dye the plate yellow, and their yellow was
  // silently replaced by white — the choice destroyed, with nothing in the UI to say so. The model
  // already says what should happen instead: an explicit colour is KEPT and rescued with a contrast
  // RING (`syncLogoContrastOutline` for the logo, `syncFillContrastOutline` for the text), which is
  // reversible where a repaint is not. So this function now only fills in colours nobody has chosen.
  /* ⛔ AND A FILL THIS PASS MOVES LEAVES THE RING AROUND IT STALE (Ryan 2026-08-09, the Angry Panda
   * white-on-white). Tracked here so the repair below can be conditional on it actually happening.
   *
   * THE COLLISION IS STRUCTURAL, not a property of one logo. Every caller of this function has just
   * derived the stack as `fillRef: 'primary'` + `defaultOutlinesFor(..., fillHex: primary)`, and that
   * ring pass reaches for `secondary` — the role it is told to prefer. Then this pass rescues the
   * fill off the plate by taking the FIRST colour on the row that clears it, and with the plate dyed
   * `primary` (which is exactly what `applyDefaultPlateColors` does to configuration #1) the first
   * one that clears is `secondary` TOO. Fill and ring converge on one slot, and the render agrees
   * with both: `effectiveTextStyle` peels a ring that merges with the PLATE, and this ring does not
   * — it merges with the fill it is wrapped around, which nothing at paint time looks at.
   *   Ryan hit it twice with two different colours, which is the tell: cream-on-cream on the desktop
   * and orange-on-orange on the phone, same mark, because all that changed between them was which
   * hex `secondary` happened to hold. */
  let fillMoved = false;
  for (const e of p.elements || []) {
    if (e.type === 'arctext' || e.type === 'weight') {
      if (e.fillUserSet !== true) {
        if (normalizeHex(resolvePaletteRef(e.fillRef || 'primary', pal)) !== normalizeHex(ink)) fillMoved = true;
        e.fillRef = ink;
      }
    } else if (e.type === 'logo' && e.logo && e.logo.dataUrl) {
      // ── A MULTI-TONE MARK IS NOT VANISHING (Ryan 2026-07-28) ────────────────────────────────
      // "the orange plate turns the ENTIRE logo — every square inch — blue, so it's just a blue
      // circle now."
      //   This function exists for the mark that genuinely disappears: one colour, dyed onto a plate
      // of that colour, gone. Flattening it to something legible is the only rescue available and it
      // costs nothing, because there was only ever one tone to lose.
      //   Its GATE, though, asks a single question — does the plate conflict with `palette.primary` —
      // and then applies a whole-mark remedy to the answer. On a badge with an orange field, a navy
      // ring, white lettering and a white horse, an orange plate makes that gate true and the mark is
      // flattened to navy: three tones and the entire drawing destroyed to rescue the one that
      // conflicted. Nothing was vanishing. The other tones were reading perfectly.
      //   So the mark is asked whether it can still be SEEN. Its own tones are on the profile —
      // the detected fill plus its bands — and if two or more survive with at least one of them
      // clear of the plate, there is a legible drawing on that plate and this is not the tool for
      // it. The conflicting parts are already handled elsewhere and reversibly: the band peel for a
      // ring that merges, the contextual fill swap for a band-less mark, a contrast ring for a
      // colour the buyer chose themselves.
      // ── NEVER AUTO-FLATTEN A MARK THAT FLATTENING RUINS (Ryan 2026-07-28) ───────────────────
      // `colorBorneDetail` already answers this, and it answers it from the ARTWORK: one contiguous
      // shape whose ring, field, lettering and mascot exist only as colour boundaries. Painting it a
      // single colour produces a disc, and no plate, no swap rule and no contrast heuristic can make
      // that a good outcome — which is precisely why the Logo row stopped offering it as a choice.
      // A thing the buyer is not allowed to choose is not a thing the app may do to them silently.
      //   This REPLACES a tone-counting gate that never fired. It required three or more tones and
      // counted the stored palette ROLES, which for this badge are two — white is an accent and
      // `tertiary` is nulled on every upload. Counting tones was always a proxy for the real
      // question, and the real question now has a real answer.
      /* ⚠ …UNLESS THE INTERIOR IS ONE TONE (Ryan 2026-08-18, the field-colour pop). The guard's
       * real question is "would painting the interior one colour destroy colour-borne detail?", and
       * a mark whose interior is UNIFORM (`fillUniform` — the fill is the only significant
       * non-band tone) has no interior detail to destroy: the flatten recolours the letters and the
       * band-labelled keyline is untouched by construction. Hanover is the case that forced the
       * refinement — at the upscaled working resolution its anti-aliasing bridges the moat, fusing
       * letters and keyline into "one blob, two tones", and `colorBorneDetail` flips true on a mark
       * that is structurally fill + detached keyline (the measurement-construction lesson again).
       * A REAL badge (CSI, Mission) has a multi-tone interior → `fillUniform` false → still
       * guarded exactly as before. */
      if (p.logoStructure && p.logoStructure.colorBorneDetail === true
          && p.logoStructure.fillUniform !== true) continue;
      /* ── R1 (WD 337, Ryan's corpus review — the blob cluster, ~16 marks) ─────────────────────
       * A mark with two or more distinct tones of its own, at least one of which CLEARS this
       * plate, is still a legible drawing — the merging tone is the band repaint / fill swap /
       * add-ring machinery's job, each of which acts on the tone that merged instead of
       * destroying the ones that didn't (Carroll Lynx flattened to beige on blue AND to blue on
       * beige; Rockford's "the middle blob wasn't necessary" — its band repaint already had the
       * conflict). The colour-borne guard above could not protect these: half the blob cluster
       * has NO structure read at all (banner icons, tiny marks), so `colorBorneDetail` was never
       * derived. `markTones` exists for exactly those marks — the roles survive extraction even
       * when structure doesn't. The genuine one-tone mark (Ryan 2026-07-25: black logo on black
       * plate → white) still flattens: one distinct tone → this gate never trips. */
      const distinctTones = [];
      for (const t of markTones) if (!distinctTones.some((u) => colorsConflict(u, t))) distinctTones.push(t);
      // ⚠ NEVER when the ink was FORCED by the field-colour pop — that flatten is Ryan's own
      // chosen presentation ("Version B is where it's at", Hanover's navy pop), not a rescue
      // this gate exists to question. Caught by the WD 337 control run: the gate erased it.
      if (!forcedInk && distinctTones.length >= 2 && distinctTones.some((t) => !colorsConflict(plate, t))) {
        // "Not vanishing" also erases a previous plate's auto flatten — the verdict is about
        // THIS plate, and leaving the old ink standing is the stale-derivation bug one hop later.
        if (e.logoCenterAuto === true) { e.logoCenterColor = null; e.logoCenterAuto = false; }
        continue;
      }
      // A flatten this function derived earlier (`logoCenterAuto`) is fair game to re-derive; one the
      // buyer picked is not.
      if (normalizeHex(e.logoCenterColor) && e.logoCenterAuto !== true) continue;
      e.logoCenterColor = ink;
      // DERIVED, not chosen (Ryan 2026-07-25). Without this flag the flatten is indistinguishable
      // from a buyer's own Logo-picker pick, and `recascadeColorDefaults` preserves the buyer's
      // logo colours unconditionally — so the white forced by a GREEN plate survived onto the next
      // plate. On white that left a white logo (then rescued by an auto green ring: "white with a
      // green outline") while the text correctly went back to green. Flagged, the re-cascade can
      // clear it and let this function re-decide from scratch for the new plate.
      e.logoCenterAuto = true;
    }
  }
  /* ── THE INVARIANT THIS PASS BREAKS, RESTORED WHERE IT BREAKS IT ──────────────────────────────
   * A ring's colour is chosen against the fill it wraps and the plate behind it. Moving the fill
   * invalidates half of that, so the ring is re-derived here rather than left for a caller to
   * remember — which is precisely what went wrong: the MANUAL dye path
   * (`design-panel.masterSwitchCascade`) runs `assignOutlineColors` as its next step and has always
   * come out right, while the AUTOMATIC dye (`logo-step`, the multi-configuration plate defaults)
   * calls `recascadeColorDefaults` alone and came out white-on-white. One caller knowing a rule the
   * other does not is the shape this project keeps paying for; the rule lives with the pass now.
   *
   * ⚠ `plate`, not `effectivePlateHex(state)` — this function has no state and its own verdict two
   * dozen lines up is already taken against `p.bodyColor`. Asking a different plate here than the
   * one that triggered the rescue is how the two halves would disagree again.
   * ⚠ Idempotent in the manual path: `masterSwitchCascade` still runs its own
   * `assignOutlineColors` afterwards with the effective plate, and that one wins.
   * ⚠ Gated on the fill ACTUALLY moving, so the calls that hit the early-out above — and the ones
   * where the ink the rescue picks is what the fill already wore — behave exactly as before. */
  /* ── AND A BARE NUMBER BESIDE AN OUTLINED MARK IS THE SAME BUG, MIRRORED (Ryan 2026-08-17) ────
   * The half above stops the rescue firing when the mark's keyline holds it off the plate. This is
   * the case where the keyline does NOT hold — dye UVA Wise's red-and-white mark WHITE and its white
   * keyline vanishes into the plate too, so the rescue is correct to fire. What it then does is
   * wrong in the other direction: the mark comes out red with a black keyline and the number comes
   * out **bare red with no outline at all**. Measured, then seen: `tools/probe-ink-vs-logo.mjs`
   * reports `(none)` for the number's outlines, and the zoomed render shows the V ringed and the 10
   * not.
   *
   * ⛔ `assignOutlineColors` CANNOT FIX THIS AND IS NOT MEANT TO. It re-colours rings that exist —
   * `if (!outs.length) continue;` — so an element that arrived with none is skipped entirely. There
   * was no pass anywhere whose job was to ADD one back, which is why the number simply loses its
   * ring and nothing notices.
   *
   * The rule is Ryan's, and it is one sentence: an outlined mark and the type beside it should wear
   * the same treatment, because the mark has already solved the identical problem on the identical
   * plate. So a text piece left bare while the mark carries bands is given a ring, chosen down the
   * ladder this file already uses everywhere else — the design's own colours first, house neutrals
   * last (on a white plate with red type that lands on black, which is what the mark wears).
   *
   * ⚠ Only where the mark CARRIES ITS OWN CONTRAST. A design with no outlined mark has no pairing to
   * mirror, and bare type is a perfectly ordinary look there — inventing rings for it would be a
   * style change nobody asked for rather than a consistency fix.
   *
   * ⛔ AND `bands.length` IS ONLY ONE WAY TO CARRY IT (Ryan 2026-08-17, the CSI badge). A
   * `colorBorneDetail` mark is a solid multi-tone blob whose ring, field and lettering exist purely
   * as colour boundaries — it has no keyline BY DEFINITION, and it is the one mark class that needs
   * this rescue most, because it sails through any plate on its own internal contrast while the flat
   * type beside it does not. Measured on CSI over its four plate options:
   *
   *     plate #0B0C0F → gold  #FCCB0C, no ring   ✅ (rescue never fires, nothing conflicts)
   *     plate #FAF2CB → gold  #FCCB0C, no ring   ⚠ thin
   *     plate #FCCB0C → cream #FAF2CB, no ring   ⛔ pale-on-pale, and the eagle beside it is perfect
   *     plate #FFFFFF → gold  #FCCB0C, no ring   ⚠ thin
   *
   * The gold row is the fault: the rescue MOVED the fill onto the only other tone in the design,
   * that tone barely clears the plate, and the `markBands.length` gate then declined to give it the
   * ring that would have held it off — for the one reason that cannot apply to a badge. Widening the
   * gate is a smaller and truer fix than loosening `colorsConflict`, which would move every contrast
   * decision in the app to repair one of them.
   *   ⚠ NOT the same question as WD 325's ring STRIP, which stays as it is. A badge gets no ring by
   * default (bare type is right on black, and 325's "obnoxious" report was exactly a ring nobody
   * needed); it gets one on the plates where the rescue has just moved its fill. Rings appear where
   * the plate demands them, not as a resting state. */
  const markBands = (Array.isArray(ls.bands) ? ls.bands : []).map(normalizeHex).filter(Boolean);
  const markCarriesOwnContrast = markBands.length > 0 || ls.colorBorneDetail === true;
  if (markCarriesOwnContrast) {
    for (const e of p.elements || []) {
      if (e.type !== 'arctext' && e.type !== 'weight') continue;
      if (Array.isArray(e.outlines) && e.outlines.length) continue;
      const fillHex = normalizeHex(resolvePaletteRef(e.fillRef || 'primary', pal));
      const ladder = [pal.secondary, pal.tertiary, pal.primary, ...(Array.isArray(p.swatches) ? p.swatches : [])]
        .map(normalizeHex).filter(Boolean);
      /* ⛔ AND IT HAS TO BE A RING YOU CAN SEE. `colorsConflict` alone answered "is this a different
         colour from the plate and the fill", and on CSI's gold plate the ladder ran out of brand
         tones and fell through to `usableAccents(...)[0]` = the house WHITE — 1.53:1 against the
         plate and 1.13:1 against the cream fill it was wrapped around. A ring nobody can see is not
         a rescue; it is the same bug wearing the remedy's clothes.
           So each candidate must also READ, and the house neutrals are ordered by how well they do
         it rather than taken in declaration order (white first). On gold-plate/cream-fill that
         returns BLACK — which is what the eagle beside it wears, so the mirror rule lands too. */
      // The same two edges `ringFitTest.must` guards, asked here because this pass invents the ring
      // rather than re-colouring one — `assignOutlineColors` corrects it moments later through that
      // very function, and the two must not disagree in the window between.
      const legible = (c) => !colorsConflict(plate, c) && !(fillHex && colorsConflict(fillHex, c))
        && readsOn(plate, c) && (!fillHex || readsOn(fillHex, c));
      const neutrals = usableAccents([plate, fillHex].filter(Boolean))
        .slice()
        .sort((x, y) => worstReadOf(y, plate, fillHex) - worstReadOf(x, plate, fillHex));
      const pick = ladder.find(legible) || neutrals[0];
      if (pick) e.outlines = [{ colorRef: pick, thickness: outlineThicknessFor(p) }];
    }
  }

  if (fillMoved) assignOutlineColors(p, plate);
  return true;
}

/**
 * The buyer just styled something BY HAND — a fill, an outline, a logo band, a ring (Ryan
 * 2026-08-08). One-way; see `styleUserEdited` in `createDefaultProfile` for what it gates and why it
 * is a latch rather than something derivable from the design.
 *
 * Called from the buyer's own handlers only. ⛔ NEVER from an auto pass and NEVER from a live
 * PREVIEW — a colour somebody dragged past and walked away from is not a choice they made, and
 * latching on it would be permanent.
 */
export function markStyleUserEdited(profile) {
  if (profile) profile.styleUserEdited = true;
}

/**
 * @param {object} state
 * @param {object|null} structure  the analysis (logo-structure.js), or null
 * @param {{sameArtwork?: boolean}} [opts]
 *   `sameArtwork` — this is the SAME logo re-analysed, not a different one. Set by the holes flip,
 *   which changes the artwork (enclosed pockets cut or filled) without changing the brand. It keeps
 *   the two things that belong to the DESIGN rather than to the logo: the plate dye and its per-plate
 *   ink memory. Everything else re-derives exactly as it does on an upload, which is the point — the
 *   flip should land where uploading that same art would have.
 */
/** Does the MARK wear a keyline? The one question the three ring-seeders must not answer
 *  differently (WD CLAUDE.md §0b — "when two places need the same answer, one will be wrong",
 *  and this cost a cycle on 2026-08-17: gating only `applyLogoStructure` missed the FIRST-upload
 *  path entirely, because `smartStart` rebuilds the elements afterwards through `createElement`,
 *  whose born ring is a hardcoded `secondary @2.5`). Reads the STORED structure, which is set
 *  before any seeder runs. */
function markWearsKeyline(p) {
  const ls = p && p.logoStructure;
  const b = ls && ls.bands;
  if (Array.isArray(b) && b.length > 0) return true;
  // The de-facto keyline the band detector declined — see `wrapToneFromTouches` (Ryan
  // 2026-08-23, ECC Kats: the dark outline wraps every element but the structure is low-conf).
  return !!(ls && ls.wrapTone);
}

/** The ring a keyline-wearing mark seeds: the detected bands' derivation when bands exist, else
 *  a single ring in the WRAP tone's own colour — the text wears what the mark wears. */
function keylineSeedOutlines(p, fillRef) {
  const ls = p.logoStructure || {};
  if (Array.isArray(ls.bands) && ls.bands.length > 0) {
    return defaultOutlinesFor(p.palette, outlineThicknessFor(p), { base: p.bodyColor, fillHex: resolvePaletteRef(fillRef, p.palette) });
  }
  return ls.wrapTone ? [{ colorRef: ls.wrapTone, thickness: outlineThicknessFor(p) }] : [];
}

export function applyLogoStructure(state, structure, { sameArtwork = false } = {}) {
  const p = state.designProfile;
  const pal = p.palette;
  // Always start from a CLEAN slate: clear the auto-style template, drop any 3rd
  // palette role, and revert the text/weight pieces to a single default outline.
  // The first-upload path rebuilds elements via smartStart so this is harmless
  // there; the RE-UPLOAD path keeps its elements, so this is what stops a new logo
  // from inheriting the previous logo's role (a leaked `tertiary`) or multi-outline
  // stack — the red/white/RUST bug where Elite Edge kept the E Logo's rust outer
  // outline (Ryan 2026-07-17).
  p.autoStyle = null;
  p.logoStructure = null; // a new logo replaces the old one's outline bands + resets any peel
  // The derivation is authoritative again — nothing below this line is the buyer's choice any more.
  // A no-op on the `sameArtwork` path, which its caller only takes while the latch is already down.
  p.styleUserEdited = false;
  // …and the ring-suppression latch with it: a NEW mark has never been judged, so a deletion aimed
  // at the old one must not silence its rescue (Ryan 2026-09-21).
  p.logoRingSuppressed = false;
  for (const e of p.elements || []) if (e.outlineEdits) e.outlineEdits = null; // WD 486: edits to the OLD logo's rings
  for (const e of p.elements || []) if (e.ringsOwn) e.ringsOwn = false; // WD 488: a new mark's text mirrors it again
  for (const e of p.elements || []) if (e.type === 'logo') { e.logoBandsOff = []; e.logoAddOutline = null; e.logoCenterColor = null; e.logoCenterAuto = false; e.logoBandColors = null; e.logoToneSwaps = null; }
  pal.tertiary = null;
  // The PLATE DYE resets too (Ryan 2026-07-25). It was the one thing the old logo left behind: swap a
  // blue-and-green mark for the green-and-yellow Wallabies and the plate stayed BLUE — a colour from
  // the previous brand, and after the palette is rebuilt below it usually isn't even on the swatch row
  // any more, so it can't be un-picked from the Plate row. Black is the default and the fastest-
  // shipping finish, and it's what a fresh upload starts on, so a re-upload should land in the same
  // place. Reset BEFORE applyVanishingLogoDefaults so the vanishing check judges the new mark against
  // the plate it will actually be on.
  //   ⚠ BOTH ARE SKIPPED ON `sameArtwork` (Ryan 2026-08-08). Every word above is an argument about a
  // DIFFERENT BRAND arriving — "a colour from the previous brand", "a new mark is a new identity" —
  // and the holes flip is the one caller for which that is false. It re-cuts the same logo, so the
  // plate the buyer dyed and the ink they chose for it are still about this design. Undoing them
  // because the artwork was re-analysed would make a checkbox the most destructive control in the
  // app. The seeding below then bases itself on the plate they are actually on, which is what it
  // already claimed to do.
  if (!sameArtwork) {
    p.bodyColor = DEFAULT_BODY_COLOR;
    // …AND THE PER-PLATE-COLOUR INK MEMORY GOES WITH IT (Ryan 2026-08-01). Same reasoning as the
    // paragraph above, one step later in time: `savedInk` holds colours chosen for the OLD artwork's
    // palette, so keeping it would let the previous brand's ink reappear the moment the buyer dyed the
    // plate again — the Wallabies bug exactly, just deferred until the next plate change instead of
    // sitting there visibly on upload. A new mark is a new identity; its ink starts empty.
    p.savedInk = null;
    // …AND THE PER-CONFIGURATION PLATE DEAL (Ryan 2026-08-23: *"upload a completely new logo
    // which is yellow and green, the plate color will still remain red and blue"*). `savedBodyColor`
    // holds the plates DEALT from the old artwork's palette, and every config walk restores from
    // it — so a 4× set kept wearing the previous brand while the single `bodyColor` above dutifully
    // reset. The Wallabies argument one axis over: those colours usually aren't even on the new
    // swatch row. The user-set latch goes too, exactly as the buyer's own dye on `bodyColor` does
    // not survive this reset. `replaceReveal` re-deals from the new palette right after.
    p.savedBodyColor = null;
    p.bodyColorUserSet = null;
  }
  // The artwork's linework weight, recorded before anything seeds an outline off it (Ryan
  // 2026-07-28). Carried on BOTH analysis paths, so a badge that returns low confidence — which is
  // most badges — still gets a ring sized to its own fine ruling.
  p.logoStrokeFrac = structure && Number.isFinite(structure.strokeFrac) ? structure.strokeFrac : 0;
  // The full-pixel tone census, carried on the PROFILE as well as on logoStructure (WD 337):
  // the marks that need it most — no structure read at all (wordmarks, tiny icons, banners) —
  // leave `logoStructure` null, and the ring/swap hygiene still needs to know what tones the
  // artwork wears. Same lifecycle as `logoStrokeFrac`: re-derived on every artwork change.
  p.logoTraceTones = structure && Array.isArray(structure.traceTones) ? structure.traceTones : null;
  /* R13 — the tones that ARE a detached one-tone piece of this mark (see `logo-structure.js`'s
     flood). On the PROFILE for the same reason as the census above: carrolllynx, the mark that
     produced the flag, returns low confidence and leaves `logoStructure` null. Re-derived on every
     artwork change, like its two neighbours; read by `dealPlateDefaults`. */
  p.logoSoloTones = structure && Array.isArray(structure.soloTones) ? structure.soloTones : null;
  /* The mark's ink in connected pieces, each labelled with the tone it is mostly made of — the
     artwork-level half of Ryan's "keep the mark and outline it" rule (`markRecolourAllowed`). On
     the PROFILE, with the two fields above and for the same reason, and it matters more here than
     for either of them: both marks Ryan judged the free-floating exception on return LOW confidence
     and leave `logoStructure` null, so a field living only on the structure record would be absent
     on exactly the marks the exception exists for. Measured once per artwork — never per plate. */
  p.logoInkPieces = structure && Array.isArray(structure.inkPieces) ? structure.inkPieces : null;
  /* ⛔ THE TWO FIELDS THAT WERE MEASURED AND NEVER SEATED (WD 459). `analyzeStructure` returns
     FIVE artwork measurements and the three above were the only ones given a seed line — so
     `markRecolourAllowed`'s neutral-touch exception (WD 448) and `keylineOnlyTones` (rule 3,
     WD 450) each read a field that is null on every mark in the app, and BOTH have therefore
     never fired on a single plate. Measured over REVIEW-2026-09-23: present on 0 of 131 marks.
     Every consumer already falls back through `p.` — `markToneRecord` reads
     `ls.toneNeighbours || p.logoToneNeighbours`, `keylineOnlyTones` reads the same pair — so the
     profile is the right home, and it is the one both rules' marks reach (both return LOW
     confidence often, which leaves `logoStructure` null; same argument as `logoInkPieces` above).
     ⚠ Carried on BOTH analysis exits, like their three siblings: `low()` closes over them and
     they are assigned before every decline but the three that precede the tone census itself
     (`no-image`, `too-few-swatches`, `empty-mask`) — and a mark with no measurable ink has no
     neighbours to report anyway. */
  p.logoToneNeighbours = structure && Array.isArray(structure.toneNeighbours) ? structure.toneNeighbours : null;
  p.logoToneSubstance = structure && Array.isArray(structure.toneSubstance) ? structure.toneSubstance : null;
  /* ⛔ A MARK WITH NO KEYLINE GIVES THE TYPE NO KEYLINE EITHER — the third and last face of
   * the mirror rule (Ryan 2026-08-17, the Lawson State plate on Live): *"the number defaults
   * to blue with a thick white obnoxious outline when the logo has none. Should be a straight
   * blue number."*
   *   The other two faces shipped earlier today in `applyVanishingLogoDefaults`: a keyline that
   * clears the plate means the mark is not vanishing, and a rescue that leaves the type bare
   * beside a banded mark has to hand it a ring. Both say the same thing — the type wears what
   * the mark wears, because the mark has already answered this question on this plate. Seeding
   * a ring onto type whose mark has none was the one direction still contradicting it.
   *
   * ⚠ TWO FAULTS COMPOUNDED HERE AND ONLY ONE IS ABOUT THE RING. `outlineThicknessFor` falls
   * back to `DEFAULT_OUTLINE_THICKNESS` (2.5 — the HEAVIEST it can return) when `logoStrokeFrac`
   * is 0, which is exactly the no-linework case. So a mark with no measurable rule got the
   * thickest ring in the app: inverted relative to the function's own intent, and the
   * "obnoxious" half of the report. Not seeding a ring at all makes it moot on this path;
   * the fallback itself is left alone because `usableAccents`-driven adds elsewhere still
   * want a sane default.
   *
   * ⚠ LEGIBILITY IS NOT LOST WITH IT. A fill that would vanish into the plate is still caught
   * downstream — `applyVanishingLogoDefaults` runs on the next line and the contrast-ring
   * rescues after it. This removes a ring nobody asked for, not the safety net. */
  const seedRing = !!(structure && structure.confidence === 'high'
    && Array.isArray(structure.outlines) && structure.outlines.length);
  for (const e of p.elements || []) {
    if (e.type !== 'arctext' && e.type !== 'weight') continue;
    e.fillRef = 'primary';
    e.outlines = seedRing
      ? defaultOutlinesFor(pal, outlineThicknessFor(p), { base: p.bodyColor, fillHex: resolvePaletteRef(e.fillRef, pal) })
      : [];
  }
  applyVanishingLogoDefaults(p);
  if (!structure || structure.confidence !== 'high' || !structure.fill) {
    // ⚠ NOT EMPTY-HANDED. A low-confidence analysis still knows whether this mark's detail is
    // colour-borne, and the Logo row needs that answer for exactly the marks that land here: a
    // concentric badge has no border-and-fill band structure to detect, so it ALWAYS takes this
    // branch (Ryan 2026-07-28). Leaving `logoStructure` null threw the one useful finding away.
    //   Everything else is the null it replaces — empty bands, no fill, not uniform — so every
    // consumer behaves exactly as it did before; there is simply somewhere for the flag to live.
    if (structure && structure.colorBorneDetail === true) {
      p.logoStructure = {
        confidence: 'low', bands: [], bandTouchesFill: [], fillHex: null, fillUniform: false,
        colorBorneDetail: true,
        toneTouches: structure.toneTouches || null,
        traceTones: structure.traceTones || null,
        // See the high-confidence record's note — the outlined-badge class lands on THIS branch
        // (ECC Kats), which is exactly where the de-facto keyline matters most.
        wrapTone: wrapToneFromTouches(structure.toneTouches || null),
      };
      /* ⭐ AND THE WRAP TONE BECOMES A REAL ONE-SLOT autoStyle (Ryan 2026-08-23, ECC's gray
         plate: the wrap ring seeded bare — no `autoIdx`, no slot — so the first fill-move
         re-derive forgot it and reached for the brown accent). With an autoStyle the ENTIRE
         preference machinery inherits: `applyAutoStyleToElement` stamps `autoIdx`,
         `preferredFor` returns the wrap colour on every re-derive, "+ Add" restores it, and
         `recascadeColorDefaults` takes the template path. One record instead of three patched
         seed sites. */
      const wt = p.logoStructure.wrapTone;
      if (wt) p.autoStyle = { fillRef: 'primary', outlines: [{ colorRef: wt, thickness: outlineThicknessFor(p) }] };
    }
    /* ⛔ THE UPLOAD PATH IS THE THIRD HOME OF THE "BLACK CAN'T BITE" PREMISE (Ryan 2026-08-23,
       Angry Panda: *"the text is purple with no outline"* on the default black plate). WD 332
       gave the rescue to both ADOPT branches; uploading onto an existing design seeds
       `fillRef='primary'` right here and never asked whether primary reads on the plate the
       buyer is looking at — the panda's #200B40 sits at ~1.2:1 on black, past every ΔE test.
       Same operator, same plate construction as the adopt branches. */
    correctIllegibleFill(p, effectivePlateHex(state));
    captureStyleBaseline(p);
    return { applied: false };
  }
  const fillHex = normalizeHex(structure.fill.hex);
  if (!fillHex) { captureStyleBaseline(p); return { applied: false }; }
  const outs = Array.isArray(structure.outlines) ? structure.outlines : [];

  // Palette roles from the layer order (max 3 named roles; a 4th outline is literal).
  pal.primary = fillHex;
  const secHex = outs[0] ? normalizeHex(outs[0].hex) : null;
  const terHex = outs[1] ? normalizeHex(outs[1].hex) : null;
  pal.secondary = secHex || pal.secondary;
  pal.tertiary = terHex || null;

  /* Outline stack: secondary, tertiary, then literal hex for any 4th — but ONLY the bands that
     actually read against the plate (Ryan 2026-08-07, Bauers Elite). This used to map `outs`
     straight through, so a brand's near-black #010101 was seeded as the text outline on the #000000
     plate: correct by the letter of the detection, invisible in fact, and it spent the slot their
     usable grey should have had.
       ⚠ THE FILTER IS NOT NEW POLICY — `colorsConflict` (ΔE < 14, CIELAB) has answered this since
     the plate-dye work and `defaultOutlinesFor` twelve lines above already consults it. What was
     missing is that `autoStyle` bypassed it, and `applyAutoStyleToElement` then overwrote the good
     answer with the raw one. See `seedOutlinesFrom` for why this is measured against the PLATE
     rather than kept as a list of near-black exceptions.
       `p.bodyColor` was reset to the default two dozen lines above, so the base here is the plate the
     buyer will actually land on, not whatever the previous logo dyed it. */
  const outlines = seedOutlinesFrom(outs, pal, {
    base: p.bodyColor,
    fillHex: fillHex,
    thickness: outlineThicknessFor(p),
  });
  p.autoStyle = { fillRef: 'primary', outlines };
  // The logo's OWN outline bands (outer→in) for the logo-outline peel — the detected
  // colours, kept independent of the palette (which the buyer may recolor for the text).
  // Built from ONE filtered list so `bands` and `bandTouchesFill` can't fall out of step — they are
  // indexed together by every consumer (the peel, the band chips, the logo-colour cascade).
  const bandSrc = outs.filter((o) => normalizeHex(o.hex));
  const bandHexes = bandSrc.map((o) => normalizeHex(o.hex)).reverse();
  /* ── CAN THE PEEL ACTUALLY REACH EACH OF THESE? (Ryan 2026-08-15, the Elite Edge lockup) ────────
   * *"it tacks on a white logo outline… press the ✕ to delete it and nothing actually changes on the
   * logo at all."* Both halves of that are one fault. `analyzeStructure` orders layers by DEPTH, so
   * it can call a shell "outermost" that the artwork encloses; every control on the Logo-outlines
   * row acts through a flood that starts at the image BORDER and walks inward, stopping at opaque
   * non-band pixels. On this mark the charcoal disc stands between the border and the white ring, so
   * the flood labels not one pixel of it — measured byte-identical either side of the toggle
   * (`tools/probe-band-toggle-effect.mjs`).
   *   So the row asks the peel rather than the detector. A band with no pixels is not "a band the
   * buyer might want to keep": it is a chip that cannot do anything, and offering it is how a
   * working control and a broken one come to look the same.
   * ⚠ The band COLOURS are untouched — this only says which are ACTIONABLE. The white here is a real
   * colour of the mark (it is the wordmark), and it keeps its place in the palette and in the text's
   * auto-style, which is the part Ryan called good.
   * ⚠ Indexed with `bands` / `bandTouchesFill`, from the same filtered list, for the reason stated
   * directly above: every consumer indexes them together. Absent (older save) reads as "peelable",
   * which is exactly today's behaviour. */
  const peelCounts = structure.peelSource && bandHexes.length
    ? bandPixelCounts(structure.peelSource, bandHexes, normalizeHex(structure.fill.hex))
    : null;
  p.logoStructure = {
    confidence: 'high',
    bands: bandHexes,
    bandPeelable: peelCounts ? peelCounts.map((c) => c > 0) : bandHexes.map(() => true),
    // Per band (same order): does it ABUT the fill? A keyline does; a detached ring doesn't. The
    // logo-colour cascade skips the ones that do, so recolouring the whole mark can't dissolve a
    // border into the shape it was drawn to separate.
    bandTouchesFill: bandSrc.map((o) => o.touchesFill === true).reverse(),
    /* Per band (same order): its own medial-axis width as a fraction of the artwork's long edge —
       the absolute weight `analyzeStructure` normalises out of `thickness` (WD 438). `autoStyle`
       already spent it at seed time; this is the copy `presentedOuterOutline` needs to weigh the
       MIRRORED outer ring off the band it is mirroring rather than off the mark's median stroke.
       Null per entry where the band carried too few ridge samples; absent on an older save, and
       both fall back to `outlineThicknessFor` exactly as before. */
    bandWidthFracs: bandSrc.map((o) => (Number.isFinite(o.widthFrac) ? o.widthFrac : null)).reverse(),
    // The centre fill colour + whether the interior is one tone — drives the "Logo fill"
    // section: uniform → highlight that swatch (no "Original" chip); else keep "Original".
    fillHex: normalizeHex(structure.fill.hex),
    fillUniform: !!structure.fill.uniform,
    // Does this mark's detail live in its colour boundaries rather than its shape? A badge does; a
    // wordmark does not. The Logo row hides its solid-fill swatches when it does, because flattening
    // such a mark can only ever produce a featureless blob (Ryan 2026-07-28).
    colorBorneDetail: structure.colorBorneDetail === true,
    // Hex-keyed boundary adjacency from the analysis — which tones share an edge in this artwork,
    // 'BG' meaning the plate at render time. The contextual repaint choosers consult it through
    // `tonesTouch` so a rescue can never fuse two touching regions (Ryan 2026-08-18, Edison +
    // Mission Patriots). Absent on older saves → the choosers behave exactly as before.
    toneTouches: structure.toneTouches || null,
    // The full-pixel tone census (WD 337) — every tone the artwork wears, including thin detail
    // under the table's 3% floor. `unmeasuredPresence` reads it: a repaint target the mark
    // already contains but the table cannot measure is refused (the Kenyon test).
    traceTones: structure.traceTones || null,
    // The de-facto keyline when the band list is empty — see `wrapToneFromTouches`. Computed
    // here once so design-profile's born-default branch can read it without an import cycle.
    wrapTone: wrapToneFromTouches(structure.toneTouches || null),
  };
  applyAutoStyleToElements(p);
  // The same upload-path rescue as the colour-borne branch above — after the stamp, so the
  // ring-exemption sees the seeded rings and a mirror keep stays a keep.
  correctIllegibleFill(p, effectivePlateHex(state));
  captureStyleBaseline(p); // the post-upload baseline "Reset to original" restores to

  return {
    applied: true,
    mode: `${outlines.length + 1}-layer`,
    fillHex,
    outlineHexes: outs.map((o) => normalizeHex(o.hex) || o.hex),
  };
}

/** Snapshot the post-upload COLOR baseline (extracted palette + swatch library) so
 *  "Reset to original" can restore it after edits (Ryan 2026-07-21). The bodyColor baseline
 *  is always black; element styling is rebuilt from autoStyle on reset (not snapshotted). */
function captureStyleBaseline(p) {
  const pal = p.palette || {};
  p.styleBaseline = {
    palette: { primary: pal.primary, secondary: pal.secondary, tertiary: pal.tertiary || null },
    swatches: Array.isArray(p.swatches) ? p.swatches.slice() : [],
  };
}

/** "Reset to original" (Ryan 2026-07-21) — undo all the color/outline experimentation back
 *  to the just-uploaded baseline: black plate + weight, the extracted palette + swatches,
 *  the logo's auto-styled text outlines, and NO logo peel / added ring / disabled outlines.
 *  Keeps the logo, layout, text content, font. One undoable step (the caller commits). */
/**
 * Restore every DESIGN COLOR to its auto-styled default (Ryan 2026-07-23) — the shared core of
 * both "Reset colors" and the plate-colour MASTER SWITCH. Drops one-off manual tweaks so the
 * plate-dye cascade re-applies cleanly:
 *   • text/weight fill + outlines → the logo-matched auto stack (literal-hex edits gone, toggled-
 *     off outlines back, added outlines removed, thickness reset), plus every contrast LOCK cleared;
 *   • the logo → its original art (band recolours/removals, added ring, and centre flatten cleared).
 * Does NOT touch the palette, the swatch pool, or the plate colour — the caller owns those.
 */
export function restoreColorDefaults(p) {
  if (!p) return;
  // …AND THE HAND-STYLED LATCH GOES WITH THEM (Ryan 2026-08-08). This function's whole job is to
  // discard the buyer's colour choices, which is exactly what the latch records the existence of —
  // leaving it up would mean a design with no manual colours left in it still claiming to be
  // hand-styled, and the holes flip would keep refusing to re-derive for a reason that no longer
  // exists. Reset is the one action that hands authorship back to the app.
  p.styleUserEdited = false;
  // …AND THE RING-SUPPRESSION LATCH, because "Reset colors" is the escape hatch the latch was
  // designed around (Ryan 2026-09-21: *"it needs to … stay that way unless they click 'reset
  // colors', then it returns to the default"*). Leaving it up would make Reset the one button that
  // cannot restore the default it promises.
  p.logoRingSuppressed = false;
  // WD 488: and the text rings the buyer took over go back to mirroring the logo - Reset returns the default.
  for (const e of p.elements || []) if (e.ringsOwn) e.ringsOwn = false;
  // ...and the DELETIONS in the per-slot memory of the text rings (WD 486), for the same reason: the rebuild below
  // brings the app's rings back. ⚠ The remembered THICKNESSES stay - Reset is colours only (Ryan 07-28) and puts the
  // buyer's thicknesses back over the rebuilt stack below; forgetting them here would let the next plate pick snap
  // a thickness Reset just kept (measured: 1.8 kept by Reset, memory gone, 1.5 on the next plate).
  for (const e of p.elements || []) {
    if (!e.outlineEdits) continue;
    const m = {};
    for (const [k, v] of Object.entries(e.outlineEdits)) if (v && !v.removed) m[k] = v;
    e.outlineEdits = Object.keys(m).length ? m : null;
  }
  // COLOURS ONLY (Ryan 2026-07-28: "make the reset button only affect colors"). The button used to
  // read "Reset all styling" and did exactly that; renaming it to "Reset colors" made it lie, so the
  // behaviour follows the label rather than the other way round.
  //   Outline THICKNESS is snapshotted here because the restore path below rebuilds the whole
  // outline stack from `autoStyle` — colours and thicknesses together — and there is no way to ask it
  // for just the colours. Taking the thicknesses out first and putting them back after is what makes
  // the rebuild a colour-only operation. Sizes need no snapshot; they are simply no longer cleared.
  const keptThickness = new Map();
  for (const e of p.elements || []) {
    if ((e.type === 'arctext' || e.type === 'weight') && Array.isArray(e.outlines)) {
      keptThickness.set(e, e.outlines.map((o) => o.thickness));
    }
  }
  if (p.autoStyle) {
    applyAutoStyleToElements(p);
  } else {
    for (const e of p.elements || []) {
      if (e.type !== 'arctext' && e.type !== 'weight') continue;
      e.fillRef = 'primary';
      e.outlines = markWearsKeyline(p) ? keylineSeedOutlines(p, e.fillRef) : [];
    }
  }
  for (const e of p.elements || []) {
    // Clears BOTH flags (Ryan 2026-07-24): the per-plate lock AND the buyer's user-set mark. Reset
    // colors is the one action that discards explicit choices — that's what makes preserving them on a
    // plate re-cascade safe.
    // SIZE goes back too (Ryan 2026-07-25). The section is "Size & color" and its button now reads
    // "Reset styling", so leaving a shrunken text piece or a resized logo behind would make the
    // button lie. Outline THICKNESS is restored by applyAutoStyleToElements above (it rebuilds the
    // stack from autoStyle, thickness included) — this clears the per-piece scale it can't see.
    //   `sizeUserSet` goes with it (Ryan 2026-07-25). Since sizes are now SEEDED from the engine's
    // edge — 75% of the room for the logo and number, as large as it fits for arc text — clearing
    // the scale without clearing the flag left the piece at a raw 1.0 that no default ever chose,
    // and the seeding was suppressed because the piece still claimed to be hand-sized. Reset means
    // "hand me the defaults back", so it has to un-say that.
    if (e.type === 'arctext' || e.type === 'weight') {
      e.fillLocked = false; e.fillUserSet = false;
      // `sizeScale` / `sizeUserSet` deliberately NOT touched — a hand-sized piece is not a colour.
      (e.outlines || []).forEach((o) => { o.locked = false; o.userSet = false; });
      // Put the buyer's thicknesses back over the rebuilt stack, by index. Only as far as both go:
      // if the rebuild produced more rings than they had, the extras keep autoStyle's thickness.
      const kept = keptThickness.get(e);
      if (kept && Array.isArray(e.outlines)) {
        for (let i = 0; i < Math.min(kept.length, e.outlines.length); i++) {
          if (Number.isFinite(kept[i])) e.outlines[i].thickness = kept[i];
        }
      }
    }
    else if (e.type === 'logo') { e.logoBandsOff = []; e.logoAddOutline = null; e.logoCenterColor = null; e.logoCenterAuto = false; e.logoBandColors = null; e.logoBandUserSet = null; e.logoToneSwaps = null; }
  }
  /* ⛔ AND THE PROFILE'S OWN STASH, or the reset is undone by the next layout change (and, since
     2026-08-12, by the build beat). `savedLogoToneSwaps` re-seeds any logo element that has no paint
     of its own — which, one line above, is now every one of them. Clearing the elements and leaving
     the stash means Reset colors puts the mark back and the very next `inheritSavedToneSwaps` paints
     it again. */
  p.savedLogoToneSwaps = null;
  // Re-derive AFTER the wipe (Ryan 2026-07-25 audit). Reset restores fillRef:'primary' and the logo's
  // original art — which on a black plate with a near-black logo is genuinely invisible INK, not just a
  // stored/painted mismatch: effectiveTextStyle skips the peel entirely on a near-black plate
  // (isNearBlackHex early return), so nothing rescues it at paint time. Reset should restore the
  // DEFAULTS, and white-on-black is what the defaults are for that logo.
  applyVanishingLogoDefaults(p);
}

/**
 * The plate-colour MASTER SWITCH re-cascade (Ryan 2026-07-24). Same job as restoreColorDefaults —
 * re-derive the auto-styled colour defaults so a new plate colour drives the design — except it
 * PRESERVES everything the buyer explicitly chose. The two were one function, which meant picking a
 * new plate colour silently reverted a custom logo fill (pink logo on a blue-logo design → back to
 * blue the moment you dyed the plate green). Reset colours stays the full wipe; that's the one
 * button that clears explicit choices, which is what makes preserving them here safe.
 *
 * ⚠ THE PARAGRAPH ABOVE IS THE ORIGINAL 2026-07-24 CONTRACT AND IT NO LONGER HOLDS — kept because it
 * is why this function exists and is separate from restoreColorDefaults, but the preservation rule it
 * describes lasted one day. Changelog 80 (2026-07-25) replaced it:
 *
 *   "A plate pick now re-baselines everything below it. Explicit picks were preserved forever;
 *    Ryan's model is that a pick is sticky until an ANCESTOR changes. STRUCTURE survives (rings you
 *    added stay, bands you switched off stay off), COLOUR re-derives."
 *
 * So what actually happens now:
 *   • STRUCTURE survives — bands switched off stay off, a ring the buyer ADDED stays a ring (an auto
 *     contrast ring does not; it belongs to the plate that needed it). Capped at the 4-outline max.
 *   • COLOUR re-derives, whether or not the buyer chose it: text/weight fills and every outline
 *     colour, `logoCenterColor`, `logoBandColors`. Every `userSet`/`locked` mark is cleared with it,
 *     since there is nothing left for them to protect.
 *   • THE ONE EXCEPTION is a mark the buyer REPAINTED (`logoToneSwaps`, 2026-07-26). That is a
 *     statement about their artwork, not about this plate, so the band wipe is followed by
 *     re-deriving the overlay from it (`applyToneSwapsToBands`).
 * Does NOT touch the palette, swatch pool, or plate.
 *
 * ⚠ An earlier version of this header still described the 07-24 rule while the body did the 07-25
 * one, AND the body cited "see the header" as its justification — a two-way circular reference to a
 * contract neither of them followed. Corrected 2026-07-29 when the smoke assertion encoding the old
 * rule was finally read rather than skipped. If this rule changes again, this block moves with it.
 *
 * ⚠ AMENDED 2026-08-01 — AND THIS FUNCTION IS DELIBERATELY UNCHANGED BY IT. Ryan's per-plate-colour
 * ink memory means a plate colour the buyer has ALREADY inked gets that ink back instead of a fresh
 * derivation. The tempting edit was to teach this function about `savedInk` and have it skip the
 * re-derive; that would have been the third rewrite of this contract in eight days, and it would have
 * made "what does a re-cascade do" depend on state stored somewhere else.
 *   So the rule below still holds exactly as written: a plate pick re-baselines everything under it,
 * structure survives, colour re-derives. What changed is the SEQUENCE around it — `masterSwitchCascade`
 * (ui/design-panel.js) now runs `restoreInk` as its last step, so the re-derive is what a plate colour
 * gets the FIRST time and the buyer's own ink is what it gets every time after. The smoke assertions
 * for this function were re-read before the change and still encode the current rule, which is why
 * they are untouched: they call `recascadeColorDefaults` directly, not the cascade sequence.
 */
/* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (WD 495, 2026-11-01) — A HAND EDIT TO AN OUTLINE BELONGS TO THE PLATE COLOUR IT WAS
   MADE ON. Ryan, UX run 3 (DB Logo, one Competition bumper, black / green / white): an outline added on black showed up on
   the logo on green and white (recoloured black - "looks goofy"), while the text's stayed put; asked to pick one rule for
   both, his answer was *"1"* - each plate keeps its own outlines - and *"this won't change the defaults right?"* (no).
   This reverses the cross-plate carry of changelog 80 (an ADDED ring rides every plate), WD 486 (a deleted / resized
   app ring holds on every plate) and WD 488 (`ringsOwn` holds on every plate) - for PLATE CHANGES only; within one
   plate every one of them still holds. The hand state now rides the per-plate ink record (`captureInk` -> `own`,
   `outlinesAdded`, `edits`, logo `addsW`), a plate change re-derives the defaults without it, and `restoreInk` puts back
   the destination plate's own. Defaults are untouched: no default design has any hand state (corpus-baseline). */
export function recascadeColorDefaults(p, { plateChange = false } = {}) {
  if (!p) return;
  const keep = new Map();
  for (const e of p.elements || []) {
    if (e.type !== 'arctext' && e.type !== 'weight') continue;
    keep.set(e, {
      // Keyed off USER-SET, not the LOCK (Ryan 2026-07-24 bug). The lock is peel/swap immunity for the
      // plate that was current when it was set — including on AUTO rings, since syncFillContrastOutline
      // locks the contrast ring it adds. Preserving locks across a plate change therefore carried a ring
      // that existed only to fix the OLD plate onto the new one, immune to the new conflict check: a
      // green outline survived onto a green plate. Locks are cleared below; user-set colours survive.
      // COLOURS ARE NOT PRESERVED ACROSS A PLATE CHANGE (Ryan 2026-07-25, changelog 80). Only the
      // STRUCTURE is: an outline the buyer ADDED goes on existing, it just re-derives its colour.
      fillRef: null,
      outlines: plateChange ? [] : (e.outlines || [])
        .map((o, i) => (o && o.added === true ? { i, o: { ...o } } : null))
        .filter(Boolean),
    });
    // WD 495: on a plate change the hand state is the OLD plate's (already stamped into its ink record) - start clean.
    if (plateChange) { e.ringsOwn = false; e.outlineEdits = null; }
  }
  if (p.autoStyle) {
    applyAutoStyleToElements(p);
  } else {
    for (const e of p.elements || []) {
      if (e.type !== 'arctext' && e.type !== 'weight') continue;
      e.fillRef = 'primary';
      e.outlines = markWearsKeyline(p) ? keylineSeedOutlines(p, e.fillRef) : [];
    }
  }
  for (const e of p.elements || []) {
    if (e.type === 'arctext' || e.type === 'weight') {
      const k = keep.get(e);
      if (!k) continue;
      // Every colour flag is cleared: the plate is the top of the cascade, so a new plate pick
      // re-baselines everything under it. `assignOutlineColors` (run right after, in
      // masterSwitchCascade) then colours the whole stack against the new plate.
      e.fillUserSet = false;
      e.fillLocked = false;
      applyOutlineEdits(e); // WD 486: the buyer's deletions + thicknesses on the app's own rings
      // WD 488: a text whose rings the buyer took over (`ringsOwn`) keeps ONLY those - no app re-seed underneath them.
      const next = e.ringsOwn === true ? [] : (Array.isArray(e.outlines) ? e.outlines.slice() : []);
      // ADDED rings survive as rings — the buyer asked for that many outlines, and a plate pick is
      // not a request to delete one — but they come back stripped of `userSet`/`locked` so their
      // colour is re-derived like every other ring.
      for (const { o } of k.outlines) next.push({ ...o, locked: false, userSet: false });
      e.outlines = next.slice(0, 4);
    } else if (e.type === 'logo') {
      // The logo branch, same rule. STRUCTURE persists (which bands are switched off, and any ring
      // the buyer added); COLOUR re-derives.
      const rings = Array.isArray(e.logoAddOutline) ? e.logoAddOutline : (e.logoAddOutline ? [e.logoAddOutline] : []);
      const manual = plateChange ? [] : rings.filter((a) => a && a.auto !== true); // WD 495: per plate on a plate change
      e.logoAddOutline = manual.length ? manual : null;
      // Fill AND band colours both go back to "derive me", whether or not the buyer chose them.
      // Clearing the fill to null means "original art", which is the right baseline whenever the mark
      // reads on the new plate; applyVanishingLogoDefaults below re-flattens it only if it would
      // vanish. Band overrides drop to the logo's own detected colours for the same reason.
      e.logoCenterColor = null;
      e.logoCenterAuto = false;
      e.logoBandColors = null;
      e.logoBandUserSet = null;
    }
  }
  // …but "derive me" does NOT mean "back to the original artwork" for a mark the buyer has
  // REPAINTED (Ryan 2026-07-26). `logoToneSwaps` is a statement about their logo, not about how a
  // colour should be chosen against this plate, so the wipe above is followed by re-deriving the
  // band overlay from it. A plate pick still re-baselines everything it owns; it just no longer
  // reaches through into their artwork. Runs BEFORE the vanishing check so that check judges the
  // mark by the colours it is actually wearing.
  applyToneSwapsToBands(p);
  // Same re-derive as restoreColorDefaults: the new plate deserves a fresh verdict, and this is a
  // no-op whenever the logo reads on it.
  applyVanishingLogoDefaults(p);
}

/**
 * SOLID WHEN THE FILL ALREADY READS (Ryan 2026-07-24) — the plate-pick half of the solid-vs-outline
 * symmetry `syncFillContrastOutline` applies on a FILL pick.
 *
 * For an outline-less logo a text outline is a CONTRAST DEVICE, not decoration: it exists so the
 * lettering survives a plate colour that swallows the fill. `syncFillContrastOutline` says so both
 * ways — a fill that merges with the plate gets ONE ring, a fill that reads gets NONE — but it only
 * ever ran when the buyer picked a FILL. A plate pick went through `masterSwitchCascade`
 * (recascade + assignOutlineColors), which re-derives the auto ring from `autoStyle` unconditionally,
 * so dyeing the plate to a colour the fill already reads against left a ring with no job.
 *
 * That ring is then usually unfixable rather than merely redundant: with a yellow fill on a green
 * plate and a two-colour palette, `assignOutlineColors` must find a colour that is neither yellow
 * (innermost ≠ fill) nor green (outermost ≠ plate), finds none, and takes its `if (!pick) continue`
 * branch — leaving the ring green for the render-time peel to hide. The plate looked right, but the
 * Colors row showed a greyed swatch for a ring that should never have been derived.
 *
 * REVERSIBLE, which is what makes deleting safe here: `recascadeColorDefaults` re-derives the auto
 * stack from `autoStyle` on EVERY plate change, and this runs immediately after it, so it only ever
 * drops rings that were just re-derived. Un-dye back to black and the ring returns with the cascade.
 *
 * Explicit rings are NOT touched — `userSet` (the buyer chose this colour) and `added` (the buyer
 * made this ring) are style choices, not contrast devices, and preserving them across a plate change
 * is exactly what entry 60 built the locked/userSet split for. Deleting them here would re-break it.
 *
 * A logo WITH its own bands keeps its layered stack untouched: those rings mirror the artwork, so the
 * fill/plate relationship doesn't govern them. Same guard `syncFillContrastOutline` opens with.
 * Returns true if anything changed.
 */
export function dropAutoOutlinesWhenFillReads(p) {
  if (!p) return false;
  const bands = p.logoStructure && Array.isArray(p.logoStructure.bands) ? p.logoStructure.bands : [];
  if (bands.length) return false;        // banded logo → deliberate layered stack, leave it alone
  // ── …AND SO IS A MULTI-TONE MARK, whether or not the band detector could see its rings ──────────
  // (Ryan 2026-07-28) "on the default black plate it had orange letters and a blue outline, looks
  // good. On the blue plate it defaulted to orange letters with NO outline… each time I hit Add for
  // Text outlines it looked phenomenal and picked the right colour."
  //   The premise above is that an auto outline is a CONTRAST DEVICE — so once the fill reads on the
  // plate the ring has no job and is just noise. True for a plain one- or two-tone mark, where the
  // whole design is a fill and a plate. False for a mark like the Denver badge, where the ring is the
  // text's half of an ECHO: the logo wears orange inside navy inside orange, and lettering that wears
  // the same pairing is the entire "their brand is the hero" idea B1 was built for. Stripping it
  // leaves the letters looking unfinished beside a logo that is anything but.
  //   The guard for that already existed — `bands.length`, "the logo has its own layered stack" — and
  // it simply never fired here, because the band detector declines this badge. Its outermost depth
  // band comes out as the WHITE anti-alias rim the background key feathers onto the silhouette, which
  // faces background 16% against a 20% floor, so a badge with two unmistakable rings reports none.
  // Fixing the detector would move band colours, the peel, the band chips and the whole auto-style
  // stack for every logo; this asks the question the rule actually cares about — is there more to
  // this design than a fill and a plate — off a number that is already measured, already shown to the
  // buyer as chips, and independent of whether the rings were geometrically resolvable.
  //   THREE is the line, and it is Ryan's: "I understand the single-colour letters when pairing with
  // 1-tone or 2-tone logos". A two-tone design has exactly one other colour, and spending it on a ring
  // the fill does not need is what this function was written to prevent. That case is untouched.
  //   0 means the palette was never read confidently — unknown, not "few" — so it keeps the old
  // behaviour rather than guessing.
  if ((p.logoToneCount || 0) >= 3) return false;
  const plate = normalizeHex(p.bodyColor);
  if (!plate || isNearBlackHex(plate)) return false; // undyed: the auto stack is the design, not a fix
  const pal = p.palette || {};
  /* ── …AND A PLATE CARRYING NEITHER OF THE TWO TONES KEEPS ITS RING (Ryan 2026-08-10) ───────────
   * The 07-28 rule above is right and rests on a premise worth naming: on a DYED plate a two-tone
   * design already shows both tones — one as the lettering, THE OTHER AS THE PLATE. The ring would
   * be a third use of a colour that is right there, which is exactly the waste this function exists
   * to prevent.
   *
   * 🔑 THAT PREMISE BREAKS ON AN INTRODUCED NEUTRAL. Since 2026-08-10 the plate sequence spends the
   * buyer's colours and then offers WHITE and BLACK, and those plates carry none of their brand. The
   * design then shows exactly ONE of their two tones and the second has nowhere else to appear — so
   * the ring stops being decoration and becomes the only way it reaches the plate at all. Ryan,
   * comparing the two neutrals in the same set: *"the black plate gets green/yellow text and logo,
   * and it too is technically a neutral one."*
   *
   * ⛔ THE TEST IS "DOES THE PLATE WEAR ONE OF THEIR TONES", NOT "IS THE PLATE NEUTRAL", and the
   * difference is not academic. A red-and-white mark (Garage Gym Reviews) has WHITE as a real brand
   * colour, so its white plate IS carrying one of the two and must still go solid — a neutral-plate
   * test would try to ring it and be wrong in spirit. Ryan asked for exactly this safeguard.
   *
   * ⚠ PERCEPTUAL, not equality: a #FEFEFE logo tone and a #FFFFFF plate are the same plate to anyone
   * looking at it, and `===` would happily call them different.
   *
   * ⚠ PURELY ADDITIVE — this can only ever KEEP a ring, never strip one that survives today, which is
   * what keeps the blast radius to the case under discussion.
   *
   * ⚠ THE TWO TONES ARE THE ROLES. This function is handed a profile and no extractor palette, and
   * for a mark that reports two tones `primary`/`secondary` ARE that pair — the same two the Add Logo
   * chips showed. Above three tones the early return has already fired, so there is no case where
   * this undercounts. */
  /* ⚠ EXACTLY TWO, and that is not a fixture detail. The whole argument is "the design's SECOND tone
     has nowhere else to appear" — a one-tone mark has no second tone, so it is owed nothing here and
     keeps the behaviour it has always had. (0 means the palette was never read confidently, which
     the early return above already treats as unknown rather than as "few".) */
  if ((p.logoToneCount || 0) === 2) {
    const wearsATone = ['primary', 'secondary']
      .map((r) => normalizeHex(pal[r]))
      .filter(Boolean)
      .some((h) => colorsIndistinguishable(h, plate));
    if (!wearsATone) return false;
  }
  let changed = false;
  for (const e of p.elements || []) {
    if (e.type !== 'arctext' && e.type !== 'weight') continue;
    const outs = Array.isArray(e.outlines) ? e.outlines : [];
    if (!outs.length) continue;
    // The STORED fill, not the effective one. The render-time contrast remap is a VIEW (it repaints a
    // vanishing fill without changing it), and a stack must never be mutated off a view — un-dyeing
    // would restore a fill whose ring we'd already deleted for reasons that no longer hold.
    const fill = normalizeHex(resolvePaletteRef(e.fillRef || 'primary', pal));
    /* ⛔ "THE FILL READS" WAS ASKED WITH `colorsConflict`, WHICH CANNOT ANSWER IT (Ryan 2026-08-17).
     * This whole function rests on the premise that once the fill reads on the plate the ring has no
     * job — and ΔE says a cream #FAF2CB number reads on a gold #FCCB0C plate, at 1.36:1. So the one
     * pass whose entire subject is legibility deleted the ring holding the only illegible pairing in
     * the design off its plate. `readsOn` is the operator that question always wanted.
     * ⚠ PURELY ADDITIVE, same discipline as the 2026-08-10 widening above: this can only ever KEEP a
     * ring, never strip one that survives today. A ring on a fill that reads is still dropped, so
     * every design Ryan has signed off on is untouched. */
    if (!fill || colorsConflict(plate, fill) || !readsOn(plate, fill)) continue; // the fill vanishes → it NEEDS its ring
    const kept = outs.filter((o) => o && (o.userSet === true || o.added === true));
    if (kept.length !== outs.length) { e.outlines = kept; changed = true; }
  }
  return changed;
}

/**
 * ── THE BUYER'S EDITS TO THE APP'S OWN TEXT OUTLINES SURVIVE A PLATE CHANGE (WD 486, Ryan 2026-11-01: option 1) ──
 * Every plate pick re-derives the text + weight outline stack from `autoStyle` (`recascadeColorDefaults`) and kept
 * only rings the buyer ADDED, so an app ring they DELETED came back and a THICKNESS they dialled snapped back. The
 * same defect the logo ring had (WD 457, `logoRingSuppressed`), but four slots, not one switch - so it is a memory
 * PER SLOT: `el.outlineEdits = { [autoIdx]: { removed: true } | { thickness } }`, keyed by `autoIdx` (which autoStyle
 * ring this is - stable across a re-derive, unlike the position). Recorded by the panel's ✕ / zero-dial / thickness
 * dial, replayed after every re-derive, cleared by a new logo and by Reset colors, and by "+ Add" restoring that slot.
 * Colour is NOT part of it: colour keeps its own rule (re-derived per plate, `restoreInk` per plate colour).
 */
/**
 * CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (WD 488, UX run 2, 2026-11-01) — THE BUYER TAKES THE TEXT RINGS OVER.
 * The text's rings on the plate are a VIEW: `effectiveTextStyle` mirrors the logo's stack onto whatever the element
 * stores. The panel listed only the stored rings, so Drop Bear on the cream plate showed "0 outlines" round a number
 * wearing a maroon one, "+" made a ring the outline re-balance deleted, and a second LOGO ring appeared on the text
 * with the panel still at 0 (Ryan, run 2 bugs 1/3/4). His answer, "hand-edit = text's own": the first press on a text
 * outline control copies what the plate SHOWS into each text element (as the buyer's own: `added` + `userSet`, so the
 * re-balance and the plate re-derive keep them) and sets `ringsOwn`, after which the mirror stands down for it.
 * Idempotent; returns true when it changed anything. Never called on a default path.
 */
export function adoptTextRings(state) {
  const p = state && state.designProfile;
  if (!p) return false;
  const plate = effectivePlateHex(state);
  const rp = renderPaletteFor(p, plate);
  let changed = false;
  for (const e of p.elements || []) {
    if ((e.type !== 'arctext' && e.type !== 'weight') || e.ringsOwn === true) continue;
    const eff = effectiveTextStyle(e, rp, plate);
    e.outlines = (eff.outlines || []).slice(0, 4).map((o) => ({
      colorRef: normalizeHex(resolvePaletteRef(o.colorRef, rp)) || o.colorRef,
      thickness: o.thickness, added: true, userSet: true,
    }));
    e.outlineEdits = null; // the stack is the buyer's now; there are no app slots left to remember
    e.ringsOwn = true;
    changed = true;
  }
  return changed;
}
/** The text rings the plate SHOWS, inner -> outer (the panel lists these until the buyer takes them over). */
export function shownTextRings(state, el) {
  const p = state && state.designProfile;
  if (!p || !el) return [];
  if (el.ringsOwn === true) return Array.isArray(el.outlines) ? el.outlines : [];
  const plate = effectivePlateHex(state);
  const rp = renderPaletteFor(p, plate);
  return (effectiveTextStyle(el, rp, plate).outlines || []).slice(0, 4).map((o) => ({
    colorRef: normalizeHex(resolvePaletteRef(o.colorRef, rp)) || o.colorRef, thickness: o.thickness,
  }));
}
export function noteOutlineEdit(el, ring, edit) {
  if (!el || !ring || ring.added === true || !Number.isInteger(ring.autoIdx)) return;
  const k = String(ring.autoIdx);
  const m = el.outlineEdits && typeof el.outlineEdits === 'object' ? el.outlineEdits : {};
  if (edit === null) delete m[k];
  else if (edit.removed) m[k] = { removed: true };
  else if (Number.isFinite(edit.thickness)) m[k] = { thickness: edit.thickness };
  el.outlineEdits = Object.keys(m).length ? m : null;
}
/** Replay `el.outlineEdits` onto a freshly derived stack: removed slots go, dialled thicknesses come back. */
export function applyOutlineEdits(el) {
  const m = el && el.outlineEdits;
  if (!m || typeof m !== 'object' || !Array.isArray(el.outlines)) return;
  el.outlines = el.outlines.filter((o) => {
    if (!o || o.added === true || !Number.isInteger(o.autoIdx)) return true;
    const ed = m[String(o.autoIdx)];
    if (ed && ed.removed) return false;
    if (ed && Number.isFinite(ed.thickness)) o.thickness = ed.thickness;
    return true;
  });
}

/**
 * "+ Add" on the text-outline row (Ryan 2026-07-24). Since ✕ now REMOVES a text outline instead of
 * graying it, Add is the only way back, so it is restore-then-append:
 *   • if any autoStyle default slot is missing, reinstate the LOWEST one at its own depth, with its own
 *     colour and thickness — identified by `autoIdx`, not by position, so deleting inside-out is safe;
 *   • otherwise append a brand-new user outline (`added: true`) from `nextSpec`.
 * A restored default slots in before the first outline that sits further out — a later default slot, or
 * any user-added ring (those are always outermost and carry no autoIdx).
 * Mutates `outs` in place. Returns the restored slot index, or -1 if it appended / did nothing.
 */
export function restoreOrAppendOutline(outs, seq, nextSpec) {
  if (!Array.isArray(outs) || outs.length >= 4) return -1;
  const list = Array.isArray(seq) ? seq : [];
  const present = new Set(outs.map((o) => (o && Number.isInteger(o.autoIdx) ? o.autoIdx : -1)));
  let missing = -1;
  for (let i = 0; i < list.length; i++) if (!present.has(i)) { missing = i; break; }
  if (missing >= 0) {
    const at = outs.findIndex((o) => !o || !Number.isInteger(o.autoIdx) || o.autoIdx > missing);
    const entry = { colorRef: list[missing].colorRef, thickness: list[missing].thickness, autoIdx: missing };
    if (at < 0) outs.push(entry); else outs.splice(at, 0, entry);
    return missing;
  }
  if (typeof nextSpec !== 'function') return -1;
  const s = nextSpec(outs.length);
  outs.push({ colorRef: s.colorRef, thickness: s.thickness, added: true });
  return -1;
}

/**
 * Re-colour the AUTO outline rings so nothing touching shares a colour (Ryan 2026-07-24).
 *
 * The stack renders as a chain — `text fill → inner … outer → plate` — and the old colour picker
 * (`nextOutlineSpec`) just cycled the logo's outline sequence by position, blind to both ends of that
 * chain. On a green plate with green text, adding a 3rd ring handed out green for the outermost slot:
 * it merged into the plate, the peel hid it, and you got a greyed swatch. Re-colouring the whole stack
 * instead of choosing one slot's colour is what turns `Y Y G(hidden)` into `Y G Y` — the green moves to
 * the MIDDLE, which is the only arrangement two colours allow.
 *
 * Constraints, in priority order:
 *   MUST   the innermost ring differs from the text fill, and the outermost from the plate — a ring
 *          that merges into its neighbour outside the stack is simply invisible.
 *   SHOULD each ring differs from the ring inside it. This one YIELDS when the palette can't satisfy
 *          everything, because two touching same-colour rings just read as one thicker band (which is
 *          why `Y Y` is the right answer for two rings on a green-on-green design, and why the buyer
 *          saw that and called it correct).
 *
 * Rings with `userSet` are never touched — the buyer's colour outranks the rule. Comparison uses
 * `colorsConflict`, the same perceptual test the peel uses, so "no conflict" here means exactly "the
 * peel will not hide this". Candidates are palette REFS first (so a later palette edit still
 * propagates) then saved swatch hexes. Returns true if anything changed.
 */
/**
 * Every colour currently WORN as an outline by another element (Ryan 2026-08-03: *"why is it not
 * detecting that another element on the weight already has a medium blue outline and matching that
 * one?"*).
 *
 * The two outline systems were built blind to each other — `assignOutlineColors` skips anything that
 * is not `arctext`/`weight`, and the logo's added rings were chosen purely against the plate. So a
 * design could hand the text medium blue and the logo dark blue with nothing noticing they were meant
 * to look like one object. This is the shared reading, ordered inner→outer per element so the first
 * hit is the most prominent ring rather than an arbitrary one.
 *
 * Deliberately a PREFERENCE and not a constraint: the caller still filters these through the same
 * contrast tests, so cohesion can never override legibility.
 *
 * @param {object} p design profile
 * @param {(ref:any)=>string|null} hexOf resolves a colorRef to a hex in the caller's palette context
 */
export function wornOutlineHexes(p, hexOf) {
  const worn = [];
  for (const e of (p && p.elements) || []) {
    if (e.type !== 'arctext' && e.type !== 'weight') continue;
    for (const o of Array.isArray(e.outlines) ? e.outlines : []) {
      const h = o && hexOf(o.colorRef);
      if (h && !worn.includes(h)) worn.push(h);
    }
  }
  return worn;
}

/* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (WD 487, 2026-11-01) — A LOGO RING THE BUYER ADDED, RE-COLOURED AGAINST THE LOGO
 * AS WELL AS THE PLATE. Hoisted from design-panel.js (`nextAddedRingColor` / `resyncAddedLogoRings`) so the weight-hop
 * path can run it too. Ryan, DB Logo run 1: he added a white ring on black, picked the green plate - the logo went
 * white and the ring stayed white; picked white - the logo went green and the ring went green. The old check measured
 * the first ring against the PLATE only (`inside` was null, although its comment said "the logo edge itself"), so a
 * ring the logo had just turned into was "still legible". Now the first ring must also clear the mark's own edge as
 * drawn on this plate. A ring the buyer coloured with the pencil (`userSet`) is never touched (his answer 3).
 * ⛔ Only buyer-added rings: an `auto` ring is `syncLogoContrastRing`'s, and the corpus never adds one by hand, so
 * the approved pass cannot move. */
function logoEdgeHexes(state) {
  const p = state.designProfile;
  const logoEl = (p.elements || []).find((e) => e && e.type === 'logo' && e.logo);
  if (!logoEl) return [];
  const centre = normalizeHex(logoEl.logoCenterColor);
  if (centre) return [centre];
  const plate = effectivePlateHex(state);
  const bare = { ...p, elements: p.elements.map((e) => (e === logoEl ? { ...e, logoAddOutline: null } : e)) };
  const out = [];
  try { const t = normalizeHex(renderPaletteFor(bare, plate).logoOuterTone); if (t) out.push(t); } catch {}
  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (WD 488, UX run 2) - AND THE EDGE THE ARTWORK DRAWS ITSELF. A band-less mark
     (Drop Bear: low-confidence structure, a maroon keyline around the bear) reports no `logoOuterTone` on a light
     plate, so a hand-added ring came in maroon on maroon - Ryan: "the exact same color as the logos current outline".
     Only the buyer's added rings ask this (addedLogoRingColor / resyncAddedLogoRings); no default reads it. */
  // ...and when that drawn edge is all the logo shows outermost, the ring touches IT, not the body inside it (Drop Bear's
  // grey body sits inside its maroon keyline, so grey is a fine ring colour there - Ryan's "pick one you can see").
  let drawnEdgeOnly = false;
  try { const w = normalizeHex(renderPaletteFor(bare, plate).logoEdgeWrap?.hex); if (w && !out.includes(w)) { drawnEdgeOnly = !out.length; out.push(w); } } catch {}
  if (!drawnEdgeOnly) { try { const d = normalizeHex(markDominantTone(bare, plate)); if (d && !out.includes(d)) out.push(d); } catch {} }
  return out;
}
export function addedLogoRingColor(state, arr) {
  const p = state.designProfile;
  const pal = p.palette || {};
  const rp = { ...pal, pool: contrastPoolFrom(pal, p.swatches) };
  const hexOf = (ref) => normalizeHex(resolvePaletteRef(ref, rp));
  const plate = normalizeHex(effectivePlateHex(state));
  const inside = arr.length ? [normalizeHex(arr[arr.length - 1].color)] : logoEdgeHexes(state);
  const clears0 = (hex) => !!hex && !(plate && colorsConflict(plate, hex)) && !inside.some((i) => i && colorsConflict(i, hex));
  /* WD 488 (UX run 2, Ryan): Drop Bear on the off-white plate got BLACK round its maroon edge - a different hex, 1.12:1,
     so "the logo's outline becomes twice as thick at the same color". His rule: pick one you can SEE when the palette
     has one; only when nothing does, the old pick stands. So the ladder runs twice - first demanding the ring READ
     against what it wraps (`readsOn`), then as before. Buyer-added rings only; no default calls this. */
  const visible = (hex) => clears0(hex) && !inside.some((i) => i && !readsOn(i, hex));
  for (const clears of [visible, clears0]) {
    // 1. cohesion - a colour the design already wears as an outline; 2. the buyer's pool
    const worn = wornOutlineHexes(p, hexOf).find(clears);
    if (worn) return worn;
    const fromPool = (rp.pool || []).map(normalizeHex).find(clears);
    if (fromPool) return fromPool;
  }
  // 3. best contrast
  const against = inside[0] || plate;
  return normalizeHex(resolvePaletteRef(bestContrastRef(rp, against), rp)) || '#FFFFFF';
}
export function resyncAddedLogoRings(state) {
  const logoEl = (state.designProfile.elements || []).find((e) => e.type === 'logo' && e.logo && e.logo.dataUrl);
  if (!logoEl) return false;
  const rings = Array.isArray(logoEl.logoAddOutline) ? logoEl.logoAddOutline : (logoEl.logoAddOutline ? [logoEl.logoAddOutline] : []);
  const plate = normalizeHex(effectivePlateHex(state));
  let changed = false;
  for (let i = 0; i < rings.length; i++) {
    const a = rings[i];
    if (!a || a.auto === true || a.userSet === true) continue;
    const cur = normalizeHex(a.color);
    const inside = i ? [normalizeHex(rings[i - 1].color)] : logoEdgeHexes(state);
    const reads = cur && !(plate && colorsConflict(plate, cur)) && !inside.some((h) => h && colorsConflict(h, cur));
    if (reads) continue; // still reads - leave it (stability)
    const pick = addedLogoRingColor(state, rings.slice(0, i));
    if (pick && pick !== cur) { a.color = pick; changed = true; }
  }
  return changed;
}

/**
 * The plate colour the ink is actually PAINTED ON — which is not `profile.bodyColor` whenever the
 * product cannot wear a dye. A Training bumper sitting in a design dyed blue still renders black
 * (main.js's `plateBodyColor()`), so anything reasoning about contrast has to ask this, not the
 * profile field. Same rule `inkContextKey` uses to bucket the ink; this returns the real hex.
 */
export function effectivePlateHex(state) {
  if (!state) return DEFAULT_BODY_COLOR;
  return productSupportsBodyColor(state.ui.product)
    ? (normalizeHex(state.designProfile.bodyColor) || DEFAULT_BODY_COLOR)
    : DEFAULT_BODY_COLOR;
}

/**
 * Swap a FILL that merges with the plate it is painted on, for pieces the buyer has not inked
 * themselves. The outline twin of this is `assignOutlineColors` below.
 *
 * ⚠ NARROW ON PURPOSE — this is for the ONE path nothing else covers (Ryan 2026-08-05). A hop to an
 * always-black product normally runs `applyCrossProductContrastRemap`, which checks fills and rings
 * and offers an Undo toast. That remap is skipped precisely when `adoptedInk` is truthy — the buyer
 * has a saved record for this plate colour, and overwriting a real choice with a guess is worse. But
 * a bucket can hold an answer nobody ever chose: the SEEDED ink, stamped the first time the design
 * sat on black. So this runs only on that path, and only for `fillUserSet !== true`.
 *
 * ⛔ NOT `fillContrastRemap`. That has the same near-black bail, and there it is DELIBERATE and
 * asserted ("black/undyed plate → no logo remap"): it adapts a LOGO to a DYED plate, and the stock
 * black plate is where the artwork should render as drawn. Same-looking guard, different question.
 */
export function correctIllegibleFill(p, baseHex, opts = {}) {
  if (!p) return false;
  const pal = p.palette || {};
  const base = normalizeHex(baseHex);
  if (!base) return false;
  const hexOf = (ref) => normalizeHex(resolvePaletteRef(ref, pal));
  const refs = [];
  const seen = new Set();
  const offer = (ref) => { const h = hexOf(ref); if (h && !seen.has(h)) { seen.add(h); refs.push(ref); } };
  ['primary', 'secondary', 'tertiary'].forEach((r) => { if (pal[r]) offer(r); });
  (Array.isArray(p.swatches) ? p.swatches : []).forEach(offer);
  usableAccents(refs.map(hexOf)).forEach(offer); // house neutrals last, same order as the ring pass
  let changed = false;
  for (const e of p.elements || []) {
    if (e.type !== 'arctext' && e.type !== 'weight') continue;
    /* ⚠ A RING BETWEEN THEM MEANS THE FILL NEVER TOUCHES THE PLATE. The fill is the innermost thing
       on the piece and the plate is outside everything, so a fill matching the plate is only a fault
       when NO enabled ring separates them. Without this the pass would move a fill that reads
       perfectly well — grey text on a grey plate is fine while a blue ring wraps it, which is exactly
       the state Ryan was in before he pressed ✕. */
    const rings = Array.isArray(e.outlines) ? e.outlines.filter((o) => o && o.enabled !== false) : [];
    /* ⚠ THE RING EXEMPTION IS FOR THE MIRROR KEEPS, NOT FOR WEAK FILLS (Ryan 2026-08-23, ECC on
       the gray plate: red text at 1.9:1 rode its ring while he wanted the white). A ringed fill
       that ΔE-MERGES the plate is the deliberate outlined look (Rockford purple-on-purple) and
       stays. A ringed fill that is merely UNREADABLE on a near-neutral plate — distinct in ΔE,
       failing luminance — is just weak, and moves like any other ghost. Chromatic plates keep
       the full exemption (hue carries — the E Logo keeps). */
    if (rings.length) {
      const base0 = normalizeHex(baseHex);
      const cur0 = hexOf(e.fillRef || 'primary');
      const weakOnNeutral = base0 && cur0 && isNearNeutralHex(base0)
        && !colorsConflict(base0, cur0) && !readsOn(base0, cur0);
      if (!weakOnNeutral) continue;
    }
    /* `force` is for the one trigger where the buyer's OWN action created the conflict: deleting the
       last ring off a fill that matches the plate. Honouring `fillUserSet` there would leave the text
       invisible and call it respect — and doing nothing is the worse answer, because the alternative
       remedy (auto-adding a rescue ring back) reads as "delete didn't work". The swap is announced and
       one undo away. Everywhere else a colour the buyer inked is still untouchable. */
    if (e.fillUserSet === true && opts.force !== true) continue;
    const cur = hexOf(e.fillRef || 'primary');
    /* ⛔ ΔE ALONE CANNOT SEE A GHOST ON A NEUTRAL PLATE (Ryan 2026-08-23, Amherst: purple text on
       the black plate — *"won't white show up better?"*). Purple-on-black is ΔE ≈ 38 — past
       `colorsConflict` AND past `nearMerges`' cap — yet reads at ~1.4:1, because a NEAR-NEUTRAL
       plate has no chroma of its own: contrast there rides luminance alone, and `readsOn` is the
       only honest operator. On a CHROMATIC plate the ΔE tests stay the whole trigger — hue
       opposition genuinely carries (E Logo's teal-on-teal keep, orange ring at 1.1:1, is the
       protected case). The pick prefers a candidate that clears every test the trigger used,
       relaxing to merely-not-merging so a weak palette still moves somewhere. Amherst's white is
       `secondary` — a real school colour, first in the ladder — which is exactly Ryan's "white
       that is part of the actual logo should be treated as an official school colour". */
    const neutralPlate = isNearNeutralHex(base);
    if (!cur || !(colorsConflict(base, cur) || nearMerges(base, cur) || (neutralPlate && !readsOn(base, cur)))) continue;
    /* ⛔ THE PICK WAS BLIND TO THE RING IT WEARS — the fill-side twin of WD 332's Add-ladder fix
       (Ryan 2026-08-23, Angry Panda: the rescue moved the ghost fill to `secondary`… which is the
       mark's orange WRAP tone, already seeded as the text's ring — orange-on-orange, the ring gone
       the moment it was needed). The first tier now also clears the element's enabled rings, so
       the panda lands cream-fill + orange-ring, the mark's own presentation. Plate readability
       outranks ring visibility: the ring-aware tier relaxes away first, and the ring machinery
       can still recolour a merged ring downstream. */
    const clearsPlate = (h) => h && !colorsConflict(base, h) && !nearMerges(base, h) && (!neutralPlate || readsOn(base, h));
    const ringHexes = rings.map((o) => normalizeHex(resolvePaletteRef(o.colorRef, pal))).filter(Boolean);
    const pick = refs.find((r) => {
      const h = hexOf(r);
      return clearsPlate(h) && !ringHexes.some((rh) => rh === h || colorsConflict(rh, h));
    })
      || refs.find((r) => clearsPlate(hexOf(r)))
      || refs.find((r) => { const h = hexOf(r); return h && !colorsConflict(base, h); });
    if (pick && hexOf(pick) !== cur) { e.fillRef = pick; changed = true; }
  }
  return changed;
}

/**
 * What a given outline ring may wear — the ONE answer, shared by the pass that derives ring colours
 * and by the picker's swap row that offers them.
 *
 * ⚠ THIS WAS INLINE IN `assignOutlineColors` UNTIL 2026-08-05, and the swap row was offering the raw
 * palette. So the shortcut cheerfully suggested a ring colour the derivation would never have picked
 * — black on black text, blue on a blue plate (Ryan: *"it's going to let me change to black (which
 * blends in with the black text color), or blue (which blends in with the blue plate color)"*). Two
 * places answering one question is the failure this project keeps paying for; now there is one.
 *
 *   • MUST — the hard constraints. The INNERMOST ring cannot merge with the fill it wraps, and the
 *     OUTERMOST cannot merge with the plate behind it. Those are the two edges that make a ring
 *     visible at all.
 *   • SHOULD — the soft one: don't merge with the neighbour immediately inside you, or the two read
 *     as a single thicker band. Relaxable; `assignOutlineColors` drops it before giving up.
 *
 * ⛔ "MERGE" WAS ASKED ENTIRELY IN ΔE, WHICH CANNOT SEE A LIGHTNESS MATCH (Ryan 2026-08-17, CSI).
 * Both MUST clauses are about a ring being VISIBLE against an edge, and `colorsConflict` answers the
 * different question "is this a different colour" — so on the CSI gold plate this pass accepted a
 * house WHITE ring at 1.53:1 against the plate and 1.13:1 against the cream fill it wrapped, and
 * reported it as fitting. Both edges now also have to READ (`readsOn`, WCAG ≥ 2.0 — see its header
 * for the corpus measurement behind that bar). With white rejected, the ladder's next house neutral
 * is BLACK, which is what the eagle beside it wears.
 *   ⚠ MUST only, never SHOULD. The soft clause is about two rings reading as one thicker band —
 * genuinely a "same colour?" question, and the pass is allowed to drop it entirely when it runs out
 * of candidates, so tightening it would only shrink an already-relaxable preference.
 *   ⚠ This narrows what MUST accepts, so a ring can now fail where it used to pass. Every ladder
 * that consults it ends in `usableAccents`, i.e. white AND black, and no plate can be too close to
 * both — so the fallback always exists and the failure mode is a different ring colour, never no
 * ring. Measured against `tools/baseline-renders.mjs` before shipping. */
export function ringFitTest({ outs, i, fill, plate, plateBites, hexOf }) {
  const isInner = i === 0;
  const isOuter = i === outs.length - 1;
  const innerNb = isInner ? fill : hexOf(outs[i - 1].colorRef);
  return {
    must: (hex) => {
      if (!hex) return false;
      if (isInner && fill && (colorsConflict(fill, hex) || !readsOn(fill, hex))) return false;
      if (isOuter && plateBites && (colorsConflict(plate, hex) || !readsOn(plate, hex))) return false;
      return true;
    },
    should: (hex) => !(innerNb && colorsConflict(innerNb, hex)),
  };
}

/**
 * Does `hex` read as outline ring `idx` on `el`, painted on `baseHex`? The swap row's filter.
 * Wraps `ringFitTest` so a caller holding an element and an index needs no knowledge of the rule.
 *
 * ⛔ THE PICKER'S BAR IS "NOT INVISIBLE", NOT "WOULD THE APP CHOOSE IT" (Ryan 2026-08-23, ECC
 * Kats on the gray plate: *"no black, white, or C0272E — what's that about?"* — the brand red
 * sat at 1.90:1 against the gray plate, a hair under `readsOn`'s 2.0, and the swap row hid it).
 * That bar exists so the AUTO derivation never seeds a weak ring; an EXPLICIT pick is the
 * buyer's, and the standing rule everywhere else is that their own colours stay pickable
 * (Bauers' #010101 note: "an explicit pick is still theirs to make"). So this filter asks only
 * the ΔE-merge questions — a ring literally indistinguishable from its fill or plate is still
 * withheld, a merely-weak one is offered. `ringFitTest`'s full bar stays on every auto path.
 */
export function outlineHexReads(p, el, idx, hex, baseHex) {
  const pal = (p && p.palette) || {};
  const hexOf = (ref) => normalizeHex(resolvePaletteRef(ref, pal));
  const outs = Array.isArray(el && el.outlines) ? el.outlines : [];
  if (!outs.length) return true;
  const plate = normalizeHex(baseHex);
  const i = Math.max(0, Math.min(idx, outs.length - 1));
  const fill = hexOf((el && el.fillRef) || 'primary');
  const h2 = normalizeHex(hex);
  if (!h2) return false;
  const isInner = i === 0;
  const isOuter = i === outs.length - 1;
  if (isInner && fill && colorsConflict(fill, h2)) return false;   // invisible against its letter
  if (isOuter && plate && colorsConflict(plate, h2)) return false; // invisible against the plate
  const innerNb = isInner ? null : hexOf(outs[i - 1].colorRef);
  return !(innerNb && colorsConflict(innerNb, h2));                // fuses with its neighbour ring
}

/**
 * Does `hex` read as the text FILL on `baseHex`? Its neighbours are the plate behind it and the
 * innermost ring around it — the same two edges, one step in.
 */
export function fillHexReads(p, el, hex, baseHex) {
  const pal = (p && p.palette) || {};
  const hexOf = (ref) => normalizeHex(resolvePaletteRef(ref, pal));
  const h2 = normalizeHex(hex);
  const plate = normalizeHex(baseHex);
  if (!h2) return false;
  if (plate && colorsConflict(plate, h2)) return false;
  const outs = Array.isArray(el && el.outlines) ? el.outlines : [];
  const ring0 = outs.length ? hexOf(outs[0].colorRef) : null;
  return !(ring0 && colorsConflict(ring0, h2));
}

/**
 * @param {object} p design profile
 * @param {string} [baseHex] the plate the ink is painted on — pass `effectivePlateHex(state)`.
 *   Omitted, it falls back to `p.bodyColor`, which is right only for a dyeable product.
 */
export function assignOutlineColors(p, baseHex) {
  if (!p) return false;
  const pal = p.palette || {};
  const plate = normalizeHex(baseHex === undefined ? p.bodyColor : baseHex);
  /* ⛔ A BLACK PLATE BITES TOO (Ryan 2026-08-05, the Bauer's Elite logo).
     This read `!!plate && !isNearBlackHex(plate)` — "an undyed/black plate never peels" — so on a
     black plate the outer ring was never tested against the plate at all. With a palette whose
     `secondary` is #010101, the seeded outline (`colorRef: 'secondary'`) was therefore accepted on a
     #0B0C0F plate the instant the logo landed, and line ~"already fine, leave it alone" then kept it
     forever. A near-black ring on a near-black plate, invisible, and nothing in the pass could see it.
       Black is not a plate that cannot bite — it is the plate that bites HARDEST, because it is the
     one every default near-black ink disappears into. `colorsConflict` is the honest test and it
     already knew; this predicate was talking over it. A buyer's own pick is still safe: `userSet`
     rings are skipped before any of this. */
  const plateBites = !!plate;
  const hexOf = (ref) => normalizeHex(resolvePaletteRef(ref, pal));
  // Candidate refs, deduped by resolved hex.
  const refs = [];
  const seen = new Set();
  const offer = (ref) => { const h = hexOf(ref); if (h && !seen.has(h)) { seen.add(h); refs.push(ref); } };
  /* ⛔ SECONDARY FIRST, AND THAT IS NOT A PREFERENCE — IT IS AGREEMENT WITH THE SEED (Ryan
   * 2026-08-08, the second half of the Angry Panda report: *"no matter the order I click on them in,
   * the outline goes to and stays on purple"*).
   *
   * `primary` is the FILL role by convention — `autoStyle.fillRef` is 'primary', `createElement`
   * hardcodes `[{colorRef:'secondary'}]` as the born ring, and BOTH of the places that choose a ring
   * colour when a design is seeded already search `['secondary','tertiary','primary']`
   * (`seedOutlinesFrom`'s fallback and `applyAutoStyleToElement`'s born-default branch). This pass
   * searched `['primary', ...]`, so the seed and the re-derive disagreed about the same question:
   * the design opened with the secondary as its ring, and the first time anything re-derived it, it
   * flipped to the primary and stayed there.
   *   On a mark with no detected band structure — which is most badges, and Angry Panda is one — the
   * template preference above has nothing to prefer, so this ORDER is the whole answer. Aligning it
   * means "the ring the design opened with" is also the ring it returns to.
   *   ⚠ `correctIllegibleFill` keeps `['primary', ...]` and must: it is choosing a FILL, where
   * primary is the right first guess for exactly the same reason it is the wrong one here. */
  /* ⭐ ONE ORDERED LADDER FOR THE BUYER'S OWN COLOURS (Ryan 2026-08-10), built in two moves:
       1. ROLES then BENCH, and the bench ranked by `ringPreferenceOrder` rather than by population —
          the extractor hands its tones over biggest-area-first, which is right for a fill and
          backwards for an outline, since the tone a designer used to rim a mark is small by
          definition. (That call has its own neutral-plate guard; see its header.)
       2. `demoteNearNeutrals`, which drops a near-neutral BEHIND every real brand colour — the rule
          the line below already applies to house neutrals, now applied to a logo's own off-white
          too. `secondary` still leads whenever it is a genuine colour, so the seed agreement holds.
     Both are pure orderings over candidates that must still pass `ringFitTest` below. */
  const brandRefs = [
    ...['secondary', 'tertiary', 'primary'].filter((r) => pal[r]),
    ...ringPreferenceOrder(p.swatches, plate),
  ];
  demoteNearNeutrals(brandRefs, hexOf).forEach(offer);
  // House neutrals LAST — a real brand colour always outranks them when picking a ring colour — and
  // only the ones that READ as new here, so a near-black logo isn't offered black (it would merge
  // with the very colour it is supposed to contrast). Same rule as the swatch row.
  usableAccents(refs.map(hexOf)).forEach(offer);
  /* ⭐ RULE 4 (Ryan 2026-09-20): *"If there is black on the logo … then it's fine to add to the
   * weight number too. If there is no black on the logo due to none of those 3 things being true,
   * then do NOT add any black to the weight number."*
   *   WD 451 built his sentence at `syncFillContrastOutline`, the door his report named — and that
   * door RETURNS ON ITS FIRST LINE for any mark whose logo wears outlines, which mcdonalds does. So
   * on the plate he was looking at the rule could not run, and the black came from HERE: the line
   * directly above adds the house neutrals to the ladder, and the neutral that reads best against a
   * RED plate is black. This is the door that writes `effOutlines`, and it is the one place the ban
   * belongs — one rule, one home (§0b).
   * ⚠ `null` means the mark carries NO census — unmeasured is not black-free — and then this
   * changes nothing, because refusing black on a mark we cannot read is a guess.
   * ⚠ A ring the colour of the PLATE is a MOAT, not black paint: WD 451's own exemption, unchanged.
   * ⚠ IT DOES NOT TOUCH THE BAND MIRROR. A ring that echoes one of the mark's own bands takes the
   * BAND's decision and never reaches the ladder below — and a black band IS black on the logo, so
   * his sentence licenses it anyway.
   * ⭐ AND IT STAMPS ITS OWN FIRING. Four rules have now been recorded as working while not working
   * and Ryan found every one by eye (WD 456/458); the first cut of THIS one was gated at the wrong
   * door and the stamp is what said so, before the render rather than after it. */
  const blackBanned = markWearsBlack(p, plate) === false;
  const blackHere = (hx) => blackBanned && isBlackInk(hx) && !isPlateMoat(hx, plate);
  p.__rule4 = { banned: blackBanned, ring: null };
  if (!refs.length) return false;

  /* The colour this ring was DERIVED to wear — its slot in the logo-matched template. That is the
     answer to "what should this ring be, all else being equal", and it is a property of the design
     rather than of the click that got here. A ring the buyer ADDED has no slot and therefore no
     preference; it is chosen from the palette like any other derived colour. */
  const autoOuts = (p.autoStyle && Array.isArray(p.autoStyle.outlines)) ? p.autoStyle.outlines : [];
  const preferredFor = (o) => {
    if (!o || !Number.isInteger(o.autoIdx)) return null;
    const slot = autoOuts[o.autoIdx];
    return slot && slot.colorRef ? slot.colorRef : null;
  };

  /* ── THE MARK ANSWERS FIRST FOR ITS OWN BANDS (Ryan 2026-08-18, the E Logo teal plate) ─────────
   * A ring that ECHOES one of the logo's outline bands used to be judged by this pass's own ladder,
   * so on the teal plate the text's orange ring failed `readsOn` (orange vs teal is chroma-distinct
   * but luminance-equal, ~1.1:1) and fell to house BLACK — while the MARK's own orange band sat on
   * the same plate as drawn, kept by the band machinery. Two treatments for one colour on one
   * plate: *"wherever possible, it should mirror the outline color from the logo on the text."*
   *   So a band-echo ring now takes the BAND's per-plate decision, from the very functions the
   * raster path calls (`bandConflictView` + the detached-band keep) — repainted with the band,
   * dropped when the band peels, and KEPT when the mark keeps it, even where `readsOn` complains:
   * the mark has already answered this question on this plate. Ryan's orange-plate exception falls
   * out with no special case — there the orange band merges with the plate and the view-level
   * conflict peel already hides the text's orange ring the same way the mark goes subtle.
   *   A ring is a band echo when the colour its autoStyle SLOT was derived to wear is one of
   * `logoStructure.bands` (ΔE-matched, not string equality). Buyer-set rings never reach this
   * (skipped above), `added` rings carry no autoIdx, and a mark with no high-confidence bands
   * changes nothing. */
  const lsM = p.logoStructure;
  const mirrorBands = (lsM && lsM.confidence === 'high' && Array.isArray(lsM.bands) && lsM.bands.length)
    ? lsM.bands.map((h) => normalizeHex(h)) : null;
  let mirrorCtx = null; // built lazily: one bandConflictView per (profile, plate) pass
  const bandDecision = (hex) => {
    if (!mirrorBands || !plate || !hex) return null;
    const b = mirrorBands.findIndex((h) => h && (h === hex || colorsConflict(h, hex)));
    if (b < 0) return null;
    if (!mirrorCtx) {
      const logoEl = (p.elements || []).find((e) => e.type === 'logo' && e.logo);
      const ov = (logoEl && logoEl.logoBandColors && typeof logoEl.logoBandColors === 'object') ? logoEl.logoBandColors : {};
      const effBands = mirrorBands.map((c, i) => normalizeHex(ov[i]) || c);
      mirrorCtx = {
        effBands,
        view: bandConflictView(effBands, plate, bandViewOptions(p)),
      };
    }
    const { effBands, view } = mirrorCtx;
    if (view.repaint[b]) return { hex: normalizeHex(view.repaint[b]) };
    if (view.peel.includes(b)) return { drop: true };
    return { hex: effBands[b] };
  };

  let changed = false;
  /** Rings that merge with the fill they wrap and have nothing to move to — removed after the walk. */
  const doomed = [];
  for (const e of p.elements || []) {
    if (e.type !== 'arctext' && e.type !== 'weight') continue;
    const outs = Array.isArray(e.outlines) ? e.outlines : [];
    if (!outs.length) continue;
    const fill = hexOf(e.fillRef || 'primary');
    for (let i = 0; i < outs.length; i++) {
      const o = outs[i];
      if (!o || o.userSet === true) continue; // the buyer picked this one
      /* ⭐ THE GAP IS THE INNER NEIGHBOUR WHERE ONE RENDERS (Ryan 2026-08-23, Hanover's white
         plate: *"should get a gap and then a blue or red outline here too?"* — yes, and the only
         reason the ladder ever answered BLACK was that this test pretended the ring hugs the red
         fill. On a gap mark, `effectiveTextStyle` renders a plate-coloured gap between the fill
         and ring 0, so ring 0's real inner edge is the PLATE — navy reads ~7:1 against the white
         gap where it measured 1.10:1 against the red fill it never actually touches. With the
         edge modeled honestly, the school colour outranks the house neutral on its own. */
      const innerIsGap = i === 0 && plate && logoGapFromStructure(p.logoStructure);
      const { must, should } = ringFitTest({ outs, i, fill: innerIsGap ? plate : fill, plate, plateBites, hexOf });
      const cur = hexOf(o.colorRef);
      /* ⛔ THE RING'S COLOUR IS A FUNCTION OF THE DESIGN, NOT OF THE ROUTE TAKEN TO IT (Ryan
       * 2026-08-08, the Angry Panda logo — six extracted colours).
       *
       * *"when I change the text color from the first one to the 2nd one, the outline color goes from
       * orange to purple. Then when I change the text color to the 3rd through 6th ones, the outline
       * color stays as purple. If however… I bypass the 2nd text color and go straight to the 3rd
       * through 6th, the outline color will then stay on orange."*
       *
       * Two routes, one destination, two different designs — and he is right that it is a bug. The
       * cause was a line here that read `if (cur && must(cur) && should(cur)) continue;` — "already
       * fine, leave it alone", commented `(stability)`. It made this pass a REPAIR (only ever moving
       * a ring that had broken) instead of a DERIVATION, so the outline carried a memory of every
       * fill the buyer had passed through. Fill #2 is the one that clashes with the orange ring, so
       * only that route ever knocked it off orange, and nothing ever put it back.
       *
       * ⚠ IT IS NOT ENOUGH TO DELETE THAT LINE. Doing only that makes every ring re-pick the
       * highest-ranked palette colour that fits — deterministic, and it would quietly throw away the
       * whole point of the B1 auto-style: a text outline that ECHOES the logo's own bands, including
       * the literal hex a 4th band carries, which is not a palette role and would never win the
       * search. The preference below is what keeps "the logo's colour if it still works" while
       * making the answer independent of history: try the slot's own colour first, then the palette.
       * Fill #2 still pushes the ring to purple because orange genuinely clashes with it; fill #3
       * now takes it back to orange, by either route. */
      const prefer = preferredFor(o);
      // The band mirror (see the header block above): the mark's own decision for this band on
      // this plate outranks the ladder entirely — keep, follow its repaint, or die with its peel.
      const mirror = prefer ? bandDecision(hexOf(prefer)) : null;
      /* ⛔ …EXCEPT WHERE EVERY BAND DIES ON THIS PLATE (Ryan 2026-08-23, the Hanover white plate:
         bare red text where pass 8's reference wants a rescued outer ring). `bandsVisibleOnPlate`
         false means the mark itself presents bare there and gets a contextual rescue ring — so
         the text's band-echo must be the LADDER's rescue too, not a kept-invisible colour the
         view then hides. When ANY band still carries (E Logo's white on the orange plate), the
         mirror keeps as usual and the view's conflict peel handles a merging outermost — which
         is the approved drop-the-orange look, NOT a recolour. First cut keyed this per-ring
         ("kept colour merges with plate") and recoloured E Logo's orange to black; the per-MARK
         fact is the pass-7 semantic and the right key. */
      const bandsGoneHere = mirror ? bandsVisibleOnPlate(p, plate) === false : false;
      if (mirror && !bandsGoneHere) {
        // A mirrored ring that merges with its OWN FILL is not an outline — it is a bolder
        // letter wearing an outline chip (the doomed rule, one axis in). The mark never has this
        // problem (its band wraps its own fill by construction); the TEXT can, whenever the text
        // fill and a band share a colour. Die rather than lie.
        if (mirror.drop || (mirror.hex && fill && colorsConflict(fill, mirror.hex))) {
          if (o.added !== true && o.locked !== true) doomed.push({ e, o });
          continue;
        }
        if (mirror.hex && cur !== mirror.hex) {
          o.colorRef = hexOf(prefer) === mirror.hex ? prefer : mirror.hex;
          changed = true;
        }
        continue;
      }
      // bandsGoneHere (or no mirror) → the ladder below derives the rescue ring.
      const ordered = prefer ? [prefer, ...refs] : refs;
      const pick = ordered.find((r) => { const h = hexOf(r); return must(h) && should(h) && !blackHere(h); })
        || ordered.find((r) => { const h = hexOf(r); return must(h) && !blackHere(h); }); // relax the SHOULD before giving up
      if (blackBanned && p.__rule4 && !p.__rule4.ring) {
        const plain = ordered.find((r) => { const h = hexOf(r); return must(h) && should(h); })
          || ordered.find((r) => must(hexOf(r)));
        if (normalizeHex(hexOf(plain) || null) !== normalizeHex(hexOf(pick) || null)) {
          p.__rule4.ring = { from: hexOf(plain) || null, to: hexOf(pick) || null };
        }
      }
      if (!pick) {
        /* ⛔ "LEAVE IT AND LET THE PEEL SPEAK" IS TRUE ON A DYED PLATE AND FALSE ON A BLACK ONE
         * (Ryan 2026-08-12, RIGAR on the default plate: *"white text and white outline (bad)…
         * should be white text and no outline"*).
         *
         * RIGAR extracts as ONE tone, pure black. The born ring is chosen while the fill is still
         * that black — white clears both the plate and the fill, correctly — and then
         * `applyVanishingLogoDefaults` rescues the unreadable fill to WHITE, i.e. onto the very
         * colour the ring was just given. That pass knows it invalidates the ring and re-derives
         * (`if (fillMoved) assignOutlineColors(...)` above), so the seam worked exactly as designed
         * and arrived HERE — where nothing in a one-tone palette can pass `must`: white now merges
         * with the fill, black merges with the plate. The pass gave up and left white on white.
         *
         * ⚠ THE DEFERRAL HAD NO ONE TO DEFER TO. `effectiveTextStyle` returns early on a near-black
         * base (`if (!base || isNearBlackHex(base)) return …`), so the peel is not merely aimed at
         * the wrong axis here — it does not run. `dropAutoOutlinesWhenFillReads` bails on the same
         * test. Three correctors, all inert on the commonest plate there is, which is the same shape
         * as WD 277: the black plate is where the least correction happens.
         *
         * ⛔ AND THE PEEL COULD NEVER HAVE COVERED IT ANYWAY. It resolves a ring against the PLATE.
         * This ring reads perfectly against the plate; it is invisible against the LETTER it wraps.
         * No plate-axis device can see that, on any plate colour.
         *
         * So: a ring that merges with its own fill, when the palette has nothing to replace it with,
         * is deleted rather than kept. An outline that cannot be told apart from the letter is not an
         * outline — it is a bolder letter wearing an outline chip in the panel, and the chip is a
         * claim about the design that is not true.
         *
         * ⚠ NARROW ON PURPOSE — only the FILL axis. A ring that fails only because it merges with
         * the PLATE is left exactly as before: on a dyed plate the peel genuinely does resolve it,
         * and on a black plate an invisible ring is the seed's problem to avoid (WD 277), not this
         * pass's to delete. `userSet` rings never reach here, and an `added` ring is the buyer's own
         * and is exempt for the same reason.
         */
        if (o.added !== true && o.locked !== true && cur && fill && colorsConflict(fill, cur)) {
          doomed.push({ e, o });
        }
        continue;
      }
      if (hexOf(pick) !== cur) { o.colorRef = pick; changed = true; }
    }
  }
  /* Spliced AFTER the walk, never during it: `ringFitTest` reads `outs.length` to decide which ring
     is the outermost, so removing one mid-loop would re-answer that question for the rings behind it. */
  for (const { e, o } of doomed) {
    const outs = e.outlines;
    const i = outs.indexOf(o);
    if (i >= 0) { outs.splice(i, 1); changed = true; }
  }
  return changed;
}

export function resetToOriginal(state) {
  const p = state.designProfile;
  p.bodyColor = DEFAULT_BODY_COLOR; // black plate / weight
  const b = p.styleBaseline;
  if (b && b.palette) {
    p.palette.primary = b.palette.primary || p.palette.primary;
    p.palette.secondary = b.palette.secondary || p.palette.secondary;
    p.palette.tertiary = b.palette.tertiary || null;
    p.swatches = Array.isArray(b.swatches) ? b.swatches.slice() : [];
  } else {
    p.swatches = []; // no baseline captured → at least clear the added swatches
  }
  /* ⛔ AND THE EXTRACTOR PALETTE, OR THE RESET LEAVES A ✕ ON THE LOGO'S OWN COLOURS (Ryan 2026-08-08:
   * *"I imported a logo with 7 colours, edited some on the CD tab, then pressed Reset colors. They
   * reset accordingly — but the one I had edited adopted an 'x' in the top right corner. Click it and
   * the colour is removed and you can't get it back… only affects the colours that aren't the primary
   * or secondary."*)
   *
   * 🔑 THE ✕ MEANS "THE BUYER ADDED THIS", and it is derived, not stored: `orderedPool` in
   * design-panel.js asks `shownLogoTones(state.ui.palette)` whether a bench colour is one of the
   * logo's own. Its comment says that stays true for free *"since `recolorEverywhere` moves the bench
   * entry and the extractor's palette in the same pass"* — and it does, faithfully, on both.
   *   The premise simply never covered THIS function. Reset put `p.swatches` back from the baseline
   * and left `state.ui.palette` wearing the edited hexes, so the question became "is this ORIGINAL
   * colour one of the EDITED tones?" — no — and every reset tone was reclassified as buyer-added.
   * Roles are exempt because only `swatches` feed that list, which is exactly why Ryan saw it on the
   * third tone onward and never on primary or secondary.
   *
   * ⚠ INVERTING THE DESIGN'S OWN RECORD, not a second stored copy. `logoToneSwaps` is already kept as
   * ONE hop from the original artwork (`recolorEverywhere` composes rather than stacks, precisely so
   * every `from` is a colour the uploaded raster contains), so it is the exact map back. A new
   * `paletteOriginal` would be the third thing to keep in step that the ✕ comment warns against.
   *
   * ⚠ AND IT MUST RUN BEFORE `restoreColorDefaults`, which clears `logoToneSwaps` — after it, the
   * only record of what to undo is gone. */
  const toneSwaps = [];
  for (const e of p.elements || []) {
    if (e.type !== 'logo' || !Array.isArray(e.logoToneSwaps)) continue;
    for (const s of e.logoToneSwaps) if (s && s.from && s.to) toneSwaps.push(s);
  }
  const uiPal = state.ui && state.ui.palette;
  if (uiPal && toneSwaps.length) {
    const back = new Map();
    for (const s of toneSwaps) {
      const to = normalizeHex(s.to); const from = normalizeHex(s.from);
      if (to && from) back.set(to, from);
    }
    const put = (sw) => { if (!sw) return; const o = back.get(normalizeHex(sw.hex)); if (o) sw.hex = o; };
    if (Array.isArray(uiPal.all)) uiPal.all.forEach(put);
    put(uiPal.primary);
    put(uiPal.secondary);
    if (Array.isArray(uiPal.accents)) uiPal.accents.forEach(put);
  }
  restoreColorDefaults(p); // fill/outline COLOURS + logo paint back to originals; locks cleared
  // ⛔ AND THE PER-PLATE-COLOUR INK MEMORY (Ryan 2026-08-01). This is the one button that clears
  // explicit colour choices, and `savedInk` is nothing but a store of those — leaving it would make
  // Reset colors a lie the moment the buyer dyed the plate again, handing back the very ink they had
  // just reset. It has to die with everything else the button resets.
  p.savedInk = null;
  // NO `seedDefaultSizes` here any more (Ryan 2026-07-28). It existed only because the line above
  // used to clear `sizeScale`/`sizeUserSet`, leaving pieces at a raw 1.0 that no default ever chose —
  // so the re-seed was repairing damage this function no longer does. Now that sizes survive a colour
  // reset, re-seeding them would be the very thing the change removes: it would silently un-size
  // every piece the buyer had hand-sized.
}

/** Replace a colour EVERYWHERE it's used in the design (Ryan 2026-07-22) — so editing a
 *  logo-derived swatch (a micro on-brand hex tweak) updates every instance at once: the
 *  palette roles, the saved swatch pool, the plate dye, and every text fill/outline + logo
 *  fill/added-ring that carries that literal hex. Role-referenced fills follow automatically
 *  (their role hex changed). A logo outline BAND whose current colour matches also follows
 *  (via a per-band override — the source raster is untouched, so "Reset to original" restores
 *  everything). "Reset to original" restores the rest. */
export function replaceColorEverywhere(state, oldHex, newHex) {
  const from = normalizeHex(oldHex), to = normalizeHex(newHex);
  if (!from || !to || from === to) return false;
  const p = state.designProfile;
  const pal = p.palette || {};
  for (const k of ['primary', 'secondary', 'tertiary']) if (normalizeHex(pal[k]) === from) pal[k] = to;
  if (Array.isArray(p.swatches)) p.swatches = [...new Set(p.swatches.map((s) => (normalizeHex(s) === from ? to : normalizeHex(s))).filter(Boolean))];
  if (normalizeHex(p.bodyColor) === from) p.bodyColor = to;
  for (const e of p.elements || []) {
    if (e.type === 'arctext' || e.type === 'weight') {
      if (normalizeHex(e.fillRef) === from) e.fillRef = to; // literal-hex fills; role refs follow the role
      if (Array.isArray(e.outlines)) for (const o of e.outlines) if (normalizeHex(o.colorRef) === from) o.colorRef = to;
    } else if (e.type === 'logo') {
      // Editing the swatch is the buyer taking ownership of the colour, so a flatten that was merely
      // DERIVED stops being derived here — otherwise the next plate change would throw their edit away.
      if (normalizeHex(e.logoCenterColor) === from) { e.logoCenterColor = to; e.logoCenterAuto = false; }
      const aoArr = Array.isArray(e.logoAddOutline) ? e.logoAddOutline : (e.logoAddOutline ? [e.logoAddOutline] : []);
      for (const a of aoArr) if (a && normalizeHex(a.color) === from) a.color = to;
      // Logo outline bands (Ryan 2026-07-22): a band whose CURRENT color (its override, else its
      // detected color) is `from` adopts `to` — so editing ANY swatch of that color updates the
      // matching band(s), and recoloring a band updates every other swatch of that color.
      const bands = (p.logoStructure && Array.isArray(p.logoStructure.bands)) ? p.logoStructure.bands : [];
      for (let i = 0; i < bands.length; i++) {
        const cur = (e.logoBandColors && normalizeHex(e.logoBandColors[i])) || normalizeHex(bands[i]);
        if (cur === from) { if (!e.logoBandColors || typeof e.logoBandColors !== 'object') e.logoBandColors = {}; e.logoBandColors[i] = to; }
      }
    }
  }
  return true;
}

/** Set the ONE plate font (applies to all text). Returns success. */
export function setFont(state, id) {
  if (!FONTS[id]) return false;
  state.designProfile.font = id;
  return true;
}

/** Plate-wide caps mode (§2.7): true = ALL CAPS, false = "As typed". */
export function setAllCaps(state, on) {
  state.designProfile.allCaps = !!on;
  return state.designProfile.allCaps;
}

/** Rewrite every element fill/outline ref of `from` to `to` (keeps model + UI in sync). */
function remapPaletteRef(profile, from, to) {
  profile.elements.forEach((e) => {
    if (e.fillRef === from) e.fillRef = to;
    if (Array.isArray(e.outlines)) e.outlines.forEach((o) => { if (o.colorRef === from) o.colorRef = to; });
  });
}

/** Set one palette color (key: 'primary'|'secondary'|'tertiary'). null clears tertiary. */
export function setPaletteColor(state, key, input) {
  if (key === 'tertiary' && (input === null || input === '')) {
    state.designProfile.palette.tertiary = null;
    // Reclaim any refs pointing at the now-removed tertiary so the dropdowns,
    // the model, and the rendered plate can't disagree (WD-15).
    remapPaletteRef(state.designProfile, 'tertiary', 'secondary');
    return null;
  }
  const hex = normalizeHex(input);
  if (hex) state.designProfile.palette[key] = hex;
  return hex;
}

// ── Color library / bench (Ryan 2026-07-20) ──────────────────────────────────
// A shared pool of saved colors ("your colors") that lives alongside the 3 active
// roles (1st=primary, 2nd=secondary, 3rd=tertiary). Bench colors are parked for quick
// access and become "used" only when swapped into a role. The SAME pool feeds the
// plate-color quick options, so a custom color picked anywhere is reusable everywhere.

const ROLE_KEYS = ['primary', 'secondary', 'tertiary'];
/** Hexes currently assigned to the 3 roles (normalized, no blanks). */
function roleHexes(profile) {
  return ROLE_KEYS.map((k) => normalizeHex(profile.palette[k])).filter(Boolean);
}

/**
 * How many more colours the shared pool can take (Ryan 2026-07-25). The pool the buyer sees is the
 * palette ROLES plus the bench, so the cap has to count both — capping the bench alone let a 3-role
 * logo reach 11 chips.
 */
export function swatchSlotsLeft(profile) {
  const p = profile || {};
  const bench = Array.isArray(p.swatches) ? p.swatches.length : 0;
  // The house neutrals occupy row slots too (Ryan 2026-07-25 audit). The cap bounds how many CHIPS a
  // row can show, so leaving them out let it render cap+2 — the exact overflow the cap exists to stop.
  const neutrals = usableAccents([...roleHexes(p), ...(Array.isArray(p.swatches) ? p.swatches : [])]).length;
  return Math.max(0, SWATCH_CAP - roleHexes(p).length - bench - neutrals);
}

/**
 * Add a color to the bench. Dedupes against the roles + existing bench. Returns true if it landed.
 *
 * HARD STOP at the cap, not eviction (Ryan 2026-07-25). This used to `shift()` the oldest entry off
 * the front once full, so holding down "+" silently deleted the colours added earliest — including
 * ones the design was actively wearing, which then had no chip to light up. Refusing the new colour
 * is the honest failure: nothing the buyer already chose disappears behind their back, and the "+"
 * buttons grey out at the limit so the refusal is visible rather than mysterious.
 */
export function addSwatch(state, hex, { ownTone = false, userPicked = false } = {}) {
  const H = normalizeHex(hex);
  if (!H) return false;
  const p = state.designProfile;
  if (!Array.isArray(p.swatches)) p.swatches = [];
  // House neutrals are ALREADY on the row permanently (DEFAULT_ACCENTS), so benching one duplicates a
  // chip and burns a cap slot for nothing (Ryan 2026-07-25 audit). This bites hardest via the AUTO
  // contrast ring, which calls addSwatch with whatever it derived — usually white or black — quietly
  // putting a colour the buyer never chose onto their saved bench, the exact thing entry 65 removed.
  // A near-neutral the buyer genuinely picked (#FAFAFA) still benches, and usableAccents then drops
  // the house white so the row still shows one.
  //   `ownTone` is the exception that proves it: a house neutral the buyer's LOGO ACTUALLY CONTAINS
  // is a brand colour, not padding. The guard above exists to stop DERIVED whites and blacks — the
  // auto contrast ring's pick — landing on a saved bench; a tone read out of the artwork is the
  // opposite of derived. Without this, the McDonald's wordmark had nowhere at all to live: white is
  // declined here, and `orderedPool` withholds the house neutrals entirely once a logo has two or
  // more tones of its own, so a three-tone mark whose third tone is white showed it in no row.
  /* ⛔ …AND SO IS A COLOUR THE BUYER PICKED IN THE "+" DIALOG (Ryan 2026-08-12: *"I click 'Add' to
     add a custom plate color of white… the plate changes appropriately… but in the plate color
     swatches, white isn't showing up as a 3rd option when it should be listed there."*).
       `userPicked` is the same exception as `ownTone`, for the same reason. This guard exists to stop
     DERIVED whites and blacks — the auto contrast ring's pick — landing on a saved bench. A hex the
     buyer chose by hand in the Add-color picker is the opposite of derived, and refusing it broke the
     one promise that row makes: every "+" caller discards this return value, so the colour was
     applied, the toast said "added to your colors", and nothing was added.
       White was unreachable by every other route too, which is why it presented as "no chip at all":
     `orderedPool` withholds the house neutrals the moment the logo has two roles of its own (Cherry
     Valley's mark is #C38F82 + black), and the `homeless` rescue asks `inUseHexes(false)` — elements
     only — because the plate has its own row. Three correct-in-isolation rules, one unreachable
     colour.
       The near-neutral case in the note above (#FAFAFA benches, `usableAccents` then drops the house
     white so the row still shows one) is the model this restores: a deliberately chosen neutral is a
     brand colour. Only the three `openAddPicker` confirms pass this — every derived caller stays
     guarded. */
  if (!ownTone && !userPicked && DEFAULT_ACCENTS.includes(H)) return false;
  if (roleHexes(p).includes(H) || p.swatches.some((s) => normalizeHex(s) === H)) return false;
  if (swatchSlotsLeft(p) <= 0) return false;
  p.swatches.push(H);
  return true;
}

/**
 * Carry the buyer's tone swaps onto the logo's OUTLINE BANDS, by deriving a per-band override for
 * every band whose detected colour they recoloured.
 *
 * Why the bands need their own mechanism: `remapBandsFromImageData` deliberately does not apply
 * raster swaps to band pixels — bands are labelled by a flood fill and repainted from
 * `logoBandColors`, which is what lets a band be peeled, recoloured, or auto-dropped against the
 * plate independently of the ink inside the mark.
 *
 * Why this is DERIVED rather than written once at edit time (Ryan 2026-07-26): picking the black
 * plate "resets the logos back to their original color form prior to adjusting the colors on the Add
 * Logo tab". The plate is the top of the colour cascade, so `recascadeFromPlate` clears
 * `logoBandColors` / `logoBandUserSet` wholesale — correct for a colour DERIVED against the plate,
 * wrong for the buyer having repainted their own artwork, which a plate pick has no more business
 * undoing than it does swapping the logo file. Keeping the swap list as the record and re-deriving
 * the overlay from it means the repaint survives anything that clears the bands, without that code
 * having to know this feature exists.
 *
 * Skips a band currently overridden to something ELSE — that is a live hand-pick on the CD tab's
 * Logo outline rows, and it outranks a tone rule. After a plate re-cascade there are no overrides
 * left to skip, which is exactly when this needs to repopulate all of them.
 *
 * @returns {boolean} whether any band override changed
 */
function applyToneSwapsToBands(p) {
  const bands = (p.logoStructure && Array.isArray(p.logoStructure.bands)) ? p.logoStructure.bands : [];
  if (!bands.length) return false;
  let changed = false;
  for (const e of p.elements || []) {
    if (e.type !== 'logo') continue;
    const swaps = Array.isArray(e.logoToneSwaps) ? e.logoToneSwaps : [];
    if (!swaps.length) continue;
    bands.forEach((bandHex, i) => {
      const detected = normalizeHex(bandHex);
      const s = swaps.find((x) => normalizeHex(x.from) === detected);
      const to = s && normalizeHex(s.to);
      if (!to) return;
      const cur = normalizeHex(e.logoBandColors && e.logoBandColors[i]);
      if (cur === to) return;
      if (cur && cur !== detected) return; // a live hand-pick on this band wins
      if (!e.logoBandColors || typeof e.logoBandColors !== 'object') e.logoBandColors = {};
      e.logoBandColors[i] = to;
      const us = new Set(Array.isArray(e.logoBandUserSet) ? e.logoBandUserSet : []);
      us.add(i | 0);
      e.logoBandUserSet = [...us].sort((a, b) => a - b);
      changed = true;
    });
  }
  return changed;
}

/**
 * Change one COLOUR of the design into another, everywhere it appears (Ryan 2026-07-26 — the
 * extracted swatches on the Add Logo tab are editable now, "and have it translate over to the CD
 * tab color swatches too").
 *
 * This is deliberately the OPPOSITE of `openSwatchEditPicker`'s local edit on the Customize Design
 * tab. There, the surfaces have been chosen one at a time and darkening the text's green must leave
 * the plate's green alone. On the Add Logo tab there are no surfaces yet — the buyer is looking at
 * "YOUR COLORS", the palette the extractor read off their mark, and what they mean by adjusting one
 * is "that isn't quite our blue". Our blue is our blue wherever it landed, so this follows the hex
 * through every place a colour can live:
 *
 *   • the palette roles (primary / secondary / tertiary)
 *   • the saved bench, in place, so the chip keeps its position on the row
 *   • the plate body, if the plate happens to be dyed that colour
 *   • every element's fill and every outline ring holding the literal hex
 *   • the logo's flattened centre, its per-band overrides and its added ring
 *   • `ui.palette.all`, the extraction result the chips and the "Original" tone-banded chip on the
 *     CD tab are BOTH drawn from — miss this and the two screens disagree about the same logo
 *
 * Refs that name a ROLE ('primary') rather than a hex need no work: the role now holds the new hex,
 * so they follow automatically. That is the whole reason roles exist.
 *
 * @returns {boolean} whether anything actually changed
 */
export function recolorEverywhere(state, from, to) {
  const F = normalizeHex(from);
  const T = normalizeHex(to);
  if (!F || !T || F === T) return false;
  const p = state.designProfile;
  const isF = (v) => normalizeHex(v) === F;
  let hit = false;
  let sawLogo = false;
  const swap = (v) => { if (!isF(v)) return v; hit = true; return T; };

  for (const k of ROLE_KEYS) if (isF(p.palette[k])) { p.palette[k] = T; hit = true; }
  if (isF(p.bodyColor)) { p.bodyColor = T; hit = true; }
  if (Array.isArray(p.swatches)) {
    // Map then dedupe, because recolouring one bench chip onto another's hex would otherwise leave
    // the row showing the same colour twice.
    const mapped = p.swatches.map((s) => normalizeHex(swap(s))).filter(Boolean);
    p.swatches = mapped.filter((s, i) => mapped.indexOf(s) === i);
  }
  for (const e of p.elements || []) {
    e.fillRef = swap(e.fillRef);
    if (Array.isArray(e.outlines)) e.outlines.forEach((o) => { o.colorRef = swap(o.colorRef); });
    if (e.logoCenterColor) e.logoCenterColor = swap(e.logoCenterColor);
    if (e.logoBandColors && typeof e.logoBandColors === 'object') {
      for (const k of Object.keys(e.logoBandColors)) e.logoBandColors[k] = swap(e.logoBandColors[k]);
    }
    if (Array.isArray(e.logoAddOutline)) e.logoAddOutline.forEach((o) => { o.color = swap(o.color); });
    if (e.type !== 'logo') continue;
    // ── repaint the MARK ITSELF (Ryan 2026-07-26: "the logo itself doesn't change. Should it?" —
    // yes) ─────────────────────────────────────────────────────────────────────────────────────
    // Recorded ONCE, here, as a swap against the original artwork. The band overrides that carry it
    // on a banded logo are DERIVED from this list by `applyToneSwapsToBands` below — see there for
    // why they can't be the record themselves.
    if (!Array.isArray(e.logoToneSwaps)) e.logoToneSwaps = [];
    // COMPOSE, never stack — see `composeToneSwap`, which is also what the pre-build path below uses.
    if (composeToneSwap(e.logoToneSwaps, F, T)) hit = true;
    sawLogo = true;
  }
  /* ⛔ …AND WHEN THERE IS NO LOGO ELEMENT YET, WHICH IS THE WHOLE OF THE ADD-LOGO TAB (Ryan
   * 2026-08-12, Ridge View): *"I changed one of the colors on the Add Logo tab… the swatch updated
   * correctly – but the logo image itself under 'YOUR LOGO' didn't change… When the plate then
   * revealed the design, the new color was used everywhere except on the logo itself."*
   *
   * `setLogo` was deliberately moved to the build beat so the plate stays black until "Design My
   * Weights" (Ryan 2026-08-07), which means `p.elements` is EMPTY for the entire tab — the loop
   * above has nothing to write to and the mark's repaint is simply dropped. Every other surface
   * updated because they read the palette roles and `state.ui.palette`, which this function does
   * reach. Doing the same edit AFTER building worked, and that difference is the tell.
   *
   * `savedLogoToneSwaps` is already the profile-level home for exactly this — a mark's paint with
   * nowhere to live — added so a trip through a text-only layout would not lose it. A logo that does
   * not exist yet is the same problem one beat earlier, so it gets the same answer, and
   * `inheritSavedToneSwaps` hands it over the moment the element is created.
   */
  if (!sawLogo) {
    if (!Array.isArray(p.savedLogoToneSwaps)) p.savedLogoToneSwaps = [];
    if (composeToneSwap(p.savedLogoToneSwaps, F, T)) hit = true;
  }
  if (applyToneSwapsToBands(p)) hit = true;
  /* ── AND THE PER-PLATE INK MEMORY, WHICH IS A COLOUR STORE LIKE ANY OTHER (2026-08-15) ──────────
   * `savedInk` records what the buyer's ink looked like on each plate colour so `restoreInk` can put
   * it back when they return to that plate. It was not on this walk, so a global recolour left the
   * OLD hex banked: edit a colour, dye back to a plate you had visited, and `restoreInk` faithfully
   * repaints the colour you just got rid of. Caught by `tools/verify-edit-any-color.mjs`, which
   * asserts "no trace anywhere" rather than checking a list of fields somebody remembered.
   *
   * ⚠ A GENERIC WALK, DELIBERATELY. The envelope nests colours at `text.fill`, `text.outlines[]`,
   * `logo.center`, `logo.bands{}` and `logo.adds[]`, and `captureInk` has grown a field on it three
   * times this month. Mirroring that shape here would be a second implementation of it, drifting the
   * next time one is added — the failure this file keeps paying for. Everything else in the envelope
   * is a boolean or an index, so a string-equality swap cannot touch anything but a colour. */
  const swapDeep = (node) => {
    if (!node || typeof node !== 'object') return;
    for (const k of Object.keys(node)) {
      const v = node[k];
      if (typeof v === 'string') { if (isF(v)) { node[k] = T; hit = true; } }
      else swapDeep(v);
    }
  };
  swapDeep(p.savedInk);
  // The extraction result is UI state, not design state, but the chips on both tabs read it — so it
  // has to move with the design or the swatch you edited springs back to the old hex on a rebuild.
  const uiPal = state.ui.palette || null;
  const all = uiPal && Array.isArray(uiPal.all) ? uiPal.all : null;
  if (all) all.forEach((c) => { if (c && isF(c.hex)) { c.hex = T; hit = true; } });
  // ⚠ AND THE EXTRACTOR'S OWN PICKS, which sit BESIDE `all` rather than inside it (Ryan 2026-07-28:
  // "the first swatch, the 3-in-1, still shows the original 3 colours whereas every other swatch has
  // updated"). `primary` / `secondary` / `accents` were added to this object hours ago so the CD
  // swatch could stop disagreeing with the Add Logo chips — and this swap, written when `all` was the
  // only field here, was never widened to cover them. So the tones list went stale the moment anyone
  // recoloured, and the one chip built from it was the one chip still showing the uploaded artwork.
  //   Same reasoning as the line above, and it is worth restating: this is UI state, not design
  // state, but both tabs' chips read it — so it has to move with the design or an edited swatch
  // springs back to its old hex on the next rebuild.
  if (uiPal) {
    for (const sw of [uiPal.primary, uiPal.secondary, ...(Array.isArray(uiPal.accents) ? uiPal.accents : [])]) {
      if (sw && isF(sw.hex)) { sw.hex = T; hit = true; }
    }
  }
  // ── AND THE NEW COLOUR GETS A HOME (Ryan 2026-07-28) ─────────────────────────────────────────
  // "every other category of swatches — plate, text, text outlines, logo outlines — has NOT included
  // the white swatch (which I changed to burgundy), but they have picked up the yellow and the red."
  //
  // Because those rows are roles + the bench + house neutrals, and an extracted ACCENT is none of the
  // three. `applyPalette` seats only primary and secondary as roles; a third tone is shown as a chip
  // on Add Logo and otherwise lives nowhere. While it stayed white that was invisible — white is a
  // house neutral, so the rows offered one anyway — and recolouring it to burgundy is exactly what
  // took away the thing that had been standing in for it.
  //
  // Recolouring a tone is a deliberate act of ownership, so the result should be reachable. Every
  // guard this needs is already inside `addSwatch`: it declines house neutrals, declines anything
  // already a role or on the bench (so red→blue adds nothing, blue being a role by then), and
  // respects the cap. Which is why this is a call and not a condition.
  if (hit) addSwatch(state, T);
  return hit;
}

/** Remove a color from the bench. Returns true if it was there. */
export function removeSwatch(state, hex) {
  const H = normalizeHex(hex);
  const p = state.designProfile;
  if (!H || !Array.isArray(p.swatches)) return false;
  const i = p.swatches.findIndex((s) => normalizeHex(s) === H);
  if (i < 0) return false;
  p.swatches.splice(i, 1);
  return true;
}

/** Put `hex` into a role slot (1st/2nd/3rd). It leaves the bench (now in use), and the
 *  color it displaces is pushed BACK to the bench so nothing is lost — a clean swap when
 *  the source is a bench color, a preserve-the-old when it's a brand-new pick. */
export function assignColorToRole(state, hex, role) {
  const H = normalizeHex(hex);
  if (!H || !ROLE_KEYS.includes(role)) return false;
  const p = state.designProfile;
  const old = normalizeHex(p.palette[role]);
  removeSwatch(state, H);              // the incoming color is now a role, not bench
  setPaletteColor(state, role, H);
  if (old && old !== H) addSwatch(state, old); // preserve the displaced color on the bench
  return true;
}

// ── Landing-gate product selection (2026-07-14) ──────────────────────────────

/** True if the buyer has picked at least one product on the landing gate. */
export function hasAnySelection(state) {
  return Array.isArray(state.ui.selectedProducts) && state.ui.selectedProducts.length > 0;
}

/** True if this {category, kind} is selected AT ALL — any instance. What a tick-box asks. */
export function isProductSelected(state, category, kind) {
  return state.ui.selectedProducts.some((p) => p.category === category && p.kind === kind);
}

/**
 * Toggle a {category, kind} pick on/off. Returns the new selected state (bool).
 *
 * ⚠ TOGGLING OFF DROPS EVERY INSTANCE, not just the first one found. The control this backs is a
 * tick-box reading "I want this product", and un-ticking it is the buyer withdrawing the product —
 * leaving a second configuration behind would put a plate in the quote that nothing on screen
 * still says they asked for. Removing ONE instance is a different gesture with its own function
 * (`removeProductInstance`), reached from the instance's own control.
 */
export function toggleProductSelection(state, category, kind) {
  const list = state.ui.selectedProducts;
  const before = list.length;
  for (let i = list.length - 1; i >= 0; i -= 1) {
    if (list[i].category === category && list[i].kind === kind) list.splice(i, 1);
  }
  if (list.length < before) return false;
  list.push({ category, kind, instance: 1 });
  return true;
}

/* ── MORE THAN ONE CONFIGURATION OF ONE PRODUCT (Ryan 2026-08-09) ────────────────────────────────
 * White 45s and green 25s: the same competition bumper, picked twice. An instance owns its plate
 * colour and its slice of the ladder; the DESIGN — logo, layout, fonts, lettering — stays shared,
 * which is what Ryan chose and what the whole tool has always said about itself.
 *
 * ⚠ `instance` IS AN IDENTITY, NOT A POSITION. It is allocated as "one past the highest in use" and
 * never renumbered, because a quantity is keyed `${productKey}|${weight}` on the quote form and a
 * per-instance plate colour will be keyed by the same key: renumbering #3 down to #2 after #2 is
 * deleted would silently hand one configuration another's colour and quantities. Gaps in the
 * numbering are correct and invisible — the switcher orders by them, it does not print them.
 */

/** Every instance NUMBER of this {category, kind} currently picked, ascending. */
export function productInstances(state, category, kind) {
  return state.ui.selectedProducts
    .filter((p) => p.category === category && p.kind === kind)
    .map((p) => instanceOf(p))
    .sort((a, b) => a - b);
}

/**
 * One pick, as it rides an envelope. `weight`/`wAt` are the denomination the circles claimed and
 * when — both OMITTED when absent, so a save from before 2026-08-10 round-trips byte-identical and
 * a design that never touched the circles adds nothing to its envelope. Same additive treatment
 * `instance` got, and for the same reason: there is nothing here to migrate.
 */
function serializePick(p) {
  const out = { category: p.category, kind: p.kind, instance: instanceOf(p) };
  if (Number(p.weight) > 0) out.weight = Number(p.weight);
  if (Number(p.wAt) > 0) out.wAt = Number(p.wAt);
  return out;
}

/** A pick's instance number, defaulting to 1 — an older save has no `instance` field. */
function instanceOf(pick) {
  const n = Math.trunc(Number(pick && pick.instance));
  return Number.isFinite(n) && n > 1 ? n : 1;
}

/**
 * Add ANOTHER configuration of an already-picked product.
 * @returns {number|null} the new instance number, or null if this product cannot be picked twice
 *   (everything but the competition bumpers) or has not been picked once yet.
 */
export function addProductInstance(state, category, kind) {
  const key = productKey(category, kind);
  if (!productSupportsMultiConfig(key)) return null;
  const used = productInstances(state, category, kind);
  if (!used.length) return null;            // "another" only means something after a first
  /* ⚠ CAPPED ON THE COUNT IN USE, NOT ON THE HIGHEST NUMBER. Instance numbers leave gaps by design
     (they are identities, never renumbered), so a buyer who added three, deleted one and added
     another would be refused at #6 on a five-rung ladder while holding only three configurations.
     What the cap protects is the partition — one configuration per rung — and that is a COUNT. */
  if (used.length >= maxConfigsFor(key)) return null;
  const next = used[used.length - 1] + 1;   // one past the highest — see the identity note above
  const pick = { category, kind, instance: next };
  /* ⭐ THE FIRST UNCLAIMED RUNG, not `configLadder[next - 1]`. They are the same number until the
     buyer moves one — and once the circles let them, the index is a collision waiting to happen:
     swap the pair to 25 and 10, press `+`, and instance #3's derived rung is 25, which is taken.
     Asking what is FREE cannot collide, and reproduces the old number exactly while nothing has
     been moved (see claimDenomination). */
  const heldNow = new Set(claimedDenominations(state, category, kind).map((h) => h.weight));
  const free = (configLadder(key) || []).find((w) => !heldNow.has(w));
  if (free != null) pick.weight = free;
  state.ui.selectedProducts.push(pick);
  return next;
}

/** True if another configuration of this product could be added right now. What a `+` asks. */
export function canAddProductInstance(state, category, kind) {
  const used = productInstances(state, category, kind);
  return used.length > 0 && used.length < maxConfigsFor(productKey(category, kind));
}

/**
 * Drop ONE configuration. Returns true if something was removed.
 *
 * Removing the LAST remaining instance removes the product, which is right: there is no such thing
 * as a picked product with no configurations, and the tick-box that reads "I want this" would
 * otherwise stay ticked over an empty set.
 */
export function removeProductInstance(state, category, kind, instance = 1) {
  const list = state.ui.selectedProducts;
  const want = Math.max(1, Math.trunc(Number(instance)) || 1);
  const i = list.findIndex((p) => p.category === category && p.kind === kind && instanceOf(p) === want);
  if (i < 0) return false;
  list.splice(i, 1);
  return true;
}

/* ── WHICH DENOMINATION EACH CONFIGURATION CLAIMS (Ryan 2026-08-10) ──────────────────────────────
 * The gate's denomination circles. Until today the rung a configuration sat on was DERIVED from its
 * instance number — `defaultWeightFor(key#2)` is `configLadder[1]`, i.e. 35 — and products.js calls
 * that "a courtesy, not the enforcement". The circles make it a choice, so it has to be stored.
 *
 * ⚠ STORED ON THE PICK, NOT AS THE INSTANCE NUMBER. It is tempting to say "selecting the 25 means
 * becoming instance 3", because `configLadder` maps the two one-to-one. It would be wrong: an
 * instance is an IDENTITY that owns a plate colour and a column of quantities (see the block above),
 * and renumbering to change a denomination would hand one configuration another's colour. The rung
 * is a property OF the configuration, not its name.
 * ⚠ Absent `weight` still means "the derived rung", so every save written before today restores
 * exactly as it did — same additive, version-neutral treatment `instance` itself got.
 */

/** Monotonic, so "which slot has stood longest" is a comparison rather than a timestamp. */
let denomTick = 0;

function findPick(state, category, kind, instance) {
  return (state.ui.selectedProducts || [])
    .find((p) => p.category === category && p.kind === kind && instanceOf(p) === instance) || null;
}

/**
 * WHERE A SLOT SITS IN THE DISPLACEMENT QUEUE. Smallest goes first.
 *
 * ⭐ UNTAPPED SLOTS ARE ORDERED NEWEST-ADDED FIRST, which is the whole subtlety and is Ryan's own
 * reasoning (2026-08-10): *"most people would prefer to deviate from 35 and go to 25 with 45, but we
 * can't assume… so most recent goes first on first click, then we go to longest standing."* A
 * configuration the buyer conjured with `+` is the one they have thought about least, so it is the
 * first a deliberate tap should redirect; a rung they chose by hand outranks every seeded one.
 *   Falls out of the arithmetic rather than needing a branch: seeded slots score `-instance` (so #2
 * at -2 precedes #1 at -1) and every negative precedes every tap, which scores a positive tick.
 */
function denomOrder(pick) {
  const at = Number(pick && pick.wAt);
  return Number.isFinite(at) && at > 0 ? at : -instanceOf(pick);
}

/** The denomination one configuration currently claims, chosen or derived. */
export function instanceWeight(state, category, kind, instance = 1) {
  const pick = findPick(state, category, kind, instance);
  const w = Number(pick && pick.weight);
  if (Number.isFinite(w) && w > 0) return w;
  return defaultWeightFor(productKey(category, kind, instance));
}

/** Every rung this product's configurations hold right now, in displacement order (first out first). */
export function claimedDenominations(state, category, kind) {
  return (state.ui.selectedProducts || [])
    .filter((p) => p.category === category && p.kind === kind)
    .map((p) => ({
      instance: instanceOf(p),
      weight: instanceWeight(state, category, kind, instanceOf(p)),
      order: denomOrder(p),
    }))
    .sort((a, b) => a.order - b.order);
}

/**
 * Point a configuration at `weight`, displacing whichever slot has stood longest.
 *
 * ⚠ A RUNG ALREADY HELD IS A NO-OP, deliberately. Tapping a lit circle could plausibly mean "drop
 * this one", but that is what `−` is for, and making a lit circle destructive means a mis-tap can
 * silently delete a configuration the buyer has already dyed. Inert is the recoverable reading.
 * @returns {boolean} true if something moved.
 */
export function claimDenomination(state, category, kind, weight) {
  const w = Number(weight);
  if (!Number.isFinite(w) || w <= 0) return false;
  const held = claimedDenominations(state, category, kind);
  if (!held.length || held.some((h) => h.weight === w)) return false;
  const pick = findPick(state, category, kind, held[0].instance);
  if (!pick) return false;
  pick.weight = w;
  pick.wAt = ++denomTick;
  return true;
}

/** The pick list as product KEYS, in the list's own order. For callers that think in keys. */
export function selectedProductKeys(state) {
  return state.ui.selectedProducts.map((p) => productKey(p.category, p.kind, instanceOf(p)));
}

// ── Landing-gate CATEGORY selection (2026-07-14, card-select redesign) ──────
// A simpler, earlier step than the {category,kind} picks above: the buyer just
// picks WHICH category cards apply (any combo of the 4), then "Next" commits
// them. Kept as its own state (not reusing selectedProducts) since kind-level
// picking may or may not still happen in a later step on top of this.

/** True if the category card is currently selected. */
export function isCategorySelected(state, category) {
  return state.ui.selectedCategories.includes(category);
}

/** Toggle a category card on/off. Returns the new selected state (bool). */
export function toggleCategorySelected(state, category) {
  const list = state.ui.selectedCategories;
  const i = list.indexOf(category);
  if (i >= 0) { list.splice(i, 1); return false; }
  list.push(category);
  return true;
}

/** True if at least one category card is selected. */
export function hasAnyCategorySelected(state) {
  return state.ui.selectedCategories.length > 0;
}

// ── Plate body color (B6 §4) ─────────────────────────────────────────────────

/** True if a hex is (near-)black — the "Black" plate choice, not a real dye. */
export function isBlackBody(input) {
  const hex = normalizeHex(input) || normalizeHex(DEFAULT_BODY_COLOR);
  const m = /^#(..)(..)(..)$/.exec(hex);
  if (!m) return true;
  const [r, g, b] = [1, 2, 3].map((i) => parseInt(m[i], 16));
  return r <= 24 && g <= 24 && b <= 24; // ≤ ~#181818 reads as black rubber
}

/** Palette role ('primary'|'secondary'|'tertiary') for a hex, else the literal hex. */
function refForHex(pal, hex) {
  const h = normalizeHex(hex);
  if (h && normalizeHex(pal.primary) === h) return 'primary';
  if (h && normalizeHex(pal.secondary) === h) return 'secondary';
  if (h && pal.tertiary && normalizeHex(pal.tertiary) === h) return 'tertiary';
  return hex;
}

/** Set the plate rubber (body) color. Returns the stored hex. */
export function setBodyColor(state, input) {
  const hex = normalizeHex(input);
  if (hex) state.designProfile.bodyColor = hex;
  return state.designProfile.bodyColor;
}

// NOTE: plate-dye text/number contrast is resolved at RENDER time as a reversible,
// non-cumulative view (design-profile.effectiveTextStyle), NOT by mutating the
// design here — so switching plate colors hides/reveals rings without ever eroding
// the stored stack. Dyeing the plate is therefore just setBodyColor + a re-render.

/* ── PER-CONFIGURATION PLATE COLOUR (Ryan 2026-08-09) ────────────────────────────────────────────
 * White 45s and green 25s. `bodyColor` is ONE field, so a design that has been picked twice needs
 * a memory beside it — the same shape as `savedInk` above, one axis over. See the `savedBodyColor`
 * block in design-profile.js for why this is the one memory keyed by the INSTANCE.
 *
 * ⛔ THE RESTORE MUST RUN BEFORE `restoreInk`, and getting that order wrong is silent. `inkContextKey`
 * resolves the ink bucket from `state.designProfile.bodyColor`, so restoring the ink first looks up
 * the bucket for the plate colour the buyer is LEAVING and paints the outgoing configuration's ink
 * onto the incoming one — which then gets stamped back under the wrong key on the next hop, so the
 * corruption compounds instead of showing up once. `adoptProduct` orders them; both of its branches
 * need it.
 */

/* ── THE DEFAULTS, AND WHY THEY CANNOT BE ASSIGNED AT PICK TIME (Ryan 2026-08-09) ───────────────
 * The colours come off the buyer's LOGO, and the logo lands on tab 2 — after the picks are made on
 * the gate. So there is no palette at the moment a configuration is created, and "default on page
 * load" has to mean "the moment the palette first exists". That is this function, called from the
 * palette apply and again whenever an instance is added.
 *
 * ⛔ IT ONLY EVER WRITES WHERE THE BUYER HAS NOT. `bodyColorUserSet` is the latch, and it exists
 * because "black" is ambiguous in a way that broke the obvious implementation: an instance that has
 * been auto-stamped black by a hop through `rememberBodyColor` is indistinguishable, by colour
 * alone, from one the buyer deliberately dyed black. Skipping "anything with a saved colour" would
 * therefore freeze the very first configuration on the stock plate — the case Ryan opens with.
 *
 * ⚠ THE POSITION IS PER PRODUCT, not across all comp bumpers. The cap is per product ("up to 5
 * colors, one for each weight") and so is the ladder they partition, so a second comp bumper KIND
 * starts its own sequence rather than continuing the first one's.
 *
 * ⚠ ONE INSTANCE STAYS BLACK. Ryan: *"if they only choose 1 comp bumper from the onset, it'll
 * default to black."* The sequence is for a SET; a single plate has nothing to contrast with.
 *
 * ⭐ AND A NEW INSTANCE TAKES THE FIRST COLOUR THE SET IS NOT ALREADY WEARING, falling back to its
 * positional slot when they are all in use. That one rule serves both of Ryan's examples: a set of
 * {red, blue} gaining a third finds nothing unused and falls back to position 3 — red, exactly as
 * he described — while a lone BLACK first plate gaining a second finds C1 unused and takes it,
 * rather than being handed the black that position 2 holds on a one-tone logo.
 */
export function applyDefaultPlateColors(state, { seed = false } = {}) {
  const p = state.designProfile;
  if (!p) return false;
  // The keyed-out school colour joins the mix AFTER the mark's own tones (Ryan 2026-08-18: "it
  // should be able to include the blue in the regular mix just like it would if it were a part of
  // the main logo"). After, not first: slot ordering among the mark's tones is the extractor's
  // judgement and item 3's open survival-ranking question — the field only extends the deck, so a
  // Hanover four-set deals red, white, NAVY, black instead of repeating a stock finish.
  /* ⭐ …AND THE DECK MUST NOT OPEN ON AN OUTLINE'S COLOUR (Ryan 2026-08-18, the E Logo).
     `shownLogoTones` is population-ordered, and that mark's chunky orange OUTER BAND out-populates
     its teal fill — so the first configuration wore the outline's colour rather than the mark's
     centre. When the tone about to lead is one of the mark's BANDS and the structure knows the
     fill, the fill leads instead; the remaining tones keep the extractor's order behind it.
     ⚠ Deliberately NARROW — the leading tone must be a DETACHED band (`bandTouchesFill` false: a
     ring standing off the mark, unambiguously "an outline"), ΔE-matched against the shown list,
     never adding a tone. Two wider cuts each moved Mission Patriots' four-set — a control Ryan
     never complained about. Its dominant navy IS a detected band, but a TOUCHING one: on a badge
     that is structural interior (the patriot's own drawing), and a buyer who calls navy "our
     colour" is right. E Logo's orange is detached — a drawn ring around the letter — and leading
     the deck with a ring's colour is the actual complaint. A mark whose dominant tone is its fill
     (Hanover, most of the corpus), or a touching band, reorders nothing by construction. */
  const { seq } = plateDealDeck(state.ui.palette, p);
  const userSet = p.bodyColorUserSet || {};
  const saved = { ...(p.savedBodyColor && typeof p.savedBodyColor === 'object' ? p.savedBodyColor : {}) };

  // Group the picked set by BASE product, keeping pick order — that order is the sequence's order.
  const groups = new Map();
  for (const it of state.ui.selectedProducts || []) {
    const key = productKey(it.category, it.kind, instanceOf(it));
    if (!productSupportsBodyColor(key)) continue;
    const base = baseProductKey(key);
    if (!groups.has(base)) groups.set(base, []);
    groups.get(base).push(key);
  }

  let changed = false;
  for (const keys of groups.values()) {
    // A set of one is the stock plate, per Ryan — no sequence, nothing to alternate against.
    if (keys.length < 2) continue;
    keys.forEach((key, i) => {
      if (userSet[key]) return;                     // the buyer chose this one; never touch it
      /* ⛔ AND AN EXISTING CONFIGURATION IS ONLY RECOLOURED AT SEED TIME. Ryan: *"if they choose 1,
         then start the design process, then go to the Pick Weights tab and choose a 2nd bumper of
         the same type, the original one that started as black … will remain as black since they
         already started designing."* So `seed` (the palette landing, the big reveal) may repaint
         everything undyed, because until that instant nothing had a colour worth keeping; every
         later call may only fill in configurations that have none. Without this split, adding a
         second plate would silently restyle the first one the buyer has been looking at. */
      if (!seed && saved[key]) return;
      /* WD 337 (Ryan on 120039000595: "shouldn't we have 2 black and 2 white?"): "unseen" is a
         Delta-E question, not equality — a mark whose own near-black sits beside the default
         black used to deal both, reading as 3-of-a-kind. When every colour has been seen, the
         repeat picks the LEAST-dealt one, so a 2-tone palette alternates 2+2 instead of 3+1. */
      // The true per-config LIST, duplicates kept — "least-dealt" counted against a deduped Set
      // always ties and hands every repeat to seq[0] (caught on RIGAR: W/B/W/B became W/B/W/W).
      const inUseList = keys.filter((k) => k !== key && saved[k]).map((k) => normalizeHex(saved[k])).filter(Boolean);
      const free = seq.find((hex) => !inUseList.some((u) => colorsConflict(u, normalizeHex(hex))));
      let want = normalizeHex(free);
      if (!want) {
        const dupCount = (hex) => inUseList.filter((u) => colorsConflict(u, normalizeHex(hex))).length;
        const least = seq.reduce((a, c) => (dupCount(c) < dupCount(a) ? c : a), seq[0]);
        want = normalizeHex(least || seq[i % seq.length]);
      }
      if (!want || normalizeHex(saved[key]) === want) return;
      saved[key] = want;
      changed = true;
    });
  }
  if (changed) p.savedBodyColor = saved;
  return changed;
}

/**
 * THE DEAL'S DECK AND SEQUENCE - the five plate colours a mark is entitled to, as a PURE function
 * of its palette and its profile.
 *
 * ⛔ HOISTED OUT OF `applyDefaultPlateColors` ON 2026-09-18 SO NOTHING HAS TO COPY IT. The dealt
 * plate is the most visible thing the extracted palette decides - WD 429b found marks dyed a
 * colour their logo does not contain - and measuring a palette change against the plates means
 * replaying five stacked rules (the detached-band lead, the ghost-plate read share, R13's solo
 * tones, the stock finishes, the near-black strip). A probe that reimplements those measures its
 * own copy, which is the trap `bandViewOptions` and `renderPaletteFor` were each hoisted to close:
 * *a dump that disagrees with the render is worse than no dump, because the pass is scored on it.*
 * One definition; `applyDefaultPlateColors` and `probe-plate-deal-diff.mjs` both call it.
 *
 * ⚠ EVERYTHING BELOW THE SEQUENCE STAYS WHERE IT WAS - which instance gets which slot is about
 * `savedBodyColor`, `bodyColorUserSet` and the product groups, i.e. about the SESSION, not about
 * the colours the mark is owed. This function answers only the colour question.
 *
 * @param {object} palette the EXTRACTOR palette (`state.ui.palette`)
 * @param {object} p       the design profile
 * @returns {{shown:string[], deck:string[], solo:string[], seq:string[]}}
 */
export function plateDealDeck(palette, p) {
  const shown = shownLogoTones(palette);
  const lsD = p.logoStructure;
  const deckBands = (lsD && lsD.confidence === 'high' && Array.isArray(lsD.bands)) ? lsD.bands.map((h) => normalizeHex(h)).filter(Boolean) : [];
  const deckTouch = Array.isArray(lsD && lsD.bandTouchesFill) ? lsD.bandTouchesFill : [];
  const fillTone = deckBands.length ? normalizeHex(lsD.fillHex) : null;
  const slotZeroIsDetachedBand = shown.length > 0 && deckBands.some((b, i) => deckTouch[i] === false && (b === shown[0] || colorsConflict(b, shown[0])));
  const lead = (slotZeroIsDetachedBand && fillTone) ? shown.find((h) => h === fillTone || colorsConflict(h, fillTone)) : null;
  const deck0 = lead ? [lead, ...shown.filter((h) => h !== lead)] : shown;
  /* ⛔ A PLATE THE WHOLE MARK GHOSTS ON IS NEVER DEALT (Ryan 2026-08-18, BC Tornados: navy +
     slate fine linework, and the dealt slate plate sat at ΔE ≈ 15 from BOTH tones — inside the
     `nearMerges` ghost zone, outside `colorsConflict`'s 14 — so the whole spiral rendered as a
     smudge he read as "super blurry"). The band/repaint machinery cannot rescue hairline
     linework (flood repaints shred it — measured), so the fix is upstream: a candidate tone is
     dealt as a plate only if at least one of the mark's tones still READS on it. Rockford's
     purple plate survives (its gray reads), Hanover's red survives (its white reads) — a mark
     with any legible ink on the candidate is untouched; only the all-ghost pairing is skipped. */
  /* Ink-share weighted, not `some(tone reads)`: a 2% highlight sliver that happens to read must
     not qualify a plate the other 98% of the mark ghosts on. The share bar sits far under the
     smallest real carrier measured (Rockford's gray at ~32% of the ink) and far over sliver
     noise. Palette absent (a resume) → share reads 1 and nothing is skipped. */
  const palAll = ((palette && palette.all) || [])
    .map((c) => ({ hex: normalizeHex(c && c.hex), pop: c && c.population > 0 ? c.population : 0 }))
    .filter((c) => c.hex);
  const totalPop = palAll.reduce((s, c) => s + c.pop, 0);
  /* ⭐ AN EXACT MERGE IS RESCUABLE; ONLY A NEAR MERGE GHOSTS (WD 440, 2026-09-19 — narrowed, NOT
     deleted). This filter used to discount ink at BOTH distances: a tone the candidate plate
     `colorsConflict`s with (ΔE < 14, the same colour) counted against the plate exactly as a tone
     in the 14–26 ghost band did. But those are opposite cases.
       ⛔ AN EXACT MERGE IS THE ONE THE APP ALREADY HAS THREE REMEDIES FOR — the ink inverts
     (`logoCenterColor`), the band repaints (`bandConflictView`), the add-ring carries the contrast.
     It is the ordinary case of a mark dyed onto a plate of its own colour, which this tool does all
     day. The near-merge is the one NOTHING rescues, and it is the one BC Tornados was: navy + slate
     linework on a slate plate at ΔE ≈ 15, both tones failing `readsOn`, the whole spiral a smudge
     he read as *"super blurry"*. That is what the guard was written for and it still refuses it.
       The conflated version cost `db-logo` its own brand green on two plates — the mark came out
     black-and-white across the whole set. With the green dealt it goes white against the green and
     takes a white rescue outline, and it reads (WD 433 measured that at a 0.01 bar; this states the
     reason instead of moving the bar).
       ⚠ THE SELF-EXCLUSION GOES WITH IT, deliberately: the tone that IS the candidate is the
     exact-merge case in its purest form, so it now counts as reading rather than as ink lost.
       Measured over all 528 plates with `probe-plate-deal-diff.mjs --detail`: 13 plates on 5 marks
     move, 3 of them flagged (db-logo cfg2 + cfg4, the reported fault) and 10 approved — a trade,
     and the ⚠ below is the reason the trade is worth showing him rather than deciding here. */
  const readShare = (cand) => (totalPop
    ? palAll.filter((c) => colorsConflict(cand, c.hex) || !nearMerges(cand, c.hex))
      .reduce((s, c) => s + c.pop, 0) / totalPop
    : 1);
  const PLATE_DEAL_MIN_READ_SHARE = 0.15;
  // ⚠ The seated FIELD swatch is in the candidate list too — BC Tornados' ghost plate was not a
  // mark tone at all but its keyed slate field, appended after a first cut of this filter ran.
  const cands = [...deck0, seatedFieldOf(p)].filter(Boolean);
  let deck = cands.filter((cand) => cands.length < 2 || readShare(cand) >= PLATE_DEAL_MIN_READ_SHARE);
  /* When EVERY candidate fails (BC Tornados: 94% of the ink is navy, so its navy plate scores
     2.7% and its slate field 0%), keep only the best-scoring one rather than resurrecting the
     whole list — the stock white/black finishes then carry the rest of the set. */
  if (!deck.length && cands.length) {
    deck = [cands.reduce((a, c) => (readShare(c) > readShare(a) ? c : a))];
  }
  /* ⛔ A SMEAR IS NEVER DEALT (WD 481, Ryan on the Grand Hyatt 35 lb maroon plate: *"I'd rather it
     went"*). `smearSwatches` (palette-extract.js) names the tones that exist only as the edge between
     two other colours, read at full size. Only the PLATE skips them - the logo and its chips keep
     every tone. A palette without the field (a resume, an older save) deals as before. */
  const smear = ((palette && palette.smearHexes) || []).map((h) => normalizeHex(h)).filter(Boolean);
  if (smear.length) {
    const kept = deck.filter((c) => !smear.includes(c));
    if (kept.length) deck = kept;
  }
  /* ── R13 (Ryan's pass-one review, 2026-09-13): DO NOT DEAL A PLATE THAT SWALLOWS A WHOLE PIECE ──
   * *"One section is COMPLETELY white, and another section is multiple colours INCLUDING white…
   * teach the tool to simply not use a white background colour on any of the plates when it has
   * these 2 individual floating parts and one of the parts is white/one-tone."* (carrolllynx cfg3;
   * ee-logo cfg2 is the same mark class, same words.)
   *   A one-tone piece has no internal boundary to survive on: on a plate of its own colour it does
   * not go subtle, it vanishes, and every tone-level rescue would also rewrite the multi-coloured
   * piece beside it that wears that colour legitimately — which he identified himself as the reason
   * the obvious fix is hard. Not dealing the colour costs nothing and breaks nothing.
   *   ⚠ IT HAS TO REACH THE STOCK FINISHES TOO, not just the deck. ee-logo's offending white is
   * `PLATE_STOCK_WHITE`, added INSIDE `plateDefaultSequence` after its own near-white tone had
   * already been filtered out of the deck by the read-share guard above — so a deck-only filter
   * would have looked right and fixed nothing. Hence `avoid`, honoured on both. */
  /* ⛔ A DETACHED KEYLINE IS NOT A SWALLOWED PIECE, and leaving that out cost Hanover its white
     plate on the first measured run — one of the two deliberate look-alike KEEPS this audit
     protects by name. Hanover's white keyline stands OFF its red letters, so it is a one-tone
     detached component by construction and R13 banned #F1F3F3 from its deal, re-dealing all four
     plates. But the app's settled answer for that class is Ryan's own: *"when it merges with the
     plate, going subtle IS the brand's own look there, and the mark still reads (fill + moat)"* —
     the same `bandTouchesFill === false` exemption `bandConflictView` makes and R6 makes. ee-logo's
     white is a band that ABUTS its fill (`bandTouchesFill` true) and carrolllynx's has no band
     structure at all, so both keep their fix. */
  // `deckBands` / `deckTouch` are the same two lists the lead-tone rule above already built.
  const detachedBands = deckBands.filter((_, i) => deckTouch[i] === false);
  const solo = (p.logoSoloTones || []).map((t) => normalizeHex(t && t.hex))
    .filter((h) => h && !detachedBands.some((d) => colorsConflict(d, h)));
  if (solo.length) {
    const kept = deck.filter((c) => !solo.some((s2) => colorsConflict(c, s2)));
    // Never empty-handed: a two-piece mark can be ALL solo tones, and dealing nothing is worse
    // than dealing one of them. The stock finishes then carry the rest of the set.
    if (kept.length) deck = kept;
  }
  const seq = plateDefaultSequence(deck, 5, { avoid: solo });
  return { shown, deck, solo, seq };
}

/**
 * The logo's AUTO contrast ring — derive (or clear) the `logoAddOutline` rescue for the plate the
 * design is on. HOISTED from a design-panel closure on 2026-08-18 so the CONFIG-SWITCH path
 * (main.js `adoptProduct`) can run it too: Ryan walks his four-configuration set with the arrows,
 * and that path ran a slimmer cascade, so the ring only ever appeared after a manual plate-chip
 * pick. One definition, two callers (the panel's `masterSwitchCascade` delegates here).
 *
 * The rules carried over verbatim from the closure (see design-panel's cascade for order notes):
 *   • Fires when the mark's presenting colour merges with the plate (the flatten's colour, else
 *     `primary`) — OR when the mark's bands are all INVISIBLE on this plate
 *     (`bandsVisibleOnPlate === false`: a detached keyline kept-but-merging, Ryan 2026-08-18 —
 *     "a banded mark's own bands carry its contrast" is false on the plate that merges them).
 *   • MATCHES THE TEXT'S SCHEME (colour and ring weight) — the ring does the same job as the
 *     text's outline; `bestContrastRef` is the fallback.
 *   • `auto: true` + `forKey` so `restoreInk` can tell this plate's rescue from the last one's.
 *   • The no-rescue path clears ONLY the derived ring; a buyer-added ring has no `auto` flag.
 */
/* The bar under which a logo's own presented edge does not separate from the plate, so the tool's
   contrast ring is REQUIRED rather than decoration. Measured, not chosen: across the nineteen plates
   Ryan judged, the two he wanted ringed sit at contrast 1.13 and 1.23 and the twelve he wanted bare
   start at 1.50 - a clean gap, and any bar inside it reproduces all nineteen. This is its middle.
   /!\ IT RESTS ON TWO PLATES. Both are a dark edge on a dark plate; a third example should be
   checked before this number is trusted any further than the answers it was fitted to. */
const LOGO_RING_MIN_CONTRAST = 1.35;
/** WD 475: a census tone under this share is a SHADE (anti-aliasing, a highlight), never a colour of its own. */
const SHADE_MAX_SHARE = 0.05;

export function syncLogoContrastRing(state) {
  const p = state.designProfile;
  const ls = p.logoStructure;
  const hasBands = !!(ls && Array.isArray(ls.bands) && ls.bands.length > 0);
  const base = normalizeHex(effectivePlateHex(state));
  const bandsGone = bandsVisibleOnPlate(p, base) === false;
  const logoEl = (p.elements || []).find((e) => e.type === 'logo' && e.logo && e.logo.dataUrl);
  if (!logoEl) return;
  /* ⛔ THE BUYER DELETED THIS RING, AND A DELETION IS A CHOICE (Ryan 2026-09-21) ────────────────
   * *"I intentionally don't want the logo to have an added outline… I go back to the red plate and
   * the logo outline has returned… no way to keep it gone."*
   *
   * THE MODEL KNEW TWO KINDS OF RING AND NEEDED THREE. A ring is either DERIVED (`auto: true`,
   * disposable) or BUYER-ADDED (survives every cascade) — and there was no state for *the buyer
   * removed the derived one*. `dropRing` simply spliced the object out, so this function re-ran on
   * the next plate pick, found the same merge it found the first time, and wrote a fresh ring.
   * Nothing was broken; the deletion just left no trace to consult. `inferHandStyledForMigration`
   * names the same hazard a thousand lines away — *"a REMOVED default leaves no trace at all"* —
   * and this is that hazard with a user-visible face.
   *
   * ⚠ DESIGN-WIDE, NOT PER PLATE (his call, asked and answered). Per-plate suppression would have
   * satisfied the literal report — his four weights are two plate colours — but the ring would come
   * back the first time he dyed to a colour he had not visited yet, which is the same surprise
   * deferred rather than fixed.
   * ⚠ A LATCH, cleared only by a NEW LOGO and by "Reset colors" — the two actions that hand
   * authorship back to the app, exactly as `styleUserEdited` is cleared. His rule, verbatim: *"it
   * needs to allow that and stay that way unless they click 'reset colors'."*
   * ⛔ NOT `styleUserEdited` ITSELF, though it is tempting: that latch is set by ANY hand edit, so
   * recolouring one band would also stand this rescue down for good — and the rescue is what stops
   * a mark vanishing into its plate. The suppression has to be set by the one act that means it. */
  if (p.logoRingSuppressed === true) {
    const held = Array.isArray(logoEl.logoAddOutline) ? logoEl.logoAddOutline
      : (logoEl.logoAddOutline ? [logoEl.logoAddOutline] : []);
    const manual = held.filter((a) => a && a.auto !== true);
    logoEl.logoAddOutline = manual.length ? manual : null;
    state.__ringWhy = { plate: base, hasBands, bandsGone, exit: 'suppressed-by-buyer' };
    return;
  }
  /* WD 479: a one-colour logo flattened to a neutral on this plate already reads (`oneColourFlatten`), so no
     ring goes around it (Ryan, Grand Hyatt: flat white *"instead of ... a thin white outline"*). Its census
     fringe still reads as a kept band, which is what armed the ring before. A buyer's own ring stays. */
  if (base && (contextualFillSwap(p, base) || []).some((s) => s && s.flat)) {
    const held = Array.isArray(logoEl.logoAddOutline) ? logoEl.logoAddOutline
      : (logoEl.logoAddOutline ? [logoEl.logoAddOutline] : []);
    const manual = held.filter((a) => a && a.auto !== true);
    logoEl.logoAddOutline = manual.length ? manual : null;
    state.__ringWhy = { plate: base, hasBands, bandsGone, exit: 'flattened' };
    return;
  }
  /* ── R4 (WD 337, Ryan's corpus review): THE INTERIOR-MERGE TRIGGER ──────────────────────────
   * "A banded mark's bands carry its contrast" has a second false face: a visible band cannot
   * hold an INTERIOR tone that IS the plate colour (benuredhawks' red hawk on the red plate —
   * black band visible, body gone, "hollow"). The ring fires too when a significant NON-band
   * tone merges with the plate and nothing else rescues it (not band-repainted — bands are
   * excluded outright; not fill-swapped on this plate). Scoped by `fillUniform !== true`: a
   * one-tone interior held by its own rings is a designed look (E Logo's teal-on-teal — Ryan's
   * accepted control), and the genuine one-tone mark is the flatten/swap's territory. */
  const interiorMerges = (() => {
    if (!base) return false;
    if (ls && ls.fillUniform === true) return false;
    const bandHexes = hasBands ? ls.bands.map(normalizeHex).filter(Boolean) : [];
    const wrap = normalizeHex(ls && ls.wrapTone);
    /* ⛔ MATCHED BY DELTA-E, NOT BY HEX (Ryan 2026-09-17, garage-gym). This was a `Set` and the
       membership test below was `swapped.has(t)` — string equality — while every other tone test in
       this function compares by ΔE, for the reason stated verbatim a few lines down: *"the census,
       the roles and the table each quantize the same tone slightly differently."* They do, and this
       is what it cost: garage-gym's red is `#F02735` in the swap and `#F22735` in the census. **ΔE
       0.8, against a sameness threshold of 14** — the same colour by every measure the app owns, and
       a miss by string equality. So the trigger concluded that 31% of the mark was vanishing into
       the plate and nothing had rescued it, when `contextualFillSwap` had already repainted exactly
       that tone to white. The plate got a black rescue ring on top of a logo that was already white
       on red, and Ryan: *"it looks like you already did add an outline -- unless I'm mistaken?"*
       Measured over every plate judgeable from the dumps: 7 lose their ring, 2 of them ones he has
       explicitly called wrong, NONE that he called right, no control touched. */
    const swapped = contextualFillSwap(p, base).map((s) => normalizeHex(s.from)).filter(Boolean);
    const trace = (ls && ls.traceTones) || p.logoTraceTones || [];
    const tones = [
      normalizeHex(ls && ls.fillHex),
      normalizeHex(resolvePaletteRef('primary', p.palette)),
      normalizeHex(resolvePaletteRef('secondary', p.palette)),
      normalizeHex(resolvePaletteRef('tertiary', p.palette)),
      ...(Array.isArray(trace) ? trace : []).filter((t) => (t.share || 0) >= 0.02).map((t) => normalizeHex(t.hex)),
    ].filter(Boolean);
    /* A tone the mark's own WRAP draws is HELD when the wrap clears this plate — IF the tone is
       line detail. ECC's white letters (ink 0.111, fully ridden by its wrap) hollow into legible
       outlined type — Ryan reviewed that presentation and let it stand. The hawk's red BODY
       (ink 0.485, also fully ridden) hollows into a broken illustration — his flag. The poles
       sit far apart; 0.25 is the open space between them. */
    const touches = (ls && ls.toneTouches) || null;
    const totPx = touches ? Object.values(touches).reduce((s, r) => s + (r.px || 0), 0) : 0;
    // Row lookup + neighbour tests by Delta-E, never hex equality — the census, the roles and
    // the table each quantize the same tone slightly differently, and the wrap pick itself
    // drifts between runs (open item 5). "Held" must not depend on which read this run got.
    const heldByOther = (t) => {
      if (!touches || !totPx) return false;
      const key = Object.keys(touches).find((k) => { const kh = normalizeHex(k); return kh && colorsConflict(kh, t); });
      const row = key && touches[key];
      if (!row || !row.boundary) return false;
      if ((row.px || 0) / totPx >= WRAP_HELD_MAX_INK) return false;
      for (const [nm, v] of Object.entries(row.touches || {})) {
        if (nm === 'BG') continue;
        const nh = normalizeHex(nm);
        if (nh && !colorsConflict(base, nh) && v / row.boundary >= WRAP_CONTACT_MIN) return true;
      }
      return false;
    };
    /* WD 475 - A SHADE OF A SWAPPED TONE IS SWAPPED WITH IT. cherry-valley-v2 cfg3: `black-on-black` repaints
       the black #020202 to white, and the census still carries #282725 at 3.6% - the anti-aliased edge of that
       same black, 19 dE away, past the sameness test - so this trigger saw part of the mark merging with the
       black plate and added a white ring Ryan called bad (*"the logo getting an outline is bad"*). A minor
       census tone (under `SHADE_MAX_SHARE`) whose NEAREST palette tone was swapped goes with it. */
    const palTones = ['primary', 'secondary', 'tertiary'].map((r) => normalizeHex(resolvePaletteRef(r, p.palette)))
      .concat((p.swatches || []).map(normalizeHex)).filter(Boolean);
    const rgbOf = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
    const nearestPal = (t) => {
      const a = rgbOf(t); let best = null, bd = Infinity;
      for (const h of palTones) { const b = rgbOf(h); const d = (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2; if (d < bd) { bd = d; best = h; } }
      return best;
    };
    const shareOf = (t) => { const r = (Array.isArray(trace) ? trace : []).find((x) => normalizeHex(x.hex) === t); return r ? (r.share || 0) : 1; };
    const shadeOfSwapped = (t) => shareOf(t) < SHADE_MAX_SHARE && !palTones.some((h) => colorsConflict(h, t))
      && (() => { const n = nearestPal(t); return !!n && swapped.some((s) => colorsConflict(s, n)); })();
    return tones.some((t) => colorsConflict(base, t)
      && !bandHexes.some((b) => colorsConflict(b, t))
      && !(wrap && colorsConflict(wrap, t))
      && !swapped.some((s) => colorsConflict(s, t))
      && !shadeOfSwapped(t)
      && !heldByOther(t));
  })();
  /* ── R10 (Ryan's pass-one review, 2026-09-13): THE OUTERMOST BAND IS THE SILHOUETTE ─────────
   * `bandsGone` asks whether EVERY band is invisible, and vikings shows why that is the wrong
   * question. Its outer keyline #0B0405 sits on #100E41 at 1.12:1 and on #391413 at 1.24:1 — the
   * mark's own edge dissolves into the plate — while its second band #E9B390 reads at 9.74:1, so
   * `bandsVisibleOnPlate` answers "visible" and nothing rescues the silhouette. Ryan: *"it looks
   * as though the logo outline colour changed to dark blue, which is the exact same colour as the
   * plate. This causes the whole logo to break."* It never changed. It was always that colour, and
   * the trigger never looked at it.
   *
   * ⚠ JUDGED ON THE PRESENTED BAND, NEVER THE DRAWN ONE — the same `bandConflictView` the raster
   * runs. A merging outer band is usually REPAINTED, and a repaint that lands on a reading colour
   * has already solved this: Abbey's white keyline on the white plate and Malone's silver on
   * silver are both approved and both must stay ringless. Reading the drawn hex would have ringed
   * them — measured, on the 08-24 dumps, before this was written.
   *
   * A PEELED outer band is left alone: peeling is the deliberate "let the plate show through"
   * decision (Hanover's detached keyline, Ryan's own call), and the tone behind it becomes the
   * edge — a different question from this one.
   *
   * ⚠ AND SINCE R11 (2026-09-15) "PEELED" MEANS "AND NO RING WAS THERE TO KEEP IT" — the two rules
   * meet here and it is worth stating that they do not chase each other. On a first arrival the
   * band peels (no ring yet), R10 bails, and `bandsGone` is true anyway, so the ring is derived and
   * written. On every arrival after that R11 keeps the band, so the peel is gone and R10 fires
   * instead — and `bandsGone` is STILL true (nothing clears, nothing repaints), so the same ring is
   * re-derived. Both routes land on one ring; neither can take it away and hand it back. */
  const outerMerges = (() => {
    if (!hasBands || !base) return false;
    const drawn = normalizeHex(ls.bands[0]);
    if (!drawn) return false;
    const view = bandConflictView(ls.bands.map(normalizeHex).filter(Boolean), base, bandViewOptions(p));
    /* ⛔ `bandConflictView` KEYS `repaint` AND `peel` BY BAND INDEX, NOT BY HEX. Read with a hex
       key this returns undefined for every band, so a band the raster repaints reads here as
       "kept, and merging" — and the ring fired on marks that were already rescued. It moved
       KENYON, the gold standard, whose white keyline is repainted black on the white plate and
       which Ryan called "a perfect logo colour change". Caught only because the control was
       measured; nothing about the code looked wrong. */
    if ((view.peel || []).includes(0)) return false;
    /* A DETACHED keyline is deliberately left to go subtle rather than repainted or peeled (the
       Hanover rule, `touchesFill[i] === false` in bandConflictView). Going quiet there is the
       brand's own look on that plate, so it is not a silhouette that needs rescuing. */
    if (Array.isArray(ls.bandTouchesFill) && ls.bandTouchesFill[0] === false) return false;
    const shown = normalizeHex(view.repaint[0]) || drawn;
    return !readsOn(base, shown);
  })();
  /* ── THE MARK WAS KEPT ON PURPOSE, SO IT NEEDS THE RING HE ASKED FOR IN ITS PLACE (WD 440b) ──
   * Ryan's new default is *"keep the logo outline/blue the same, then just add a 2nd white
   * outline"* — TWO halves, and `markRecolourAllowed` only does the first. The second half is not
   * automatic and the corpus proved it in one render: cherry-valley cfg1/cfg4 lost the pink→white
   * swap he had explicitly asked for (*"The D7A8A3 section of the logo should have changed to white
   * on this plate"*) and came back with `logoAddOutline: null`, because `interiorMerges` bails on
   * `fillUniform` and `outerMerges` only looks at the outermost BAND. Neither trigger can see a
   * rescue that was deliberately declined, so the plate was left merging with nothing in its place
   * — strictly worse than what shipped, which is the one way this rule can lose.
   *
   * ⚠ SCOPED TO EXACTLY THE PLATES THE GATE TOUCHED, which is what keeps it from over-ringing.
   * `refusedRecolours` is empty on every plate the rule does not change, so no plate that rings
   * today gains or loses one on account of this; and a refused recolour is a tone that CONFLICTS
   * with the plate by construction (that is the only reason either door proposed it), so there is
   * no case here of ringing a mark that already reads — R14's complaint. */
  const markKept = !!base && refusedRecolours(p, base).length > 0;
  // ⚠ diagnostic — see the `state.__ringWhy` note below. Set before every exit so a plate that
  // never reaches the candidate ladder still says WHY, instead of leaving a stale entry from the
  // previous config and reading as though this one had been judged.
  state.__ringWhy = {
    plate: base, hasBands, bandsGone, interiorMerges, outerMerges, markKept,
    structureMissing: !hasBands && !(ls && ls.confidence === 'high'),
    won: null, wonFrom: null, exit: 'bands-hold',
  };
  if (hasBands && !bandsGone && !interiorMerges && !outerMerges && !markKept) return;
  const center = normalizeHex(logoEl.logoCenterColor);
  const mark = center || normalizeHex(resolvePaletteRef('primary', p.palette));
  const outerBand = hasBands ? normalizeHex(ls.bands[0]) : null;
  /* ── R14 (Ryan's ring question round two, 2026-09-17): THE MERGE TRIGGER WAS ASKING ABOUT A
   * COLOUR THE PLATE NEVER SHOWS — the third time this function has made the "presented, never
   * drawn" mistake, after R10's band read and the Kenyon repaint trap.
   *
   * `mark` is the mark's DRAWN primary. On these plates `contextualFillSwap` has already repainted
   * that tone to the mark's other colour before the plate is rastered, so `colorsConflict(base,
   * mark)` fires for ink the buyer never sees, and a rescue ring goes on a mark that is already
   * legible. Ryan on two of them:
   *   layton    "The white outline isn't even needed.. it's a light blue/white logo, and it's a
   *              dark blue plate color... just leave the logo as-is and it'll stand out fine"
   *   wallabies "this outline shouldn't even be there.. just make the entire logo green"
   *
   * ⛔ READING THE SWAPPED TONE ALONE IS A BAD TRADE — measured over the corpus BEFORE this was
   * written: it clears 5 and strips the ring off 16 plates that were approved, because the swap
   * often lands somewhere that still needs the ring (buccaneersports' black on its blue plate).
   * The swap has only SOLVED the problem when what it landed on actually reads, so the waiver is
   * gated on `readsOn` — the app's OWN 2.0:1 bar, deliberately not a new constant tuned to fit
   * these plates.
   *
   * ⭐ RYAN'S 28-PLATE ANSWER IS WHAT SETS THAT BAR, AND IT IS UNANIMOUS ABOVE IT. Every plate
   * where the repaint reads at 2.0:1 or better he marked "ring is WRONG" — 11 of 11, seven of them
   * plates he had previously approved in silence. Below the bar he SPLITS (uvfpatriots 1.33 and
   * ridge-view 1.33 keep their rings; bscbobcats 1.25 and uisprairiestars 1.42 do not) and nothing
   * measurable separates those four, so this rule deliberately does not reach them.
   *
   * Scored over all 528 plates: fires on 12, of which he called 11 WRONG and 0 RIGHT, none were
   * unseen, and no named control is touched.
   *
   * ⚠ ONLY THE TRIGGER MOVES. `mark` still feeds `neighbour`, `selfTone` and `works` below, which
   * answer a different question — "what tone will this ring sit BESIDE?" — that was not measured
   * here. Widening the change to those was not tested and must not be assumed safe. */
  const markShown = (() => {
    if (!mark || !base) return mark;
    const sw = (contextualFillSwap(p, base) || []).find((s) => {
      const from = normalizeHex(s.from);
      return from && colorsConflict(from, mark);
    });
    return sw ? (normalizeHex(sw.to) || mark) : mark;
  })();
  const repaintSolvedIt = markShown !== mark && readsOn(base, markShown);
  const markMerges = !!mark && colorsConflict(base, mark) && !repaintSolvedIt;
  if (state.__ringWhy) { state.__ringWhy.markShown = markShown; state.__ringWhy.repaintSolvedIt = repaintSolvedIt; }
  /* -- RYAN'S OWN RULE: AN OUTLINE IS ONLY ADDED WHEN IT IS REQUIRED (WD 453) -------------------
   * His words, on cinderwood cfg1, shown the plate rendered both ways:
   *
   *   "The as it ships now adds an extra outline for no reason. Outline should only be added when
   *    required. Meaning, if the outer most outline on the logo was naturally red, and we were
   *    placing the logo on a red plate, then we'd need to add another outline so you don't have the
   *    red on red clash... But if that's not the case and this extra white outline was added just
   *    because, then don't."
   *
   * A VETO over every trigger above, not a sixth trigger. ⛔ And it has to be: replayed over the
   * plates he judged, `outerMerges` fires on the two he KEPT and on nine he wants dropped, and
   * `interiorMerges` fires on seven he wants dropped - so no trigger separates his answers and
   * scoping the veto to one of them fails the rest.
   *
   * ⛔ IT ASKS THE **PRESENTED** EDGE, which is the whole reason it separates them when the
   * triggers do not. `outerMerges` reads `ls.bands[0]`, the band as DRAWN; this asks what the plate
   * actually shows after the peel, the repaint and the swallow test - `presentedOuterOutline` with
   * the added ring suppressed, which is the honest question "would you see this logo if we added
   * nothing?". Cherry Valley's near-black crest sits on a rose plate at contrast 9.9 and was being
   * ringed anyway; that is the plate he flagged in pass 8 and again this week.
   *
   * ⚠ THE BAR IS FITTED TO TWO OF HIS ANSWERS AND THIS FILE SAYS SO. Colour distinctness alone
   * (`colorsConflict`) reproduces 17 of his 19; the two it misses are bears cfg4 and 120039004726
   * cfg3, both a DARK edge on a DARK plate, where the tones differ but nothing separates them. The
   * contrast between plate and edge sorts all nineteen with a clean gap - 1.13 and 1.23 for the two
   * he KEPT, 1.50 and up for the twelve he dropped - and any bar in 1.30-1.45 reproduces 19 of 19.
   * 1.35 is the middle of the measured gap, not a chosen constant, but it still rests on two plates
   * and a third dark-on-dark example should be checked before it is trusted further.
   *   It is the same fact `contextualFillSwapSplit` already names: it is the near-black plate that
   * bites hardest, because every dark ink vanishes into it.
   * ⚠ 51 of the 162 ringed plates lose the ring. 17 of those Ryan judged this week or flagged in
   * pass 8; the rest he has not seen, and the render round is what shows him. */
  /* ── A BAND-LESS MARK'S WRAP IS ITS EDGE (WD 474, Ryan's step-2 flags) ───────────────────────
   * The veto below learns the outer edge from `presentedOuterOutline`, which knows only BANDS (and a
   * wrap on a banded mark). dscc wears a white keyline the band detector declined: bands [], wrap
   * #FEFDFE, and the touch table gives that wrap 100% of the background frontier. Knowing no edge,
   * the veto answered "needed" on every plate - so the grey plate (whose grey beak trips
   * `interiorMerges`) got a red ring the white keyline never needed, while on the WHITE plate,
   * where the keyline really vanishes, no trigger fired at all (`interiorMerges` skips the wrap tone).
   * So when a band-less mark's wrap owns the frontier (`WRAP_FRONTIER_OWN_MIN`, the wrap detector's own
   * monopoly bar; msubsports at 0.66 is the one mark it keeps out, whose number would go navy on black) and
   * that wrap merges with this plate, that is a trigger of its own. 5 plates move: dscc cfg3 (his flag) and
   * four approved plates of the same shape, put to him as pictures.
   * ⛔ THE VETO HALF IS MEASURED DEAD: letting the same wrap tell the veto "it reads, no ring" (which is
   * what dscc cfg4's red ring needs) strips the ring from 30+ approved plates. So is the wide reading: any
   * band-less frontier as the edge, plus the off-band frontier on banded marks (for cmlions), moved 61
   * plates, 57 of them approved. */
  const edgeMerge = (() => {
    const touches = ls && ls.toneTouches;
    if (!base || hasBands || !touches || typeof touches !== 'object') return null;
    const wrap = normalizeHex(ls.wrapTone);
    if (!wrap || frontierShare(touches, wrap) < WRAP_FRONTIER_OWN_MIN) return null;
    const sw = (contextualFillSwap(p, base) || []).find((s) => { const f = normalizeHex(s.from); return f && colorsConflict(f, wrap); });
    const shown = (sw && normalizeHex(sw.to)) || wrap;
    return { wrap, shown, merges: colorsConflict(shown, base) || contrastRatio(base, shown) < LOGO_RING_MIN_CONTRAST };
  })();
  const wrapEdgeMerges = !!edgeMerge && edgeMerge.merges;
  /* WD 481 candidate (7c, cmlions 45 lb - Ryan: "the blue portion of the logo is blue on blue plate... the red plate
     adopted a white outline... it only worked for the red but not the blue?"): on a BANDED mark the veto reads the
     band as the whole edge, but a tone that is not a band can own a big share of the background frontier
     (cmlions: navy, beside the red band). When such a tone merges with this plate, the edge does not read. */
  const offBandEdge = (() => {
    const touches = ls && ls.toneTouches;
    if (!base || !hasBands || !touches || typeof touches !== 'object') return null;
    const bands = (ls.bands || []).map((h) => normalizeHex(h)).filter(Boolean);
    // ⛔ never the WRAP: a keyline round the whole mark that merges is a plate moat, his settled Hanover answer
    // (hclarks 35 lb, white wrap on the white plate, approved ringless - the one other mover without this).
    const wrap = normalizeHex(ls.wrapTone);
    for (const k of Object.keys(touches)) {
      const t = normalizeHex(k);
      if (!t || bands.some((b) => colorsConflict(b, t)) || (wrap && colorsConflict(wrap, t))) continue;
      const share = frontierShare(touches, t);
      if (share < OFF_BAND_EDGE_MIN) continue;
      if (colorsConflict(t, base) || contrastRatio(base, t) < LOGO_RING_MIN_CONTRAST) return { tone: t, share };
    }
    return null;
  })();
  const ringIsNeeded = (() => {
    /* /!\ ILLUSTRATION ESCAPE HATCH - `app/tools/probe-stack-variants.mjs` ONLY, and the fourth of
       its kind. Once this veto ships, "the ring as it used to be" is no longer renderable by asking
       the app for it, so the before/after round that puts this rule in front of Ryan would have no
       BEFORE. Read off `globalThis`, set through `addInitScript` before boot; nothing a buyer can
       reach sets it. */
    if (globalThis.__wdNoRingVeto) return true;
    if (!base) return true;
    const bareProfile = { ...p, elements: (p.elements || []).map((e) => (e.type === 'logo' ? { ...e, logoAddOutline: null } : e)) };
    const edge = presentedOuterOutline(bareProfile, base);
    const hex = edge ? normalizeHex(edge.hex) : null;
    if (!hex) return true;                                   // nothing shows at all - it needs one
    if (offBandEdge) return true;                            // part of the edge is not the band and merges
    if (colorsConflict(hex, base)) return true;              // his red-on-red
    return contrastRatio(base, hex) < LOGO_RING_MIN_CONTRAST; // ...or too dark to separate
  })();
  /* WD 480 candidate (Ryan, dscc 10 lb: "It shouldn't have an outline on the logo. We added a red one, why?"): an
     INTERIOR merge is the only trigger and the wrap that owns the edge reads - a ring round the outside cannot bring
     an inner part back, so none is added. */
  const interiorOnlyEdgeReads = interiorMerges && !markMerges && !bandsGone && !outerMerges && !markKept
    && !wrapEdgeMerges && !!edgeMerge && !edgeMerge.merges;
  if (state.__ringWhy) { state.__ringWhy.ringIsNeeded = ringIsNeeded; state.__ringWhy.edgeMerge = edgeMerge; }
  if (mark && base && ringIsNeeded && !interiorOnlyEdgeReads
    && (markMerges || bandsGone || interiorMerges || outerMerges || markKept || wrapEdgeMerges)) {
    const pal = p.palette || {};
    const rp = {
      ...pal,
      pool: contrastPoolFrom(pal, p.swatches),
      logoOutlined: hasBands,
      logoGap: logoGapFromStructure(ls),
      logoBandsVisible: bandsVisibleOnPlate(p, base),
      /* ⛔ `logoOuterTone` IS DELIBERATELY ABSENT, AND ADDING IT WOULD MAKE THIS EAT ITS OWN TAIL
         (2026-09-15). `effectiveTextStyle`'s mirror gives the number the colour and weight of the
         ring this function is in the middle of choosing; asking for the MIRRORED stack here would
         derive the mark's rescue ring from a text ring that is a copy of the mark's rescue ring.
         The question this call asks is the un-mirrored one — "what does the number wear on its
         OWN?" — and that is the honest input for sizing and colouring the mark's own edge. */
    };
    const e0 = (p.elements || []).find((e) => e.type === 'arctext' || e.type === 'weight');
    const eff = e0 ? effectiveTextStyle(e0, rp, base) : null;
    const effOuts = eff && Array.isArray(eff.outlines) ? eff.outlines : [];
    const textRing = effOuts.length ? normalizeHex(resolvePaletteRef(effOuts[effOuts.length - 1].colorRef, rp)) : null;
    const textFill = eff ? normalizeHex(resolvePaletteRef(eff.fillRef, rp)) : null;
    /* ⭐ GAP-AWARE, like the text ladder it mirrors (Ryan 2026-08-23, Hanover's white plate). On
       a gap mark this ring wraps the plate-coloured MOAT, not the mark's ink — so the
       mark-adjacency clause is waived exactly there, and the ring may be the mark's own colour
       (red-gap-red, the double-stroke athletic look the text just derived). The plate and
       kept-band clauses still hold; every gapless mark keeps the old test verbatim. */
    const gapHere = !!rp.logoGap;
    /* R3 (WD 337): the ring's real neighbour is the mark's outermost VISIBLE tone — the fill
       when the bands are gone (or there are none), the surviving outer band when they hold
       (the interior-merge trigger). The ring must READ against that neighbour (2.0:1 —
       `colorsConflict` alone let a black ring sit on Stout's #111C2C fill at 1.1:1), and it may
       not be a tone the mark wears that the touch table cannot measure (`unmeasuredPresence` —
       the census veto; a black ring on 120018000234's dark linework fused invisibly). A mark
       with NO structure read skips the neighbour test: any legible ring beats none (dscc's red
       ring, Ryan's own ask). */
    const traceCtx = (ls && (ls.traceTones || ls.toneTouches)) ? ls
      : (p.logoTraceTones ? { traceTones: p.logoTraceTones, toneTouches: null } : null);
    /* WD 474: when the wrap-edge trigger alone fired, the ring sits OUTSIDE the wrap keyline, so the wrap (as shown) is
       what it touches - not the fill. dscc's red ring reads on its white keyline whatever the navy says. */
    const wrapOnly = wrapEdgeMerges && !(markMerges || bandsGone || interiorMerges || outerMerges || markKept);
    const neighbour = wrapOnly ? edgeMerge.shown : (hasBands && !bandsGone) ? outerBand : (ls ? mark : null);
    const fits = (hx) => !!hx && !colorsConflict(base, hx)
      && (gapHere || !neighbour || readsOn(neighbour, hx))
      // On the keyline-invisible trigger the ring wraps the BAND, not the fill — it must not
      // merge into the very keyline it exists to rescue (black around Hanover's white ✓).
      && !(bandsGone && outerBand && colorsConflict(outerBand, hx));
    const works = (hx) => fits(hx) && !unmeasuredPresence(traceCtx, hx);
    /* The fallback goes THROUGH `works` too (WD 337): unchecked, `bestContrastRef` handed
       Kenyon's purple plate a white ring around the mark's own white keyline — the doubled-ring
       class Ryan flagged on Philadelphia PD ("the outline is already yellow — why double
       down?"). No survivor now means NO ring, which the clear path below already handles. */
    /* ⭐ …AND A HOUSE-NEUTRAL RUNG, BUT ONLY WHERE THIS RULE TOOK THE RECOLOUR AWAY (WD 440b).
     * Ryan's sentence has two halves and the second one is a COLOUR: *"keep the logo outline/blue
     * the same, then just add a 2nd WHITE outline."* On tigers cfg1 the first three rungs are the
     * mark's own orange three times over — `bestContrastRef` escapes to a neutral only when its
     * best swatch would merge with the PLATE, and here the orange clears the navy plate fine; what
     * kills it is `works`'s NEIGHBOUR test, because an orange ring around the orange fill is
     * invisible. So the ladder ran out with a survivor sitting right there: white clears the navy
     * plate AND reads on the orange, which is the ring he asked for by name.
     *   ⚠ BOTH NEUTRALS, for the reason `fillContrastRemap`'s `bothNeutrals` already states — a
     * vetoed first neutral must not end the walk while the other still reads — and both go through
     * `works` like every other candidate, so the Kenyon white-on-white trap stays shut.
     *   ⛔ SCOPED TO `markKept`, and that scope is the whole safety of it. "No survivor means NO
     * ring" is a deliberate behaviour (see the note directly above) and widening it everywhere
     * would put rings on plates this rule never touched. `markKept` is empty on every plate the
     * gate did not change, so every other plate's ladder is byte-identical. */
    /* ⭐ …AND THE MARK'S OWN COLOURS COME BEFORE THE NEUTRAL FALLBACK (Ryan 2026-09-22). He asked
     * for this twice, about two different marks, and then answered it as a rule:
     *     `120039000589`  *"the line is so thin that it got approved b/c it was hard to tell it was
     *                      even black. To my eye, it looked like navy blue to match what's on the
     *                      logo… it'd be better to use a logo colour."*
     *     babsonathletics *"why we didn't go with a green outer outline on both, since that colour
     *                      is already represented on the logo and would be more 'on-brand'."*
     *   Asked whether an added outline should prefer a colour the logo actually wears and fall back
     * to black or white only when none of them contrasts enough, and whether that governs the ring
     * on the LOGO as well as the one on the number: *"Yes. Applies to both."* It governs both from
     * this one place, because the number's ring is the logo's ring mirrored.
     *
     * ⛔ THE FALLBACK IS NOT A SEPARATE CLAUSE — IT IS `works`. Every rung already has to clear the
     * plate, read against the ring's real neighbour, and survive the census veto, so a logo colour
     * that cannot be seen here is skipped and the walk reaches `bestContrastRef` exactly as it does
     * today. Nothing is added to say "unless it doesn't contrast"; that sentence is already code.
     * ⚠ `markToneOrder` is the app's own list — the mark's tones AS DRAWN on this plate, heaviest
     * first — and it is the same one `renderPaletteFor` hands the text ladder as `logoToneOrder`.
     * A second, hand-rolled ordering here is how the two would start disagreeing. */
    /* WD 474 - THE NUMBER'S RING AS IT WILL BE ON THIS PLATE, FIRST, when the wrap-edge trigger is the ONLY one.
       Ryan on dscc cfg3: *"keep the white outline, then add a red outline around the outside of it to
       match what we did with the weight number."* The number's red is set by `assignOutlineColors`, which
       runs AFTER this function (see the order note in the plate-move path), so `textRing` here is still
       the white wrap ring that vanishes on the white plate. Asked on a throwaway copy, the same helper
       answers what the number is about to wear, and the logo's ring matches it. */
    const cascadeRing = wrapOnly ? (() => {
      const q = { ...p, elements: (p.elements || []).map((e) => (e.type === 'logo' ? e : structuredClone(e))) };
      assignOutlineColors(q, base);
      const te = q.elements.find((e) => e.type === 'arctext' || e.type === 'weight');
      const o = te && Array.isArray(te.outlines) ? te.outlines : [];
      return o.length ? normalizeHex(resolvePaletteRef(o[o.length - 1].colorRef, pal)) : null;
    })() : null;
    const ladder = [cascadeRing, textRing, textFill, normalizeHex(resolvePaletteRef(bestContrastRef(rp, base), rp)),
      ...(markKept ? DEFAULT_ACCENTS : [])];
    /* ⛔ THE CENSUS VETO RANKS THE LADDER; IT MUST NOT EMPTY IT (Ryan 2026-09-20, oakton-owls
     * cfg1 + cfg4: *"The white outline doesn't match."* — THE criterion, and it does not match
     * because the mark has no outline at all while the number wears white).
     *   `unmeasuredPresence` is the Kenyon test, and its own docstring says what it is for and what
     * it assumes: a REPAINT target the mark already wears is unsafe when the adjacency table cannot
     * weigh the fusion, and *"the reversible add-ring carries the contrast instead."* On this ladder
     * there IS no add-ring behind it — this IS the add-ring — so the veto spends a safety it does
     * not have and leaves the mark bare. Oakton's census carries `#FCFCFC` at 1.5% with no touch
     * row, so white is refused; its other three candidates fail the neighbour test; the ladder ends
     * empty and the plate ships with a white-ringed number beside a ringless mark.
     *   ⚠ SO THE VETO STILL WINS WHENEVER ANYTHING ELSE SURVIVES IT — Kenyon's white-on-white trap
     * and Philadelphia PD's doubled yellow are both plates where a NON-vetoed candidate exists, and
     * both are untouched. This only reaches the case where the alternative is nothing.
     *   ⛔ AND ONLY WHERE THE MARK'S OWN EDGE IS WHAT WENT — `bandsGone` or R10's `outerMerges`.
     * On an INTERIOR merge the silhouette still reads, so shipping ringless is a real option and
     * not a failure, and Ryan has chosen it by name: www-nmmiathletics on its red plate is one of
     * the five plates he was shown both ways and answered "Mark kept" (see `RINGLESS_ACCEPTED` in
     * `probe-keep-mark-rule.mjs`). Un-scoped, this fallback hands that plate a ring and reverses
     * his own answer, along with bwyellowjackets and frostburg.
     *   Measured over all 528 plates of pass 8 (`probe-ring-net.mjs --pass REVIEW-2026-09-20`):
     * THREE plates move — oakton cfg1 + cfg4, both of them his flags, and gomountainlions cfg1,
     * which moves ONLY in the replay (⚠ it is one of the plates where the replay sees a text ring
     * the live cascade had not built yet — see that probe's header; the app's own `ringWhy` records
     * `textRing: null` there, so at HEAD's call order this rule cannot reach it). Zero approved
     * plates move, zero named controls move. Dropping the veto from `works` outright instead moves
     * 18, EIGHT of them plates he approved, because that also lets a vetoed tone OUT-RANK a
     * surviving one; un-scoped but ranked moves 11, three of them approved. This is the narrowest
     * of the three and the only one that costs nothing. */
    const ladderRing = ladder.find(works) || ((bandsGone || outerMerges) ? ladder.find(fits) : undefined);
    /* ⭐ …AND WHEN THE LADDER LANDS ON A NEUTRAL, A COLOUR THE MARK ACTUALLY WEARS TAKES IT
     * (Ryan 2026-09-22). He asked for this twice, about two different marks, then gave it as a
     * rule when it was put to him as one:
     *     `120039000589`  *"the line is so thin that it got approved b/c it was hard to tell it
     *                      was even black. To my eye, it looked like navy blue to match what's on
     *                      the logo… it'd be better to use a logo colour."*
     *     babsonathletics *"why we didn't go with a green outer outline on both, since that colour
     *                      is already represented on the logo and would be more 'on-brand'."*
     *   Asked whether it governs the ring on the LOGO as well as the one on the number: *"Yes.
     * Applies to both."* It governs both from here, because the number's ring is this one mirrored.
     *
     * ⛔ IT SWAPS A WINNER; IT NEVER CREATES ONE. Written as an extra RUNG instead — the mark's
     * tones inserted before the neutral fallback — it put a ring on EIGHT plates that ship
     * ringless, because a surviving logo tone appeared where the ladder had previously run out.
     * "No survivor means NO ring" is deliberate (see the note above) and this rule has nothing to
     * say about it. So the ladder decides WHETHER, exactly as it does today, and this decides only
     * WHICH — hence `ladderRing` first, and a swap only if there is something to swap.
     * ⛔ AND "A LOGO COLOUR" IS `isNeutralTone`, THE APP'S OWN TEST, NOT "any tone in the census".
     * The same first attempt swapped black for `#2A0F26` on the very plate his note is about — a
     * near-black plum out of the tone census, which is not the navy he meant and not a difference
     * anyone could see. It is the same fact this session measured on that plate from the other
     * side: the mark's darker navy and plain black move 50 pixels of the whole disc. A tone that
     * reads as a neutral cannot be the answer to "use a brand colour instead of a neutral".
     * ⚠ The swapped-in colour goes through `works` like every other candidate, so it still has to
     * clear the plate, read against the ring's neighbour and survive the census veto — which is
     * the whole of "fall back to black or white when no logo colour contrasts enough". */
    const keylineOnly = keylineOnlyTones(p);
    const onBrand = (ladderRing && isNeutralTone(ladderRing))
      ? markToneOrder(p, base).find((h) => h && !isNeutralTone(h)
        && !keylineOnly.some((k) => colorsIndistinguishable(k, h))
        /* ⛔ AND NEVER THE COLOUR THE WEIGHT NUMBER IS ALREADY PAINTED IN. The number mirrors this
         * ring, and a ring the same colour as its own fill is the one thing the mirror must refuse
         * (*"never do two same colors on top of each other like this with text"*) — so choosing it
         * here guarantees the pair can NEVER match, which is the opposite of what this rule is for.
         *   ⚠ FOUND ONLY BY THE COMBINED NET, and it is the reason that net exists. Measured on its
         * own this rule looked clean at 22 plates; chained through the mirror it stripped the ring
         * off SIX — dscc cfg1, broncos cfg1, gostillman cfg1, msubsports cfg1, mvclions cfg1 and
         * wexmoor cfg1 — each one a logo ringed in a brand colour beside a bare weight number
         * painted that same colour, and each one worse than the white ring it replaced. Excluded,
         * the ladder's neutral stands on exactly those plates and the pair matches again. */
        && !(textFill && colorsConflict(textFill, h))
        && fits(h))
      : null;
    const ringHex = onBrand || ladderRing;
    /* ⚠ DIAGNOSTIC ONLY (2026-09-16, corpus pass five). Writes no colour and renders nothing —
       it records WHICH of the four triggers brought us here and which candidate won, because the
       corpus dump cannot answer either. `logoStructure.traceTones` is dumped but `p.logoTraceTones`
       — the fallback the interior-merge trigger reads on a mark whose structure read FAILED — is
       not, so an offline replay of a structureless mark is blind to its own deciding input. That
       blindness made the first cut of the pass-five ring rule describe 589/4078 as "an invented
       ring on a structureless mark" when the real trigger is R4: the mark's own white merging into
       the white plate. Lives on `state`, never on the element — an element field would serialise
       into the design profile and into the review fingerprint. */
    state.__ringWhy = {
      /* ⚠ MERGE, never replace — the earlier write on this call carries `markShown` and
         `repaintSolvedIt`, and replacing the object dropped them on exactly the plates that DID
         get a ring, which read as "the rule never ran" when it had. */
      ...(state.__ringWhy || {}),
      plate: base, mark, structureMissing: !hasBands && !(ls && ls.confidence === 'high'),
      hasBands, bandsGone, interiorMerges, outerMerges,
      neighbour, gapHere, textRing, textFill,
      fallback: normalizeHex(resolvePaletteRef(bestContrastRef(rp, base), rp)),
      won: ringHex || null,
      /* ⛔ `fallback` USED TO MEAN BOTH REMAINING RUNGS AND THAT MADE THE DIAGNOSTIC LIE (WD 444).
         The ladder is textRing -> textFill -> `bestContrastRef` -> the house neutrals, and the last
         rung is the WD 440b one scoped to `markKept`. Labelling an accent win as `fallback` says
         "the contrast pool chose this" about a ring the keep-the-mark rule minted — and the pass-8
         dump reports `wonFrom: 'fallback'` on twelve plates where `bestContrastRef` had in fact run
         out. It is the difference between narrowing a TRIGGER and narrowing a RUNG, and reading the
         old label cost a session that distinction on the five plates Ryan flagged.
         `censusVetoed` is the same honesty about the other exit: a ring that only got through on
         the second pass below is not the ring the ladder would have chosen. */
      wonFrom: (cascadeRing && ringHex === cascadeRing) ? 'cascadeRing' : ringHex === textRing ? 'textRing'
        : ringHex === textFill ? 'textFill'
          : ringHex === normalizeHex(resolvePaletteRef(bestContrastRef(rp, base), rp)) ? 'fallback'
            : ringHex ? 'accent' : null,
      censusVetoed: !!ringHex && !works(ringHex),
      traceTones: ((ls && ls.traceTones) || p.logoTraceTones || []).map((t) => ({ hex: t.hex, share: t.share })),
    };
    const textRingW = effOuts.length ? Number(effOuts[effOuts.length - 1].thickness) : 0;
    /* ⛔ THE NO-TEXT-RING FALLBACK WAS A STALE PEG, AND IT WAS THE HEAVIEST RING THE APP COULD
     * DRAW (Ryan 2026-09-16, corpus pass three — uvfpatriots: *"the outline thickness defaulted to
     * being pretty thick, when I'm not sure why it settled on this… the small room for improvement
     * here is to not make the outline so thick when the 25 and 35 lb weight numbers receive no
     * weight outline on their weight numbers"*; the same words on buccaneersports and lakeeriestorm,
     * and *"a thick obnoxious white outline around a thin/intricate/detailed logo"* on Cherry
     * Valley v2).
     *   `0.06` was pegged when a fraction of the logo bought ~96 viewBox units, which made it ~5.8 —
     * a middling ring against the 20 the dial offered then. `LOGO_BOX_NOMINAL_VB` corrected that
     * rate to 52 on 2026-08-15 and every OTHER derived weight was re-pegged with it; this constant
     * was not. At 52 it is 3.12 units, i.e. ABOVE `DEFAULT_OUTLINE_THICKNESS` — the ceiling the
     * mirror branch one line up, `outlineThicknessFor`, `presentedOuterOutline` and the buyer's own
     * dial all obey. The mark's rescue ring was the one ring in the app allowed to out-weigh every
     * ring it might have to sit beside.
     *   The honest fallback is the same one `presentedOuterOutline` already uses for the same
     * question ("what does this mark's own edge weigh?"): the artwork's measured linework. Measured
     * over the corpus first — 38 plates carry this fallback and 18 of them are in Ryan's 76 (47%),
     * against 9 of the 95 plates whose ring is mirrored from a real text ring (9%).
     *   ⚠ IT IS A TRADE AND IT WAS REPORTED AS ONE. Twenty plates he approved wear a 0.06 ring and
     * all twenty get lighter, Hanover cfg2 among them. They arrive on the next pass as REGRESSION,
     * which is what the ledger is for; the alternative was keeping a constant that is provably
     * outside the app's own ceiling because nobody had complained about it yet. */
    const ringFrac = (textRingW > 0 ? textRingW : outlineThicknessFor(p)) / LOGO_BOX_NOMINAL_VB;
    /* ⚠ THE SELF-TONE GUARD COMPARES AGAINST THE TONE THE RING WILL SIT BESIDE, WHICH ON A
       BANDED MARK IS THE BAND, NOT THE FILL (vikings, 2026-09-15). This read `ringHex !== mark`,
       and `mark` is the flatten colour or `primary` — right when the ring wraps the fill, wrong
       the moment R10 fires, because there the thing dissolving into the plate is the OUTER BAND.
       Vikings' only surviving candidate is its own red fill: a red ring around a near-black
       keyline is a real, visible ring, and this line threw it away for being 'the mark's own
       colour'. `works` already requires the ring to READ against `neighbour`, so comparing to
       the same tone is the consistent test and cannot admit an invisible ring. */
    /* ⚠ SCOPED TO THE TRIGGER THAT NEEDS IT (measured, 2026-09-15). Comparing against the
       BAND instead of the fill is right only when the band is what merges — i.e. when R10 is
       the reason we are here. Applied unconditionally it also widened the `bandsGone` and
       interior-merge paths, where the ring effectively wraps the FILL, and handed rings to
       three plates Ryan had approved (Abbey cfg3 among them, whose own outer band clears its
       plate at 2.02:1 and never triggered R10 at all). Outside R10's case the historical
       comparison stands. */
    const selfTone = (outerMerges && !bandsGone) ? (neighbour || mark) : mark;
    if (ringHex && (gapHere || ringHex !== selfTone)) { // gap between → the mark's own colour is a real ring
      state.__ringWhy.exit = 'ring-written';
      logoEl.logoAddOutline = [{ color: ringHex, thickness: ringFrac, auto: true, forKey: inkContextKey(state) }];
      addSwatch(state, ringHex);
      return;
    }
  }
  if (state.__ringWhy && state.__ringWhy.exit === 'bands-hold') state.__ringWhy.exit = 'no-candidate-or-no-trigger';
  // Only ever clears the DERIVED ring — a buyer-added one carries no `auto` flag and must survive.
  const rings = Array.isArray(logoEl.logoAddOutline) ? logoEl.logoAddOutline : (logoEl.logoAddOutline ? [logoEl.logoAddOutline] : []);
  const manual = rings.filter((a) => a && a.auto !== true);
  logoEl.logoAddOutline = manual.length ? manual : null;
}

/** Latch a configuration as hand-dyed, so the defaults above leave it alone for good. */
export function markBodyColorUserSet(state, key) {
  if (!key) return;
  const p = state.designProfile;
  p.bodyColorUserSet = { ...(p.bodyColorUserSet && typeof p.bodyColorUserSet === 'object' ? p.bodyColorUserSet : {}), [key]: true };
}

/** Stamp the live plate colour against `key`. No-ops on a product that cannot wear one. */
export function rememberBodyColor(state, key) {
  if (!key || !productSupportsBodyColor(key)) return;
  const p = state.designProfile;
  const hex = normalizeHex(p.bodyColor);
  if (!hex) return;
  p.savedBodyColor = { ...(p.savedBodyColor && typeof p.savedBodyColor === 'object' ? p.savedBodyColor : {}), [key]: hex };
}

/**
 * Put `key`'s remembered plate colour back on the design.
 * @returns {boolean} whether anything actually MOVED — false both when there is nothing remembered
 *   and when what is remembered is already on the plate. Callers use it to decide whether a
 *   re-render is owed, and reporting a no-op change would re-record history on every hop.
 */
export function restoreBodyColor(state, key) {
  if (!key || !productSupportsBodyColor(key)) return false;
  const p = state.designProfile;
  const want = normalizeHex(p.savedBodyColor && p.savedBodyColor[key]);
  // ⚠ ABSENCE IS "INHERIT", NOT "RESET TO BLACK". A configuration the buyer has never dyed opens
  // wearing the design's current colour, because it IS the same design — see the profile note.
  if (!want || want === normalizeHex(p.bodyColor)) return false;
  setBodyColor(state, want);
  return true;
}

/** Every configuration's plate colour, for a surface that has to tell two of them apart. */
export function bodyColorFor(state, key) {
  if (!productSupportsBodyColor(key)) return DEFAULT_BODY_COLOR;
  const p = state.designProfile;
  if (key === state.ui.product) return normalizeHex(p.bodyColor) || DEFAULT_BODY_COLOR;
  return normalizeHex(p.savedBodyColor && p.savedBodyColor[key]) || normalizeHex(p.bodyColor) || DEFAULT_BODY_COLOR;
}

// ── Per-plate-colour INK memory (Ryan 2026-08-01) ────────────────────────────
//
// The view above answers "can this be READ against the plate". It cannot answer "what did the buyer
// CHOOSE for this plate", and that is the gap this closes: black ink on a light-yellow bumper and
// white ink on a black dumbbell are both deliberate, both correct, and cannot both live in one shared
// set. Whichever was touched last silently ruined the other.
//
// The axis is the PLATE COLOUR, not the product — see the `savedInk` block in design-profile.js for
// why those are not the same question. Everything black shares one bucket, so the nine products that
// cannot be dyed behave exactly as they always have.

/**
 * The plate colour this design's ink is painted against: `'black'` or an upper-case dye hex.
 * Pass `body` / `product` to ask the question about a state that is about to be replaced — that is
 * how the seams stamp the OUTGOING context before mutating.
 *
 * ⚠ The dye only counts on a product that can wear it. A bumper dyed yellow and a dumbbell are two
 * contexts; a dumbbell and a TRAINING bumper are one, even though the design is still dyed, because
 * both paint the ink against black.
 */
export function inkContextKey(state, { body, product } = {}) {
  const prod = product || state.ui.product;
  const raw = productSupportsBodyColor(prod)
    ? (body === undefined ? state.designProfile.bodyColor : body)
    : DEFAULT_BODY_COLOR;
  return isBlackBody(raw) ? 'black' : ((normalizeHex(raw) || DEFAULT_BODY_COLOR).toUpperCase());
}

/**
 * Read the ink off the live elements — ONE record per ROLE, not one per piece.
 *
 * ⚠ PER ROLE, BECAUSE THE UI HAS NO PER-PIECE COLOUR. `setFillAll`, `setOutlineColorAll` and
 * `setOutlineUserSetAll` all fan a single pick out to EVERY text+weight element; there is no control
 * anywhere in the tool that inks one piece differently from its siblings. The first cut keyed records
 * by `type:slot`, which was over-specified in a way that actively broke, because every always-black
 * product shares ONE bucket while their element sets differ: a bumper writes `arctext:top /
 * arctext:bottom / weight:right`, a grip reads `weight:left / weight:right`. A stored set therefore
 * landed on SOME of the destination's pieces and not others. Ryan saw both halves of that — the grip
 * came up with its two weight numbers in DIFFERENT colours, and the bumper came home with an outline
 * on its arc text that the weight number did not have (the arc text does not exist on the grip, so it
 * was rebuilt fresh from the preset, seeded with a ring, with no old element to migrate a stripped
 * stack from).
 *
 * Colour, PLUS the ring count. Thicknesses, which bands are off, and where a piece sits stay on the
 * elements — but "solid or outlined" is an inking decision here rather than a structural one (it is
 * exactly what `syncFillContrastOutline` and `dropAutoOutlinesWhenFillReads` toggle), so the
 * remembered stack length CAPS the live one on restore. It never invents a ring; it only declines to
 * keep extras.
 *
 * Refs are kept as refs, so a buyer who later edits a palette role sees the remembered ink follow it.
 */
function captureInk(profile) {
  const els = profile.elements || [];
  const out = {};
  const txt = els.find((e) => e.type === 'arctext' || e.type === 'weight');
  if (txt) {
    const rec = {};
    if (txt.fillRef) rec.fill = txt.fillRef;
    if (txt.fillUserSet === true) rec.fillUserSet = true;
    const outs = (txt.outlines || []).slice(0, 4);
    // ALWAYS set, even when empty — the LENGTH is the cap, so "the buyer made this solid" has to be
    // a recordable state rather than an absent one.
    rec.outlines = outs.map((o) => (o && o.colorRef) || null);
    // ⚠ AND THE WIDTHS, because the count is now a FLOOR as well as a ceiling (Ryan 2026-08-05) —
    // `restoreInk` can RE-CREATE a ring, and a re-created ring needs its own thickness or it comes
    // back at the design default and reads as "my outline got thinner", which is its own bug report.
    // Parallel array rather than a shape change: an older envelope simply lacks it and falls back.
    rec.outlinesW = outs.map((o) => (o && Number.isFinite(o.thickness) ? o.thickness : null));
    /* ⚠ AND EACH RING'S SLOT IDENTITY (Ryan 2026-08-18, the E Logo four-set). The restore is
       positional: it recolours whatever ring happens to sit at each index and re-creates the rest —
       so a survivor kept the autoIdx (and width) of the ring it USED to be, and every re-created
       ring had none. The re-derive that runs right after trusts autoIdx to know which autoStyle
       slot a ring echoes (`preferredFor`, the band mirror), so one hop through a one-ring plate
       turned the black plate's white+orange stack into orange+teal. Faithful means the whole
       identity: colour, width, AND slot. Parallel array again; an older bucket lacks it and the
       restore then leaves autoIdx exactly as it always did. */
    rec.outlinesAuto = outs.map((o) => (o && Number.isInteger(o.autoIdx) ? o.autoIdx : null));
    // ⚠ WHOSE CHOICE EACH RING WAS, by index — the `logoBandUserSet` convention. An earlier cut
    // stamped `userSet` on every ring it restored, which promoted derived and rescue rings to "the
    // buyer chose this" — and `userSet` is exactly what makes a ring immune to
    // `dropAutoOutlinesWhenFillReads` and `assignOutlineColors`. One hop made a transient permanent.
    const us = outs.map((o, i) => (o && o.userSet === true ? i : -1)).filter((i) => i >= 0);
    if (us.length) rec.outlinesUserSet = us;
    // WD 495: the HAND state, per plate colour - which rings the buyer added, whether they took the stack over, and their
    // deletions / thicknesses on the app's own rings. An older record lacks all three and restores as "no hand edits".
    const ad = outs.map((o, i) => (o && o.added === true ? i : -1)).filter((i) => i >= 0);
    if (ad.length) rec.outlinesAdded = ad;
    if (txt.ringsOwn === true) rec.own = true;
    if (txt.outlineEdits && typeof txt.outlineEdits === 'object' && Object.keys(txt.outlineEdits).length) {
      rec.edits = JSON.parse(JSON.stringify(txt.outlineEdits));
    }
    out.text = rec;
  }
  const logo = els.find((e) => e.type === 'logo');
  if (logo) {
    // `center: null` means "the original artwork", which is a choice like any other and must be
    // recorded rather than omitted — otherwise a flatten made on one plate leaks onto the next.
    const rec = { center: normalizeHex(logo.logoCenterColor) || null };
    if (logo.logoBandColors && typeof logo.logoBandColors === 'object') {
      const b = {};
      for (const k of Object.keys(logo.logoBandColors)) {
        const hx = normalizeHex(logo.logoBandColors[k]);
        if (hx) b[k | 0] = hx;
      }
      if (Object.keys(b).length) rec.bands = b;
    }
    if (Array.isArray(logo.logoBandUserSet) && logo.logoBandUserSet.length) {
      rec.bandsUserSet = logo.logoBandUserSet.map((i) => i | 0);
    }
    const raw = Array.isArray(logo.logoAddOutline) ? logo.logoAddOutline : (logo.logoAddOutline ? [logo.logoAddOutline] : []);
    const manualRings = raw.filter((a) => a && a.auto !== true && normalizeHex(a.color)).slice(0, OUTLINES_MAX);
    const adds = manualRings.map((a) => normalizeHex(a.color));
    if (adds.length) {
      rec.adds = adds;
      rec.addsW = manualRings.map((a) => (Number.isFinite(a.thickness) && a.thickness > 0 ? a.thickness : null)); // WD 495
    }
    out.logo = rec;
  }
  return out;
}

/** Stamp the ink the elements are CURRENTLY wearing into `key`. Call BEFORE anything re-derives. */
/* ── THE NUMBER'S POSITION IS A PROPERTY OF THE PRODUCT, NOT OF THE DESIGN (Ryan 2026-08-09) ──────
 * *"if I move it lower on the comp urethane bumper, the position of it on the dumbbells, fixed
 * barbells, training bumper, etc. should be unchanged."*
 *
 * 🔑 AND THAT IS THE SAME ASK AS "IT REVERTED WHEN I CAME BACK TO THE DUMBBELL", not a second one.
 * Both are answered by a per-product MEMORY rather than by carrying the value on the element: carry
 * it and every product shares one position, drop it and every product forgets. A map keyed by
 * product gives each its own, remembered.
 *
 * ⚠ THE ELEMENT STILL HOLDS THE LIVE VALUE. This is a memory beside it, exactly as `savedInk` is a
 * memory beside the live ink — which is what keeps a LAYOUT change inside one product carrying the
 * position through `migrateElement` (Ryan's earlier bug, fixed 2026-08-09) while a PRODUCT change
 * re-reads it from here. Two different journeys, two different mechanisms, one stored fact.
 *
 * ⚠ KEYED BY PRODUCT, NOT BY FAMILY. The dumbbell and the fixed barbell share a layout family and a
 * cap zone, and Ryan named them as two things that must not move together. Family keying is the trap
 * `savedArcText` fell into (CLAUDE.md §5): one memory cell serving two products, each overwriting
 * the other's answer.
 */
export function weightNudgeKey(state, { preset } = {}) {
  /* ⚠ THE BASE KEY — two configurations of one product SHARE this, and that is the whole point of
     the model Ryan chose. An instance owns its plate colour and its denominations; the DESIGN is
     one design, and where the weight number sits on a comp bumper's face is the design. Keying on
     the instance would let the white 45s and the green 25s disagree about a position the buyer set
     once, with no control anywhere that explains why. `savedInk` is the deliberate opposite — it
     is keyed by PLATE COLOUR, so it already gives each configuration its own ink for free. */
  const prod = baseProductKey(String((state && state.ui && state.ui.product) || ''));
  /* ⚠ AND THE LAYOUT (Ryan 2026-08-09: *"if I move the weight number on the full face layout for a
     dumbbell, the sidelines layout should have its weight numbers completely unaffected"*).
       Product alone was too coarse by exactly one axis. A position is only ever meaningful against
     the arrangement it was judged in — Full Face puts the number over a full-bleed mark, Sidelines
     puts it in clear space, and "a bit lower" means two different things there. Keying on both is
     what makes each answer stay where it was given.
       This is the same correction `savedArcText` needed (CLAUDE.md §5), one axis further along: that
     one was keyed by slot and needed the family; this one was keyed by product and needs the
     layout. */
  const lay = String(preset !== undefined ? (preset || '') : ((state && state.designProfile && state.designProfile.layoutPreset) || ''));
  return `${prod}::${lay}`;
}
/** Stamp the live number's offset against `key`. Zero is stored as a real answer, not as absence —
 *  "I put it back to centre here" has to survive a trip to another product and home again. */
export function rememberWeightNudge(state, key) {
  if (!key) return;
  const p = state.designProfile;
  const w = (p.elements || []).find((e) => e && e.type === 'weight');
  /* ⛔ THE BIG LOGOS ONLY — Badge ×4 and Full Face (Ryan 2026-08-09, correcting a widening he had
     asked for an hour earlier: *"the logo size is in lock step with each other because all the other
     layouts have scales for their logos which are the same, so it makes sense that if you change one
     to 20% on one, you'll likely want it to be 20% on the others. The only exception is the 4x BIG
     BADGE and the FULL FACE layouts — these need their own rules due to the nature of them having a
     different scale and much larger size."*)
       That is a real property of the geometry and not a preference: a slot logo is fitted into a
     slot, so 20% means roughly the same apparent size on every arch layout and carrying it across is
     what a buyer expects. A big mark is placed by a RULE against the ring or the face and is several
     times larger, so its 20% is a different animal — and lock-stepping the two would drag every arch
     layout down whenever someone shrank a badge.
       So the memory covers big marks and the ELEMENT still carries the size for everything else,
     exactly as it always has (`migrateElementSize`). */
  const l = (p.elements || []).find((e) => e && e.type === 'logo' && e.big === true);
  const store = (p.savedWeightNudge && typeof p.savedWeightNudge === 'object') ? { ...p.savedWeightNudge } : {};
  const rec = {};
  if (w) rec.w = { u: clampBigNudge(w.nudgeU, WEIGHT_NUDGE_MAX, WEIGHT_NUDGE_STEP),
                   v: clampBigNudge(w.nudgeV, WEIGHT_NUDGE_MAX, WEIGHT_NUDGE_STEP) };
  // The LOGO rides the same record: it is the same question about the same arrangement, and one
  // store means the two can never disagree about which layout an offset belonged to.
  /* ⛔ AND ITS SIZE (Ryan 2026-08-09: *"if I resize/position the logo on BIG BADGE, do not have it
     affect the SVG for BIG BADGE FLIP — that's its own independent layout"*).
       Position was already per-layout; size was still travelling on the element through
     `migrateElementSize`, so the two halves of the same control disagreed about what a layout owns.
     They ride one record now, which is also what stops a future edit fixing one and forgetting the
     other. `userSet` comes too — without it the size re-seeder treats a restored hand-size as fair
     game and re-pegs it on the next layout change. */
  if (l) {
    rec.l = { u: clampBigNudge(l.nudgeU), v: clampBigNudge(l.nudgeV) };
    if (Number.isFinite(l.sizeScale)) rec.l.s = l.sizeScale;
    if (l.sizeUserSet === true) rec.l.userSet = true;
  }
  if (Object.keys(rec).length) store[key] = rec; else delete store[key];
  p.savedWeightNudge = Object.keys(store).length ? store : null;
}
/** Put `key`'s remembered offset onto the live weight elements. A product with no record resets to
 *  centre — which is the honest answer: it has never been positioned, so it wears the layout's seed. */
/** The record stored for `key`, or null. Exposed so a PREVIEW can ask for a layout's offsets without
 *  going through the live restore — see `presetThumbSVG`. */
export function nudgeRecordFor(profile, key) {
  return (key && profile && profile.savedWeightNudge && profile.savedWeightNudge[key]) || null;
}
/** Write a record's offsets onto a profile's pieces. Split out of `restoreWeightNudge` so the layout
 *  CARDS apply the identical rule to their own clone — one definition of "what this layout's offsets
 *  mean", rather than a second copy that can drift from the live one. */
export function applyNudgeRecord(profile, rec) {
  const wu = rec && rec.w ? clampBigNudge(rec.w.u, WEIGHT_NUDGE_MAX, WEIGHT_NUDGE_STEP) : 0;
  const wv = rec && rec.w ? clampBigNudge(rec.w.v, WEIGHT_NUDGE_MAX, WEIGHT_NUDGE_STEP) : 0;
  const lu = rec && rec.l ? clampBigNudge(rec.l.u) : 0;
  const lv = rec && rec.l ? clampBigNudge(rec.l.v) : 0;
  for (const e of (profile && profile.elements) || []) {
    if (!e) continue;
    if (e.type === 'weight') { e.nudgeU = wu; e.nudgeV = wv; }
    /* ⚠ A NON-BIG LOGO IS NOT TOUCHED AT ALL HERE — not its nudge, not its size. It has no pad, and
       its size is deliberately shared across the slot layouts; reaching in to reset it is what made
       Varsity Arch forget a size the buyer had set on another arch. See the note on `l` above. */
    else if (e.type === 'logo' && e.big === true) {
      e.nudgeU = lu;
      e.nudgeV = lv;
      /* No record for this layout → it has never been sized here, so hand it back to the seeder
         rather than letting the previous layout's number stand. `setLayoutPreset` runs
         `seedDefaultSizes` immediately after this, and that honours `sizeUserSet` — so clearing the
         flag is precisely how a layout gets its OWN default instead of inheriting one. */
      if (rec && rec.l && Number.isFinite(rec.l.s)) {
        e.sizeScale = rec.l.s;
        if (rec.l.userSet) e.sizeUserSet = true;
      } else {
        /* ⚠ AND DROP THE CARRIED NUMBER, not just the flag. Clearing `sizeUserSet` alone is enough
           for the LIVE path, where `setLayoutPreset` re-seeds a line later — but a layout CARD has
           no seeder behind it, so the previous layout's hand-set scale simply stood and Big Badge
           Flip went on previewing Big Badge's size. Undefined renders at the layout's own nominal
           size and re-seeds the moment the buyer actually picks the layout. */
        delete e.sizeScale;
        e.sizeUserSet = false;
      }
    }
  }
  return !!rec;
}
export function restoreWeightNudge(state, key) {
  const p = state.designProfile;
  const rec = (key && p.savedWeightNudge && p.savedWeightNudge[key]) || null;
  return applyNudgeRecord(p, rec);
}

export function rememberInk(state, key) {
  if (!key) return;
  const p = state.designProfile;
  const ink = captureInk(p);
  const store = (p.savedInk && typeof p.savedInk === 'object') ? { ...p.savedInk } : {};
  if (Object.keys(ink).length) store[key] = ink; else delete store[key];
  p.savedInk = Object.keys(store).length ? store : null;
}

/**
 * Paint `key`'s remembered ink back onto the live elements. Returns true if anything was adopted.
 *
 * ⚠ RUNS AFTER THE CASCADE, NOT INSTEAD OF IT (Ryan 2026-08-01, decision 4). A plate pick still
 * re-derives everything below it — that is the right answer for a colour the buyer has never inked.
 * This then overwrites that derivation for a colour they HAVE, which is what makes re-picking the
 * same yellow keep the black text they chose for it the first time.
 *
 * Marks what it restores as user-set, because it IS the buyer's choice — otherwise the next auto
 * pass would treat it as derived and feel free to replace it.
 */
export function restoreInk(state, key) {
  const p = state.designProfile;
  /* ⛔ FIRST, DROP THE OUTGOING PLATE'S RESCUE RINGS — unconditionally, even when there is nothing
     remembered to put back.
       `syncFillContrastOutline` wipes the stack and pushes exactly one `{colorRef, thickness, locked}`
     ring whenever the chosen fill would merge with the plate, and `syncLogoContrastOutline` adds the
     logo's twin with `auto: true`. Both exist ONLY to rescue the plate that needed them —
     `recascadeColorDefaults` says so in as many words ("an auto contrast ring does not [survive]; it
     belongs to the plate that needed it") and drops them on a plate PICK. Nothing dropped them on a
     product HOP, so `migrateElementStyle` deep-copied them onto the next product.
       The text ring carries no marker, so it is identified by flags: `locked && !added && !userSet` is
     exactly "a rescue ring the app added for the plate we are leaving". A ring the buyer ADDED or
     COLOURED fails that test and stays, which is the point of the distinction. */
  for (const e of p.elements || []) {
    if (e.type === 'logo') {
      const rings = Array.isArray(e.logoAddOutline) ? e.logoAddOutline : (e.logoAddOutline ? [e.logoAddOutline] : []);
      /* ⛔ "BELONGS TO THE PLATE THAT NEEDED IT" HAS TO MEAN *WHICH* PLATE (2026-08-15, the Tigers
         crest). This dropped every `auto` ring unconditionally — and it is the LAST step of
         `masterSwitchCascade`, whose SECOND step is `syncLogoContrastOutline` deriving that ring for
         the plate being picked. So the cleanup for the plate being LEFT ran after the rescue for the
         plate being ENTERED and deleted it: the logo's contrast ring has never once survived a plate
         pick. It was invisible until now only because the rescue used to require a flattened logo,
         which is a rarer road to the same line.
           `forKey` is `inkContextKey` at derivation time, i.e. the same string this function is
         keyed by — so a ring for THIS plate is kept and one carried in from anywhere else (the
         product hop this cleanup was written for, per the note above) is still dropped.
         ⚠ An older save has no `forKey` and is dropped exactly as before, which is the safe
         direction: the next plate pick re-derives it. */
      const manual = rings.filter((a) => a && (a.auto !== true || (a.forKey && a.forKey === key)));
      e.logoAddOutline = manual.length ? manual : null;
      continue;
    }
    if (e.type !== 'arctext' && e.type !== 'weight') continue;
    if (!Array.isArray(e.outlines)) continue;
    e.outlines = e.outlines.filter((o) => !(o && o.locked === true && o.added !== true && o.userSet !== true));
    // `locked` is per-plate immunity by definition; a survivor must not carry the old plate's into the
    // contrast view on the new one.
    for (const o of e.outlines) if (o) o.locked = false;
  }
  const set = p.savedInk && p.savedInk[key];
  if (!set) return false;
  let hit = false;

  // ONE record, applied to EVERY piece of the role — the same fan-out `setFillAll` performs. This is
  // what makes a set written on one product land completely on another whose composition differs.
  const t = set.text;
  if (t) {
    for (const e of p.elements || []) {
      if (e.type !== 'arctext' && e.type !== 'weight') continue;
      if (t.fill) {
        e.fillRef = t.fill;
        // Faithful, not blanket — see `captureInk`.
        e.fillUserSet = t.fillUserSet === true;
        hit = true;
      }
      if (Array.isArray(t.outlines)) {
        if (!Array.isArray(e.outlines)) e.outlines = [];
        /* WD 488 (UX run 2): rings the buyer took over (`ringsOwn`) hold their COUNT, width and identity on every plate
           - the same "latest edit holds everywhere" WD 486 gave deletions. Measured: two rings on cream, hop to black,
           black's remembered one-ring stack truncated the buyer's outer ring away. Only the per-plate COLOUR applies. */
        /* WD 495: whether the stack is the buyer's comes from THIS plate's record, not the live element - the live
           flag belongs to the plate being left. An own stack is put back whole; otherwise the positional restore runs. */
        const own = t.own === true;
        e.ringsOwn = own;
        e.outlineEdits = t.edits && typeof t.edits === 'object' ? JSON.parse(JSON.stringify(t.edits)) : null;
        if (own) {
          e.outlines = t.outlines.slice(0, 4).map((c, i) => {
            const w = Array.isArray(t.outlinesW) ? t.outlinesW[i] : null;
            return { colorRef: c || 'secondary', thickness: Number.isFinite(w) && w > 0 ? w : outlineThicknessFor(p), added: true, userSet: true };
          });
          hit = true;
        }
        // The remembered LENGTH caps the live stack. This is what brings a piece the buyer made SOLID
        // back solid — including one the destination preset has just seeded fresh with a ring, which
        // is how an outline appeared on the bumper's arc text and nowhere else.
        if (!own && e.outlines.length > t.outlines.length) {
          e.outlines = e.outlines.slice(0, t.outlines.length);
          hit = true;
        }
        /* ⛔ AND IT IS A FLOOR TOO (Ryan 2026-08-05). This was a ceiling ONLY — if the live stack was
           SHORTER than remembered there was no else, so nothing ever put a ring back. Ring counts
           could therefore only ever go DOWN, and every hop was another chance to lose one: remove a
           ring on the Training bumper and the Competition plate could not restore its own, which read
           as "removal propagates". It did not — the other plate was simply unable to recover. Adding
           it back then could not survive a hop either, because the plate you hopped to truncated it
           and the plate you came from could not re-create it. A ratchet, not a design.
             With the floor, each plate colour's ring COUNT behaves the way its ring COLOURS already
           did: per plate, restored in both directions, and an edit on one plate stops silently
           rewriting another. Rings are re-created un-`userSet` so the contrast passes above stay free
           to colour them for THIS plate — the remembered colorRef is applied just below, and
           `outlinesUserSet` re-marks only the ones the buyer really chose. */
        for (let i = e.outlines.length; !own && i < t.outlines.length; i++) {
          const w = Array.isArray(t.outlinesW) ? t.outlinesW[i] : null;
          e.outlines.push({
            colorRef: t.outlines[i] || 'secondary',
            thickness: Number.isFinite(w) && w > 0 ? w : outlineThicknessFor(p),
          });
          hit = true;
        }
        const us = new Set(Array.isArray(t.outlinesUserSet) ? t.outlinesUserSet : []);
        (e.outlines || []).forEach((o, i) => {
          const c = t.outlines[i];
          if (o && c) { o.colorRef = c; o.userSet = us.has(i); hit = true; }
          /* Restore the ring's full identity, not just its colour (see `captureInk`): the slot it
             echoes and the width it wore on this plate. A survivor recoloured into another slot's
             position otherwise keeps the OLD slot's autoIdx and thickness, and the re-derive that
             follows compounds the lie. Older buckets lack the arrays → unchanged behaviour. */
          if (!own && o && Array.isArray(t.outlinesAuto) && i < t.outlinesAuto.length) {
            const a = t.outlinesAuto[i];
            if (Number.isInteger(a)) { if (o.autoIdx !== a) { o.autoIdx = a; hit = true; } }
            else if (Number.isInteger(o.autoIdx)) { delete o.autoIdx; hit = true; }
          }
          if (!own && o && o.userSet !== true && Array.isArray(t.outlinesW) && i < t.outlinesW.length) {
            const w = t.outlinesW[i];
            if (Number.isFinite(w) && w > 0 && o.thickness !== w) { o.thickness = w; hit = true; }
          }
          // WD 495: which rings the buyer ADDED on this plate (the re-balance and the next re-derive key off it).
          if (!own && o) {
            const was = o.added === true;
            const now = Array.isArray(t.outlinesAdded) && t.outlinesAdded.includes(i);
            if (now) o.added = true; else delete o.added;
            if (was !== now) hit = true;
          }
        });
      }
    }
  }

  const lg = set.logo;
  if (lg) {
    for (const e of p.elements || []) {
      if (e.type !== 'logo') continue;
      // Assigned unconditionally, null included: "back to the original artwork" is a remembered state,
      // and skipping it would let a flatten chosen for one plate ride onto the next.
      e.logoCenterColor = lg.center || null;
      e.logoCenterAuto = false;
      if (lg.bands) {
        const bc = {};
        for (const k of Object.keys(lg.bands)) bc[k | 0] = lg.bands[k];
        e.logoBandColors = bc;
      } else {
        e.logoBandColors = null;
      }
      e.logoBandUserSet = Array.isArray(lg.bandsUserSet) ? lg.bandsUserSet.slice() : null;
      if (Array.isArray(lg.adds) && lg.adds.length) {
        const keepThickness = Array.isArray(e.logoAddOutline) ? e.logoAddOutline : [];
        e.logoAddOutline = lg.adds.map((c, i) => ({
          color: c,
          // WD 495: the record's own width first - the live ring may be another plate's, or gone after a plate change.
          thickness: (Array.isArray(lg.addsW) && Number.isFinite(lg.addsW[i]) && lg.addsW[i] > 0 && lg.addsW[i])
            || (keepThickness[i] && keepThickness[i].thickness) || (keepThickness[0] && keepThickness[0].thickness) || 0.06,
        }));
      } else {
        /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (WD 495, 2026-11-01) — "NO OUTLINE ON THIS PLATE" IS REMEMBERED TOO, the way the
           text's remembered stack length already caps its rings. Ryan, UX run 3 (DB Logo, Competition bumper): black, green
           and white each opened with no outline; he added one to the text and logo on BLACK, then went back to green - *"The
           text didn't have an outline like it didn't originally -- good. But the logo did get an outline, and it was black.
           … if you think it SHOULD get an outline, shouldn't the text get one too?"* and the same on white. This branch used
           to do nothing when the plate's record had no added rings, so the ring added on black rode onto every plate
           visited before it and was recoloured there. Now a plate that remembers no added logo ring gets none; the app's
           own contrast ring for THIS plate (`auto`, `forKey === key`) is left alone - the cleanup above already settled it. */
        const live = Array.isArray(e.logoAddOutline) ? e.logoAddOutline : (e.logoAddOutline ? [e.logoAddOutline] : []);
        const autoHere = live.filter((a) => a && a.auto === true && a.forKey === key);
        if (autoHere.length !== live.length) e.logoAddOutline = autoHere.length ? autoHere : null;
      }
      hit = true;
    }
  }
  return hit;
}

// ── Cross-product contrast remap (Fable §4.3) ────────────────────────────────

/** WCAG relative luminance — RE-EXPORTED so `design-panel.js`'s existing import keeps working.
 *  ⚠ EXPORTED 2026-08-09 for the plate chip's lead-time badge, which sits ON the swatch and has to
 *  pick its ink from the colour underneath (Ryan: *"the text color needs to be dynamic so it's white
 *  on dark colors and dark on light colors"*). The BODY moved to design-profile.js on 2026-08-17,
 *  where `readsOn` needs it and where the layer below can reach it — for the reason this comment
 *  already gave: a second copy of the WCAG curve is the "two places need the same answer, one will
 *  be wrong" trap this project has paid for repeatedly, and `0.2126R + 0.7152G + 0.0722B` on
 *  LINEARISED channels is exactly the kind of arithmetic that gets approximated into something that
 *  looks fine on the six colours you tested. */
export { relLuminance } from '../design-profile.js';

const CONTRAST_BLACK_MIN = 3; // WCAG AA "large text" floor — plate/dumbbell letters are big

/**
 * §4.3 — a buyer who hops from a colored-body bumper to an always-black product
 * (dumbbell today; barbell/grip later) may be carrying text/outline colors tuned
 * to pop on that body color, not on true black. Remap ONLY the pieces that
 * actually fail contrast on black, preferring the bumper's own body color first
 * (Ryan's example: pink plate + dark-purple text → pink text on the black
 * dumbbell), then the palette. Pieces that already read fine are untouched.
 * Caller (main.js) owns the toast/undo — this just mutates + returns a
 * before-snapshot so a caller-side Undo can restore literal colors exactly.
 * @returns {{changed:boolean, snapshot:object|null}}
 */
export function applyCrossProductContrastRemap(state) {
  const p = state.designProfile;
  const pal = p.palette || {};
  const candidates = [p.bodyColor, pal.primary, pal.secondary, pal.tertiary]
    .map(normalizeHex)
    .filter((h) => h && contrastRatio(h, '#000000') >= CONTRAST_BLACK_MIN);
  const pieces = p.elements.filter((e) => e.type === 'arctext' || e.type === 'weight');
  if (!candidates.length || !pieces.length) return { changed: false, snapshot: null };

  const before = designSnapshot(state);
  let changed = false;
  const bestFor = (currentHex) => {
    if (contrastRatio(currentHex, '#000000') >= CONTRAST_BLACK_MIN) return null; // already fine
    return candidates.reduce((best, c) => (contrastRatio(c, '#000000') > contrastRatio(best, '#000000') ? c : best));
  };

  for (const e of pieces) {
    const fillHex = resolvePaletteRef(e.fillRef || 'primary', pal);
    const fillSwap = bestFor(fillHex);
    if (fillSwap) { e.fillRef = refForHex(pal, fillSwap); changed = true; }
    for (const o of e.outlines || []) {
      const oHex = resolvePaletteRef(o.colorRef, pal);
      const oSwap = bestFor(oHex);
      if (oSwap) { o.colorRef = refForHex(pal, oSwap); changed = true; }
    }
  }

  return { changed, snapshot: changed ? before : null };
}

/** Restore a design snapshot taken before a remap (literal-color Undo). */
export function restoreSnapshot(state, snapshot) {
  if (snapshot) applySnapshot(state, snapshot);
}

/** Find an element by id. */
export function getElement(state, id) {
  return state.designProfile.elements.find((e) => e.id === id) || null;
}

/** Patch an element in place (shallow merge). Returns the element or null. */
export function updateElement(state, id, patch) {
  const elm = getElement(state, id);
  if (!elm) return null;
  Object.assign(elm, patch);
  return elm;
}

/** Add a new element; returns it and selects it. */
export function addElement(state, type, slot, overrides = {}) {
  const elm = createElement(type, slot, overrides);
  state.designProfile.elements.push(elm);
  state.ui.selectedElementId = elm.id;
  return elm;
}

/** Remove an element by id. Returns success. */
export function removeElement(state, id) {
  const els = state.designProfile.elements;
  const i = els.findIndex((e) => e.id === id);
  if (i < 0) return false;
  els.splice(i, 1);
  if (state.ui.selectedElementId === id) {
    state.ui.selectedElementId = els[0] ? els[0].id : null;
  }
  return true;
}

/** Set the displayed weight number (profile is the truth; ui.weight mirrors it). */
export function setWeight(state, weight) {
  if (Number.isFinite(weight) && weight > 0) {
    state.designProfile.weightValue = weight;
    state.ui.weight = weight;
  }
  return state.designProfile.weightValue;
}

/** Set lb/kg unit. */
export function setUnit(state, unit) {
  const u = unit === 'kg' ? 'kg' : 'lb';
  state.designProfile.unit = u;
  state.ui.unit = u;
  return u;
}

/**
 * The preset that is the CURRENT layout with exactly one piece swapped for a logo — used when a
 * logo is uploaded onto a layout whose every anchor is already occupied (Ryan 2026-07-25).
 *
 * Match rules, deliberately strict so this is a swap and never a redesign:
 *   • same layout family/product (an arched plate layout never jumps to a badge or a dumbbell),
 *   • the SAME slot footprint — the identical set of anchors, so the composition is unchanged,
 *   • exactly ONE slot differs from the current layout, and it differs by BECOMING a logo.
 * Ties break on the same anchor preference the free-slot search uses (left first).
 *
 * The Seal (arch top + bottom, weight left + right) → Varsity Arch (arch top + bottom, LOGO left,
 * weight right). Returns null when no such sibling exists, and the caller then leaves the design
 * untouched rather than inventing one.
 */
function presetSwappingOneSlotForLogo(profile) {
  const cur = getLayoutPreset(profile && profile.layoutPreset);
  if (!cur || !Array.isArray(cur.elements)) return null;
  const footprint = (els) => els.map((e) => e.slot).slice().sort().join(',');
  const curPrint = footprint(cur.elements);
  const curType = new Map(cur.elements.map((e) => [e.slot, e.type]));
  const family = layoutPresetProduct(cur);
  const prefer = ['left', 'top', 'bottom', 'right'];
  let best = null;
  let bestRank = Infinity;
  for (const p of LAYOUT_PRESETS) {
    const els = p.elements;
    if (!Array.isArray(els) || p.id === cur.id) continue;
    if (layoutPresetProduct(p) !== family) continue;
    if (footprint(els) !== curPrint) continue;
    const changed = els.filter((e) => curType.get(e.slot) !== e.type);
    if (changed.length !== 1 || changed[0].type !== 'logo') continue;
    const rank = prefer.indexOf(changed[0].slot);
    if (rank >= 0 && rank < bestRank) { bestRank = rank; best = p; }
  }
  return best;
}

/**
 * Attach a logo. With an explicit `elementId` it targets just that element;
 * otherwise it sets EVERY logo slot — a plate shares ONE logo image across all
 * its logo slots (e.g. "Logo top & bottom"), so an upload must fill all of them
 * (creates one on the 'left' slot if none exist). Flips ui.hasLogo.
 */
export function setLogo(state, logo, elementId = null) {
  const merged = { ...createDefaultLogo(), ...(logo || {}) };
  if (merged.quality == null && !merged.isVector) {
    merged.quality = assessQuality(merged.width, merged.height);
  }
  // Stash the uploaded image on the PROFILE so it survives layouts with no logo
  // slot (text-only) and is re-seeded when a logo layout reappears (see
  // applyLayoutPreset). Without this, scrolling through a text-only layout lost it.
  if (merged.dataUrl) state.designProfile.savedLogo = { ...merged };
  if (elementId) {
    const elm = getElement(state, elementId);
    if (elm) {
      elm.logo = merged;
      state.ui.hasLogo = !!merged.dataUrl;
    }
    return elm;
  }
  let logos = state.designProfile.elements.filter((e) => e.type === 'logo');
  if (!logos.length) {
    // The current layout has NO logo slot (e.g. Competition = weight left + weight right).
    // Drop the new logo into a slot that ISN'T already occupied by another element, so it can
    // never land on top of an existing weight/arc-text (Ryan 2026-07-23). Priority = the natural
    // logo homes (left first, matching the old default), then top/bottom/right.
    const taken = new Set(state.designProfile.elements.map((e) => e.slot));
    const free = ['left', 'top', 'bottom', 'right'].find((s) => !taken.has(s));
    if (free) {
      logos = [addElement(state, 'logo', free)];
      // A recolour made before this element existed is waiting on the profile — see
      // `inheritSavedToneSwaps`. Called again at the end of this function so the OTHER creation
      // paths (a preset swap, an existing empty slot) are covered by one line rather than three.
    } else {
      // EVERY anchor is occupied. The old code fell back to 'left' here, with a comment claiming
      // that was "not possible with the current presets, but degrades safely" — both halves wrong
      // (Ryan 2026-07-25). "The Seal" is arch top + arch bottom + weight BOTH sides, i.e. all four
      // anchors, so uploading a logo from the Add-Logo tab stacked it straight on top of the left
      // weight number.
      //   There is nowhere to PUT a logo here, so move to the layout that has room for one: the
      // sibling preset that is this same arrangement with one piece swapped for a logo. From The
      // Seal that is Varsity Arch — same arch top & bottom, same weight right, logo where the left
      // weight was. Requiring an identical slot footprint and exactly ONE slot changed (to a logo)
      // is what keeps this a swap rather than a redesign; the buyer's arc text migrates across
      // because applyLayoutPreset preserves it.
      const swap = presetSwappingOneSlotForLogo(state.designProfile);
      if (swap) {
        applyLayoutPreset(state.designProfile, swap.id);
        reseedIdentityIntoPlaceholders(state.designProfile);
        state.ui.selectedElementId = state.designProfile.elements[0] ? state.designProfile.elements[0].id : null;
        logos = state.designProfile.elements.filter((e) => e.type === 'logo');
      }
      // No such sibling → leave the design ALONE rather than stack. The image is already stashed on
      // `savedLogo` above, so it appears the moment a layout with a logo slot is chosen. Never
      // overlap two pieces on one anchor: that reads as a rendering fault, not a design.
      if (!logos.length) { state.ui.hasLogo = false; return null; }
    }
  }
  // Each slot gets its own copy so per-slot edits (e.g. an upside-down bottom
  // logo) never alias the same object.
  //   A NEW image also resets the manual size dial (Ryan 2026-07-25: "I just uploaded a logo to
  // test and it was blown up way bigger than it would normally be… on upload, defaults are there
  // for a reason so let's use em"). `sizeScale` is an OVERRIDE of the auto-fit, and it was living
  // on the element rather than on the image — so it outlived the logo it was chosen for and the
  // next upload arrived wearing the previous mark's dial. The auto-fit is per-image (it reads the
  // aspect and size-matches the sibling weight), so a fresh image starts from its own default and
  // the buyer re-dials if they want to.
  //   Deliberately NOT in applySnapshot's path: a RESUME must keep the size the buyer chose. This
  // only fires where a genuinely different image lands.
  logos.forEach((e) => { e.logo = { ...merged }; if (merged.dataUrl) e.sizeScale = 1; });
  /* A recolour the buyer made on the Add Logo tab happened before any of these elements existed, so
     its paint is waiting on the profile — hand it over now that there is something to paint. One
     call, after every branch above, rather than one per creation path. See `inheritSavedToneSwaps`. */
  inheritSavedToneSwaps(state.designProfile);
  state.ui.hasLogo = !!merged.dataUrl;
  return logos[0];
}

/**
 * Apply a palette-extraction result to profile.palette.primary/secondary.
 * On low confidence, stores the chips but does NOT guess colors.
 * @returns {boolean} whether colors were prefilled.
 */
export function applyPalette(state, result) {
  // ⚠ KEEP THE EXTRACTOR'S OWN CHOICES, not just the raw list (Ryan 2026-07-28: the McDonald's logo
  // shows red / white / yellow on Add Logo and a two-tone red/yellow swatch on Customize Design).
  //   This used to store `{ confidence, all }` and nothing else. The Add Logo tab never noticed,
  // because the reveal is handed the FULL extraction result directly — but every other surface has
  // only this copy, and `primary` / `secondary` / `accents` are not recoverable from `all`: they are
  // the extractor's judgement about which tones are the brand's, not simply the most populous. The
  // McDonald's wordmark is the case that proves it — white is the SECOND colour by that judgement and
  // roughly the third by pixel count, so anything re-deriving from `all` would both drop it and
  // reorder the two that survived.
  //   Keeping them means one rule (`shownLogoTones`) can answer "which colours does this logo have"
  // for any caller, which is what the swatch and the chips failing to agree came down to. Additive
  // and version-neutral: an older autosave simply lacks these keys, and the roles fallback covers it.
  // ── A LOGO SWAP RETIRES THE OLD BRAND'S COLOURS (Ryan 2026-07-28) ────────────────────────────
  // `applyLogoStructure` resets everything else a previous logo left behind — the plate dye, the
  // palette roles, the third role, the outline stack — on the stated grounds that "a re-upload should
  // land in the same place" a fresh upload does. The BENCH was the one thing it never touched, so
  // swapping the Denver badge for the Drop Bear mascot carried Denver's white onto the new design's
  // colour rows, in a row labelled as this logo's colours.
  //
  // ⚠ ONLY THE PREVIOUS LOGO'S OWN TONES, never the whole bench. The bench also holds colours the
  // BUYER added — every custom plate dye lands there — and it rides into the quote as their curation.
  // Wiping it would delete their work to fix a tidiness problem.
  //
  // Derived exactly the way `orderedPool` derives the ✕ (a bench colour that is one of the logo's
  // tones is a logo tone), which is why this has to happen HERE: `applyPalette` is the last moment
  // both palettes exist. One line later `state.ui.palette` is the NEW logo and the old tones are
  // unknowable. It inherits that rule's one limitation too — a colour the buyer added by hand that
  // happens to equal a tone of the outgoing logo is indistinguishable from the tone itself, and goes
  // with it. That is the same trade the ✕ already makes.
  //
  // No-op on a FIRST upload (`state.ui.palette` is null, so there is nothing to retire) and on a
  // resume (same reason), so only an actual swap is affected.
  const retiring = new Set(shownLogoTones(state.ui.palette).map((h) => normalizeHex(h)).filter(Boolean));
  // The outgoing logo's KEYED FIELD is one of its tones too — it just never lived in the palette
  // (it was stripped before extraction), so the derivation above cannot see it. Without this a
  // logo swap left the previous school's navy on the new design's bench (the exact leak the
  // paragraph above describes, arriving through the 2026-08-18 field seeding).
  {
    const oldField = normalizeHex(state.designProfile.keyedFieldHex);
    if (oldField) retiring.add(oldField);
  }
  if (retiring.size && Array.isArray(state.designProfile.swatches)) {
    state.designProfile.swatches = state.designProfile.swatches
      .filter((s) => !retiring.has(normalizeHex(s)));
  }
  state.ui.palette = result
    ? {
      confidence: result.confidence,
      all: Array.isArray(result.all) ? result.all : [],
      primary: result.primary || null,
      secondary: result.secondary || null,
      accents: Array.isArray(result.accents) ? result.accents : [],
      // WD 481: the tones that exist only as a smear - read by `plateDealDeck`, never dealt as a plate
      smearHexes: Array.isArray(result.smearHexes) ? result.smearHexes : [],
    }
    : null;
  // HOW MANY TONES THIS MARK HAS, recorded once, from the one function that answers that question
  // (Ryan 2026-07-28). Not a fifth definition — `shownLogoTones` is what the Add Logo chips show and
  // what the Customize Design swatch is divided into, so when Ryan says "this 4-tone logo" this is the
  // 4 he is counting. Stored on the profile because a colour RULE reads it (see
  // `dropAutoOutlinesWhenFillReads`) and a rule's input has to survive a reload — the same lesson the
  // `colorBorneDetail` serialization bug taught an hour earlier.
  //   Set before the low-confidence return below, so an unreadable palette records an honest 0 rather
  // than leaving the previous logo's count in place after a swap.
  state.designProfile.logoToneCount = shownLogoTones(state.ui.palette).length;
  if (!result || result.confidence !== 'high' || !result.primary) return false;
  const pal = state.designProfile.palette;
  const primary = normalizeHex(result.primary.hex);
  if (primary) pal.primary = primary;
  const secondary = result.secondary ? normalizeHex(result.secondary.hex) : null;
  // A ONE-TONE logo clears the slot rather than leaving the app's default sitting in it
  // (Ryan 2026-07-25). Extraction already refuses to fabricate a second colour — it returns
  // `secondary: null` — but the profile's own #FBBF24 stayed underneath, so the design opened
  // outlined in a "brand" colour the mark never contained. Null here means the piece seeding
  // (defaultOutlinesFor) leaves the text SOLID, and the buyer adds a ring if they want one.
  //   Safe to null: resolvePaletteRef('secondary') already falls back to #FFFFFF, so nothing
  // downstream can resolve to undefined.
  pal.secondary = secondary || null;
  // ── EVERY TONE THE LOGO HAS GETS A HOME, HERE, AT UPLOAD (Ryan 2026-07-28) ───────────────────
  // Only primary and secondary become ROLES, and the swatch rows are roles + bench + house neutrals —
  // so a third tone was shown as a chip on Add Logo and existed in no row on Customize Design. It
  // took a recolour to expose it (white standing in for the McDonald's wordmark until it became
  // burgundy and vanished), and `recolorEverywhere` benching the result was a patch on the symptom:
  // it only helps a tone the buyer happens to edit, and it stops helping at the cap, silently.
  //   Seating them at upload is the actual fix. `addSwatch` already declines a role, a duplicate and
  // a full bench, so this is a loop over the same list the chips are built from and nothing else.
  //
  // ⚠ `ownTone` — these are read out of the artwork, so a white or black among them is the brand's,
  // not a derived neutral. See the guard in `addSwatch`.
  //
  // The comment this replaces said black and white are never injected because they are house
  // neutrals always available anyway. That was true when it was written and stopped being true when
  // `orderedPool` began withholding the neutrals from any logo with two or more tones of its own —
  // at which point "always available" quietly became "available except on the logos most likely to
  // contain one".
  for (const hex of shownLogoTones(state.ui.palette)) addSwatch(state, hex, { ownTone: true });
  return true;
}

// ── THE KEYED-OUT FIELD IS A SCHOOL COLOUR (Ryan 2026-08-18) ─────────────────────────────────────
// "When the background isn't black or white… and it varies from any of the other extracted colors…
// perhaps we can assume this is a school color which should get pulled into the swatches? So even
// though it's being removed from the logo itself, it can still be added as a swatch… the text
// outline could perhaps be that dark blue first as a higher priority than the black."
//
// Hanover is the proof: a known Red|Blue school whose brand-kit export sits on its own navy. The
// keyer rightly strips the navy from the DRAWING, and the palette then extracts red/white — the
// blue was real brand data being thrown away. Seating it on the BENCH (never a role — the roles
// drive how the mark itself is recoloured, and the field is no longer part of the drawing) is the
// whole mechanism: every ladder already runs roles → bench → house neutrals (`assignOutlineColors`,
// `ringPreferenceOrder`, `bandRescuePool`, `orderedPool`), so the outline preferring navy over
// black, the navy plate chip, and the text/logo rings converging on one colour all fall out of one
// seeding call with no ladder surgery. `readsOn` (WD 326) still guards any pairing it can't wear.
/** Below this CIELAB chroma a field reads as a matte/tint, not a brand colour.
 *  ⭐ MEASURED, not picked (`probe-field-swatch.mjs --all`, 2026-08-18): every keyed field in the
 *  corpus is either a true neutral (chroma 0), a duplicate of a mark tone, or one of three:
 *  cvcc #BDDAE9 at 12.4 (the pale SKY in a photo of a campus building — must NOT seat),
 *  Hanover #0B476C at 26.7 and EAC #4C1C70 at 54.9 (real school colours — must seat).
 *  18 sits in the open space between the sky and the navy; re-measure before moving it. */
export const FIELD_SWATCH_MIN_CHROMA = 18;
/**
 * Seat a keyed-out CHROMATIC background field on the swatch bench. No-op (returns null) when the
 * field is a neutral (near-black / near-white / low-chroma gray), already represented among the
 * mark's tones or bench (ΔE), or the bench is full. Returns the seated hex otherwise.
 * @param {object} state
 * @param {number[]|null} fieldRgb the measured background colour (`logo.keyField` / `pick.field`)
 */
export function seedKeyedFieldSwatch(state, fieldRgb) {
  // ⚠ ALWAYS re-answered per upload: a new logo with no seatable field must clear the previous
  // one's claim, or its swatch would keep reading as "brand" for artwork it never belonged to.
  const p = state.designProfile;
  p.keyedFieldHex = null;
  if (!Array.isArray(fieldRgb) || fieldRgb.length < 3) return null;
  const hex = normalizeHex(`#${fieldRgb.map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('')}`);
  if (!hex) return null;
  // Neutral fields are not brand data: black/white are house colours already, and a low-chroma
  // gray is a photo matte. (Near-white check mirrors bg-detect's NEAR_WHITE_CHANNEL.)
  if (isNearBlackHex(hex)) return null;
  if (fieldRgb.every((v) => v >= 225)) return null;
  if (hexChroma(hex) < FIELD_SWATCH_MIN_CHROMA) return null;
  // Already represented → nothing to add. ΔE, not string equality: the field is measured off
  // pixels and will rarely byte-match an extracted tone it visually is.
  const pal = p.palette || {};
  const known = [pal.primary, pal.secondary, pal.tertiary,
    ...shownLogoTones(state.ui.palette),
    ...(Array.isArray(p.swatches) ? p.swatches : [])]
    .map(normalizeHex).filter(Boolean);
  if (known.some((k) => colorsConflict(k, hex) || colorsIndistinguishable(k, hex))) return null;
  if (!addSwatch(state, hex, { ownTone: true })) return null;
  p.keyedFieldHex = hex;
  // The post-upload baseline was captured inside `applyLogoStructure`, one step BEFORE this
  // seeding — so without this, "Reset to original" would drop the school colour off the bench
  // while `keyedFieldHex` kept claiming it. The field is part of the original; the baseline
  // must say so.
  if (p.styleBaseline && Array.isArray(p.styleBaseline.swatches)
      && !p.styleBaseline.swatches.some((s) => normalizeHex(s) === hex)) {
    p.styleBaseline.swatches.push(hex);
  }
  return hex;
}

/**
 * The keyed-out school colour, IF it is still living on the bench — the single answer every
 * surface asks: the Add Logo chips append it, `orderedPool` withholds its ✕ (it is brand, not
 * buyer-added), and `applyDefaultPlateColors` deals it into the multi-configuration mix. Bench
 * membership is the gate so a colour the buyer has since recoloured or evicted stops counting.
 */
export function seatedFieldHex(state) {
  return seatedFieldOf(state && state.designProfile);
}

/**
 * The same answer from the PROFILE ALONE, for the callers that hold no `state`.
 * ⚠ THE TEST WAS WRITTEN OUT BY HAND IN TWO OTHER PLACES before this existed (the keyline-holds
 * rescue above says so in its own comment, and the plate deal reached for `state` only for this).
 * Bench membership is the gate either way: a field the buyer has recoloured or evicted stops
 * counting.
 */
export function seatedFieldOf(p) {
  const hex = p && normalizeHex(p.keyedFieldHex);
  if (!hex) return null;
  return Array.isArray(p.swatches) && p.swatches.some((s) => normalizeHex(s) === hex) ? hex : null;
}


// ── Arriving on a configuration — the ONE sequence, shared by the hop and the packet ──────────
//
// CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (WD 460, 2026-09-22 — the Hartnell push)
//
// Ryan designed four Competition Urethane configurations in two school colours, primed, ticked
// 45 / 25 / 10 on the staged row and sent — and every plate in the packet came out in the colour
// of the ONE configuration he happened to be standing on. `ui/project-push.js` now stages one row
// per configuration, and `plate-batch.js` has to draw a configuration the rep is NOT standing on:
// restore its plate colour, restore the ink the buyer chose for that colour, re-balance both
// against the plate they land on, re-derive the logo's rescue ring, drop the rings the new plate
// does not need, and colour what survives — in that order, because every line of it has bitten
// once when it ran in another. That sequence lived in `main.js adoptProduct`, twice, word for
// word (the cross-family branch and the same-family branch). A third copy in the renderer is
// exactly the drift the memory note *"never re-derive a family's design headlessly"* warns
// about, so the block moved HERE with every note it carried, and all three callers run it.
//
// ⛔ THE CALLER HAS ALREADY STAMPED THE OUTGOING PLATE AND MOVED `state.ui.product`. This is the
// restore half of the seam only — `adoptProduct` stamps (`rememberBodyColor`, `rememberInk`,
// `rememberWeightNudge`) before it moves the product, and `openConfiguration` below mirrors that
// for a throwaway state. Do not add the stamps here: the cross-family hop rebuilds the layout
// between the two halves, and the stamp must land before that rebuild reads the design.

/**
 * Put `key`'s plate colour and ink back onto the live design and re-balance them — having
 * ALREADY set `state.ui.product = key`. Returns what the two hop branches used to compute inline.
 * @returns {{plateMoved:boolean, adoptedInk:boolean}}
 */
export function arriveOnConfiguration(state, key) {
  let adoptedInk = false;
  /* ⛔ THE PLATE COLOUR GOES BACK FIRST, AND THE ORDER IS THE WHOLE POINT (Ryan 2026-08-09).
     `inkContextKey` resolves the ink bucket FROM `bodyColor`, so restoring the ink before the
     colour looks up the bucket for the plate being LEFT — painting the white configuration's
     ink onto the green one, which the next hop then stamps back under the wrong key. Nothing
     throws; the design just quietly rots one hop at a time. Both branches, mirroring the stamp
     seam at the top of this function. */
  const plateMoved = restoreBodyColor(state, key);
  adoptedInk = restoreInk(state, inkContextKey(state));
  // …and this product's own number position, or centre if it has never been given one.
  restoreWeightNudge(state, weightNudgeKey(state));
  /* ⛔ RE-BALANCE WHAT WAS RESTORED AGAINST THE PLATE IT LANDS ON (Ryan 2026-08-05).
     `restoreInk` returns the colours this plate-colour bucket last held — right in principle,
     but a bucket can hold an answer that was never legible. The black bucket for the Bauer's
     Elite logo held the seeded #010101 ring, stamped while the design still sat on the undyed
     black plate, so every hop to an always-black product faithfully restored an invisible
     outline. The cross-product remap below cannot catch it: that is skipped precisely WHEN
     `adoptedInk` is truthy, so the one path that has a saved record is the one with no contrast
     check on it at all. Both hop branches need this — Competition↔Training is a SAME-family hop
     that still changes the plate.
       `userSet` rings are skipped inside, so a colour the buyer chose is never overridden. */
  // Fill FIRST — the rings are balanced against it one line down.
  if (adoptedInk) correctIllegibleFill(state.designProfile, effectivePlateHex(state));
  /* ⛔ A PLATE COLOUR THE BUYER HAS NEVER INKED OWES A FULL DERIVATION, NOT A RESCUE (Ryan
   * 2026-08-10, the Wallabies 4th configuration).
   *
   * *"Started with 3 comp bumpers, all colours were perfect. I then manually added a 4th and it
   * went to a black bumper with all green text/logo. Yellow would pop better against the black.
   * And should the black plate not default to the same colour variation black adopts when I
   * start with only 1 bumper — black plate, yellow & green logo, yellow text with green
   * outline?"*
   *
   * Yes, and the reason it did not is the shape this whole entry is about: a plate colour
   * arriving here has changed, but nothing ran the cascade a plate CHANGE is supposed to run.
   * `restoreInk` puts back what this bucket held, and a configuration seen for the first time
   * has an empty bucket — so the design simply carried the fill it had derived for the plate it
   * was LEAVING. Green was derived against the yellow plate; on black it is the wrong answer and
   * `effectiveTextStyle` cannot rescue it, because the render-time peel deliberately
   * early-returns on a near-black base. Black is the one plate where nothing downstream catches
   * this.
   *
   * ⭐ AND RYAN'S REFINEMENT FALLS OUT OF THE EXISTING DERIVATION FOR FREE — no new colour rule:
   * *"to spruce the black up a little, it should get the 2-tone logo and 2-tone text/outline,
   * unless one of the two tones is black or very close to it, then 1-tone on black is
   * preferred."* `recascadeColorDefaults` sets the fill to `primary` and asks `defaultOutlinesFor`
   * for the ring, and that function already refuses a candidate which fails to clear the plate
   * behind it (the 2026-08-05 Bauers fix — "A BLACK PLATE BITES TOO"). So a two-tone mark gets
   * both tones, and a mark whose second tone is near-black gets no ring at all. Exactly his rule,
   * already written down.
   *
   * ⚠ GATED THREE WAYS, because re-deriving where a buyer has chosen is how this rots silently:
   *   • `plateMoved` — `restoreBodyColor` returns false for a non-dyeable product and for a
   *     configuration with nothing remembered, so a Training bumper and an un-dyed hop are both
   *     excluded by construction.
   *   • `!adoptedInk` — a bucket WITH a record is the buyer's choice for this colour and
   *     `restoreInk` has just repainted it; re-deriving over that would undo a real decision.
   *   • It runs BEFORE `assignOutlineColors` below, in `masterSwitchCascade`'s order: drop the
   *     rings the new plate does not need, then colour what survives. */
  else if (plateMoved) {
    recascadeColorDefaults(state.designProfile, { plateChange: true }); // WD 495
    /* ⛔ THE FRESH DERIVATION IS `fillRef = 'primary'`, UNCONDITIONALLY — and primary can be a
       ghost on the very plate that was just restored (Ryan 2026-08-23, Amherst: purple primary
       on the first visit to the BLACK configuration, ~1.6:1 — *"won't white show up
       better?"*). The rescue used to run only on the `adoptedInk` branch, so the one path
       that RESETS the fill was the one that never checked it. Same call, same plate, right
       after the reset; `fillUserSet` is respected inside, and a primary that reads (the
       Wallabies' yellow on black) is a no-op by the trigger. */
    correctIllegibleFill(state.designProfile, effectivePlateHex(state));
  }
  /* ⛔ THE LOGO'S RESCUE RING IS RE-DERIVED ON EVERY ARRIVAL, NOT ONLY ON A FIRST VISIT
   * (Ryan 2026-09-13, corpus pass one — oakton: *"I can't for the life of me figure out why the
   * 45lber works and the 10lber went with a completely different approach which does not work,
   * especially when both use the same plate colour"*; the same words again on umobile).
   *
   * It used to live INSIDE the `else if (plateMoved)` branch above — the branch that runs only
   * when `restoreInk` found nothing — and that is a hole which two individually correct
   * decisions leave between them:
   *   • `inkRecordFor` deliberately never STAMPS an auto ring (`a.auto !== true`), because a
   *     rescue "belongs to the plate that needed it".
   *   • `restoreInk` returns truthy for ANY logo record, and a visited plate always leaves one
   *     ({"center":null} is a record).
   * So the SECOND visit to a plate colour had a bucket (→ no re-derive) that never carried the
   * ring (→ nothing to restore): the rescue was neither remembered nor re-derived. Measured over
   * the 08-24 corpus run — the ring lands on 20 cfg1 plates, 12 cfg2, 11 cfg3 and ZERO cfg4, and
   * six marks put cfg1 and cfg4 on the SAME plate colour with opposite answers (120018000234,
   * benuredhawks, oakton, umobilerams, uvfpatriots, valley). `tools/probe-ring-revisit.mjs`
   * drives the real app and shows the rule producing the missing ring the moment it is asked.
   *
   * ✅ SAFE ON THE RESTORE PATH BY CONSTRUCTION, which is why it can sit outside the gate that
   * was written to protect the buyer's choices: this function only ever writes or clears rings
   * carrying `auto: true`, and `restoreInk` cannot have restored one — it keeps exactly one auto
   * ring, the one whose `forKey` IS this plate, i.e. the ring being re-derived. A ring the buyer
   * added or coloured has no `auto` flag and is never touched.
   *
   * The guard is "we arrived on a plate at all", so a product that cannot wear a plate colour
   * (a Training bumper, an un-dyed hop — neither branch fires) stays exactly where it was. */
  if (plateMoved || adoptedInk) { syncLogoContrastRing(state); resyncAddedLogoRings(state); } // WD 487: the buyer's ring too
  /* ⚠ ORDER, MEASURED: the ring mirrors the TEXT's ring weight, so it must be derived
     BEFORE the drop that can take that ring away. Hoisting the sync below this line cost
     umobile's white-plate rescue its measured 0.048 and fell back to the 0.06 default —
     i.e. it silently broke the WD 329 thickness mirror. Same order as before the hoist. */
  if (plateMoved && !adoptedInk) dropAutoOutlinesWhenFillReads(state.designProfile);
  assignOutlineColors(state.designProfile, effectivePlateHex(state));
  return { plateMoved, adoptedInk };
}

/**
 * A throwaway state standing on configuration `key`, rebuilt from an autosave envelope — the
 * packet renderer's and the tray thumbnail's way of drawing a plate the rep is not looking at.
 *
 * The envelope carries everything a sibling configuration needs: `savedBodyColor` holds each
 * instance's plate colour, `savedInk` holds the buyer's ink per plate colour, and the elements
 * are the ONE design every instance of a product shares. So this leaves the envelope's live
 * product exactly as `adoptProduct` leaves one (the three stamps), moves to `key`, and arrives
 * through the same function the hop uses. A `key` that IS the live product, or that belongs to
 * a different product family, gets the envelope untouched — the first needs no hop, and the
 * second is a layout rebuild this deliberately does not attempt (the envelope primed on that
 * product is that product's design already).
 *
 * @param {string} envelope `toAutosave(state)` / `ctx.shareEnvelope()`
 * @param {string} key a products.js key, instance suffix included
 * @returns {object|null} the state, or null when the envelope cannot be re-opened
 */
export function openConfiguration(envelope, key) {
  const state = createState();
  let ok = false;
  try { ok = fromAutosave(envelope, state).restored; } catch { ok = false; }
  if (!ok || !state.designProfile) return null;
  const live = String(state.ui.product || '');
  const want = String(key || '');
  if (!want || want === live || baseProductKey(want) !== baseProductKey(live)) return state;
  // The stamp seam, in `adoptProduct`'s order: plate colour first, then the ink it keys.
  rememberBodyColor(state, live);
  rememberInk(state, inkContextKey(state));
  rememberWeightNudge(state, weightNudgeKey(state));
  state.ui.product = want;
  const { category, kind } = parseProductKey(want);
  state.ui.productCategory = category;
  state.ui.productKind = kind;
  arriveOnConfiguration(state, want);
  return state;
}

// Re-export for callers that build ad-hoc elements (design panel).
export { serializeElement };
